import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MODULES, DEFAULT_WEIGHTS, DEFAULT_IC_THRESHOLD, BUSINESS_SECTIONS, BRIEF_SECTIONS, INDUSTRY_SECTIONS,
  score, grade, valn, thesisFit, rec, proj, diliStats, growth, ipoYears, num, isDefaultRatings,
  statementsToMarkdown, inr, pct, xx, oneLine, type Deal, type ModuleKey, type Ratings, type ProvenancedField, type IndustryDetail, type BusinessBrief, type ModuleRatingsAI,
} from '@krit/core';
import { getDeal, deleteDeal, getSettings, getTeam, patchDeal, archiveDeal, unarchiveDeal, addCall, analyseCallApi, deleteCall, uploadCallRecording, getCallStatus, peersLookup, peersPopulate, peersIpoDetails, peersDetail, uploadDocument, deleteDocument, uploadDataroomZip, documentDownloadUrl, getDocumentText, saveDocumentText, extractFromDeck, peopleFetch, industryFetch, businessFromDeck, teamResearchFetch, questionsFetch, scoreFetch, screenerSync, synthesizeCallsTrend, synthesizeInternalCallsTrend, fetchAgencyRatings, moduleRatingsFetch, ocrDeckImages, reextractDataroomFile, applyDataroomText, dataroomFileRawUrl, dataroomFileText, applyDataroomBulk, getMe, canEdit, isAdmin, probe42Get, probe42Fetch, probe42Refresh, probe42RequestAccess, probe42Usage, type DealDto, type CallInput, type PeopleFetchResult, type PeopleSourceReport } from '../lib/api.js';
import {
  AUTOPOP_STAGES, AUTOPOP_STEPS, AUTOPOP_LABELS, shouldRunStep, shouldAutoRunStep,
  needsAutoPopulate, unproductiveAttempts, type AutopopStepKey,
} from '../lib/autopop.js';
import type { PeerDetail, PeerRow as PeerRowT, IpoHistory, IpoField, IpoDetails, ResearchDossier, PersonDepth, TeamSynthesisItem, PromoterView, CreditRating, AgencyRatings, DataRoomFile } from '@krit/core';
import { extractDeckWithMeta, extractDeckText, renderPdfToImages, isLikelyScanned } from '../lib/pdf.js';
import { extractAudioToWav } from '../lib/audio.js';
import { EditDealModal } from '../components/EditDealModal.js';
import { Modal } from '../components/Modal.js';
import { ChatWidget } from '../components/ChatWidget.js';
import { Discussion } from '../components/Discussion.js';
import { DealTasks } from '../components/Tasks.js';
import { Pill, Spinner, Skeleton, StagedLoader, InlineBusy, ProgressBar, ComingSoon } from '../components/ui.js';
import { Menu, MenuItem, MenuSeparator } from '../components/Menu.js';
import { StagePill } from '../components/StagePill.js';
import { Financials } from '../components/Financials.js';
import { humanize, P42Table, P42Section, isArrayOfRecords, SectionInsight, ViaRegistryIntel, useInsightRefresh } from '../lib/probe42Format.js';

/* ---------- UI (shared kit look; local wrapper keeps mb-4 stacking) ---------- */
function Card({ title, sub, children, actions }: { title?: string; sub?: ReactNode; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="bg-card border border-line rounded-xl shadow-sh mb-4">
      {(title || actions) && (
        <header className="flex justify-between items-center gap-3 px-5 py-3.5 border-b border-line-2">
          <div className="min-w-0">{title && <h3 className="text-[13.5px] font-semibold text-navy tracking-[-0.1px]">{title}</h3>}{sub && <div className="text-[11px] text-muted mt-0.5">{sub}</div>}</div>
          {actions && <div className="flex items-center gap-2 flex-none">{actions}</div>}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}
function DL({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12.5px]">
      {rows.map(([k, v], i) => (
        <div key={i} className="contents"><dt className="text-muted font-semibold">{k}</dt><dd className="text-navy font-semibold text-right">{v}</dd></div>
      ))}
    </dl>
  );
}
function DataTable({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <table className="w-full border-collapse text-[12px]">
      <thead><tr>{head.map((h, i) => <th key={i} className={`text-left text-muted font-bold text-[10.5px] uppercase px-2 py-1.5 border-b border-line ${i > 0 ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className={`px-2 py-1.5 border-b border-line-2 ${j > 0 ? 'text-right mono' : ''}`}>{c}</td>)}</tr>)}</tbody>
    </table>
  );
}
function H4({ children }: { children: ReactNode }) { return <h4 className="text-navy text-[13.5px] mt-4 mb-1.5 font-semibold">{children}</h4>; }

/* ---------- loading skeletons (match the real layout) ---------- */
/** Full-page skeleton shown while the deal itself loads: gradient header shell + tab bar + body cards. */
function DealSkeleton() {
  return (
    <div aria-busy="true" role="status" aria-label="Loading deal">
      <div className="text-[11px] text-muted mb-2">Deals / …</div>
      <div className="bg-gradient-to-br from-navy to-navy-2 rounded-xl px-5 py-5 mb-3.5">
        <Skeleton w={220} h={22} className="bg-white/20 mb-2" />
        <Skeleton w={320} h={12} className="bg-white/15 mb-4" />
        <div className="flex gap-6 flex-wrap">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}><Skeleton w={54} h={9} className="bg-white/15 mb-1.5" /><Skeleton w={64} h={18} className="bg-white/20" /></div>
          ))}
        </div>
      </div>
      <div className="flex gap-3 border-b border-line mb-4 pb-2 flex-wrap">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} w={70} h={14} />)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <section key={i} className="bg-card border border-line rounded-xl shadow-sh">
            <div className="px-5 py-3.5 border-b border-line-2"><Skeleton w={140} h={13} /></div>
            <div className="p-5"><Skeleton lines={4} h={11} /></div>
          </section>
        ))}
      </div>
    </div>
  );
}

/** Placeholder rows for a table body while section data is being (re)fetched. */
function TableRowsSkeleton({ cols, rows = 3 }: { cols: number; rows?: number }) {
  return (
    <tbody aria-hidden="true">
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }).map((_, c) => (
            <td key={c} className={`px-2 py-1.5 border-b border-line-2 ${c > 0 ? 'text-right' : ''}`}>
              <Skeleton w={c === 0 ? '70%' : '55%'} h={11} className={c > 0 ? 'ml-auto' : ''} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/** Standalone table skeleton (header + rows) for peer / document / calls sections. */
function TableSkeleton({ head, rows = 3 }: { head: string[]; rows?: number }) {
  return (
    <table className="w-full border-collapse text-[12px]">
      <thead><tr>{head.map((h, i) => <th key={i} className={`text-left text-muted font-bold text-[10.5px] uppercase px-2 py-1.5 border-b border-line ${i > 0 ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
      <TableRowsSkeleton cols={head.length} rows={rows} />
    </table>
  );
}

const FIT_TONE: Record<string, string> = { Fit: 'green', Borderline: 'amber', Pass: 'red' };
const STATUS_TONE: Record<string, string> = {
  Open: 'gray', 'Under Review': 'teal', Interested: 'green', 'In Discussion': 'purple', Diligence: 'amber',
  'IC Review': 'purple', 'Term Sheet': 'green', Invested: 'teal', 'Not Interested': 'red', Intake: 'gold',
};
const REASON_TONE: Record<string, string> = { ok: 'bg-green-50 text-green', warn: 'bg-amber-50 text-amber', bad: 'bg-red-50 text-red' };

const TABS = [
  'Overview', 'Team & Mgmt', 'Compliance', 'Business', 'Research', 'Financials', 'Valuation',
  'Peers & Industry', 'Questions', 'Internal Calls', 'Earnings Calls', 'Discussion', 'Tasks', 'Krit Score', 'Analyst View', 'Documents', 'Registry Intelligence',
] as const;
type Tab = (typeof TABS)[number];

/** Lets a summary card on one tab jump straight to the detailed section on another (e.g. "209 cases
 *  → view all" on an Overview card switches to Compliance) without prop-drilling a callback through
 *  every tab component's signature. */
const TabNavContext = createContext<(t: Tab) => void>(() => {});
/** A summary card's "→ View in <Tab>" link — consistent styling everywhere one is used. */
function JumpToTab({ tab, label }: { tab: Tab; label?: string }) {
  const goTo = useContext(TabNavContext);
  return (
    <button onClick={() => goTo(tab)} className="text-[11px] font-semibold text-teal-d hover:underline whitespace-nowrap">
      {label ?? `View in ${tab} →`}
    </button>
  );
}


/**
 * Client-sequenced auto-populate. Runs each pass as its OWN foreground request, awaiting each
 * in turn (fail-soft — one failing pass never sinks the rest), invalidating the deal query
 * after EACH so the tabs fill progressively. `current` drives the top-of-page StagedLoader with
 * REAL progress.
 *
 * Three ways to run, differing ONLY in which passes are selected (see @krit/core `autopop.ts`):
 *   • auto first-open  — output missing AND the pass has never been attempted on this deal.
 *   • `ignoreAttempts` — blanks-only but disregarding the ledger (a NEW deck was just uploaded, so
 *                        passes that previously had nothing to work from deserve another go).
 *   • `force`          — the ↻ buttons: re-run every pass with `fresh: true` (spends credits).
 *
 * The attempt ledger is what keeps the auto run to ONCE per deal. Gating on "is the output column
 * still null?" alone meant a pass that ran and legitimately produced nothing (no groundable deck
 * text, no people, an upstream error) re-fired on EVERY open of that deal — the loader appeared
 * every time and the credits were re-spent every time.
 */
function useAutoPopulate(deal: DealDto | undefined, id: string, qc: ReturnType<typeof useQueryClient>, mayWrite: boolean) {
  const [state, setState] = useState<{ running: boolean; current: number; done: boolean; errors: Partial<Record<AutopopStepKey, string>> }>(
    { running: false, current: 0, done: false, errors: {} },
  );
  const inFlight = useRef(false);
  const startedRef = useRef(false);
  const dealRef = useRef<DealDto | undefined>(deal);
  dealRef.current = deal;

  const run = useCallback(async (opts?: { force?: boolean; only?: AutopopStepKey; ignoreAttempts?: boolean }) => {
    if (inFlight.current) return;
    const snap = dealRef.current;
    if (!snap) return;
    inFlight.current = true;
    const errors: Partial<Record<AutopopStepKey, string>> = {};
    setState({ running: true, current: 0, done: false, errors });
    for (let i = 0; i < AUTOPOP_STEPS.length; i++) {
      const step = AUTOPOP_STEPS[i]!;
      setState((s) => ({ ...s, current: i }));
      // `only` → run just that one pass; `force` → re-run even when the deal already has the data
      // (manual buttons re-fetch + re-distribute); otherwise select per the gate above.
      if (opts?.only && step.key !== opts.only) continue;
      const selected = opts?.force || (opts?.ignoreAttempts ? shouldRunStep(step.key, snap) : shouldAutoRunStep(step.key, snap));
      if (!selected) continue;
      try {
        // A FORCE run passes fresh:true → bypass the 24h enrichment cache (fetch new web data,
        // spends credits). An auto first-open run passes fresh:false → served free from cache.
        await step.run(id, !!opts?.force);
      } catch (e) {
        errors[step.key] = String((e as Error).message);
        setState((s) => ({ ...s, errors: { ...errors } }));
      }
      // Refresh after EACH pass so the tabs fill progressively as data lands.
      qc.invalidateQueries({ queryKey: ['deal', id] });
    }
    setState((s) => ({ ...s, running: false, done: true, current: AUTOPOP_STEPS.length }));
    inFlight.current = false;
  }, [id, qc]);

  // Auto-trigger ONCE per deal: a deck-bearing deal with at least one pass whose output is missing
  // and that has never been attempted. A pass added after the deal was enriched has no ledger entry,
  // so it still backfills on the next open — then records its outcome and stops asking.
  // `mayWrite` — every pass route is analyst+, so firing this for a viewer would just 403 nine times
  // per deal open (and, before the ledger learned to ignore caller-fault statuses, stamp the whole
  // ledger `error` and suppress auto-populate for the analyst who opened it next).
  useEffect(() => {
    if (!deal || !mayWrite || startedRef.current || inFlight.current) return;
    // Data-room deals do NOT auto-populate on open — they carry a big document set and firing the
    // full enrichment chain automatically overlapped with data-room extraction and exhausted the DB
    // pooler. Analysts run it deliberately via the "↻ Auto-populate" button instead.
    if (deal.intel?.dataroom) return;
    if (needsAutoPopulate(deal)) {
      startedRef.current = true;
      void run();
    }
  }, [deal, mayWrite, run]);

  return { ...state, run };
}

/* ---------- page ---------- */
// Deals whose Screener sync we've already auto-kicked this session (fire competitor calls once).
const screenerAutoTried = new Set<string>();
export function DealDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('Overview');
  const [editing, setEditing] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery({ queryKey: ['deal', id], queryFn: () => getDeal(id), retry: 2, retryDelay: 800 });
  const { data: settingsData } = useQuery({ queryKey: ['settings'], queryFn: getSettings, retry: 1, staleTime: 60_000 });

  const weights = (settingsData?.settings?.weights ?? DEFAULT_WEIGHTS) as Record<ModuleKey, number>;
  const icThreshold = settingsData?.settings?.icThreshold ?? DEFAULT_IC_THRESHOLD;

  const patch = useMutation({
    mutationFn: (p: Partial<Deal>) => patchDeal(id, p),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['deal', id] }); qc.invalidateQueries({ queryKey: ['deals'] }); },
  });

  // Archiving is reversible, so it only needs a light confirm. The deal vanishes from the board
  // on success, so there's nothing left to look at here — go back to the list.
  const archive = useMutation({
    mutationFn: () => archiveDeal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['archivedDeals'] });
      navigate('/deals');
    },
  });

  // Restore, for an admin who opened an archived deal from the archive.
  const restore = useMutation({
    mutationFn: () => unarchiveDeal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deal', id] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['archivedDeals'] });
    },
  });

  const { data: meData } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false, staleTime: 300_000 });
  const admin = isAdmin(meData?.identity);
  const del = useMutation({
    mutationFn: () => deleteDeal(id),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['deal', id] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['archivedDeals'] });
      navigate('/deals');
    },
    onError: (e) => alert(`Couldn't delete: ${String((e as Error).message)}`),
  });

  // ── Auto-populate: client-sequenced foreground passes (see useAutoPopulate) ──
  const autopop = useAutoPopulate(data?.deal, id, qc, canEdit(meData?.identity));

  // New deals don't get competitor calls until the weekly Screener sync — so once auto-populate has
  // set the peer list and there are no calls yet, kick the Screener sync once (own + competitor
  // concalls → the Calls/Transcripts tabs + industry trend). Guarded per-deal so it fires just once.
  useEffect(() => {
    const dl = data?.deal;
    if (!dl || !autopop.done || !canEdit(meData?.identity)) return;
    if (screenerAutoTried.has(id) || (dl.peers?.length ?? 0) === 0 || dl.calls.length > 0) return;
    screenerAutoTried.add(id);
    screenerSync(id).then(() => qc.invalidateQueries({ queryKey: ['deal', id] })).catch(() => {});
  }, [autopop.done, data?.deal, id, meData, qc]);

  if (isLoading) return <DealSkeleton />;
  if (error || !data) return (
    <div className="p-10 text-center text-muted">
      <p className="mb-1">Couldn't load this deal.</p>
      {error ? <p className="text-[11px] text-muted-2 mb-3">{String((error as Error).message)}</p> : null}
      <div className="flex gap-2 justify-center">
        <button onClick={() => refetch()} disabled={isFetching} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white disabled:opacity-50">{isFetching ? 'Retrying…' : 'Try again'}</button>
        <Link to="/deals" className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line">← Back to deals</Link>
      </div>
    </div>
  );

  const d = data.deal;
  const v = valn(d);
  const s = score(d, weights);
  const gr = grade(s);
  const f = thesisFit(d);
  const r = rec(d, weights, icThreshold);
  const unproductive = unproductiveAttempts(d);

  return (
    <div>
      <div className="text-[11px] text-muted mb-2"><Link to="/deals" className="hover:text-teal-d">Deals</Link> / {d.short}</div>

      {/* header */}
      <div className="bg-gradient-to-br from-navy to-navy-2 text-white rounded-xl px-5 py-5 mb-3.5 relative overflow-hidden">
        <h1 className="text-[22px] text-white relative">{d.company}</h1>
        <div className="text-[12.5px] text-[#adc0dc] mt-1 relative">{d.sector} · {d.location} · {d.banker} · POC {d.poc || '—'}</div>
        {/* Krit Score/Alloc are analyst-entered (module ratings, a proposed allocation) — genuinely
            blank until someone rates/prices the deal, not a bug. Show that plainly instead of a bare
            "0 (C)"/"₹0 Cr", which reads as a real (bad) score rather than "not yet rated/set".
            Impl P/E · MoS · Base MOIC need a positive FY26 PAT (the engine's "current year" anchor) —
            correctly "—" when the deal has no forward FY26 figure yet (e.g. a registry-sourced
            company whose latest FILED accounts only reach FY25; FY26 simply hasn't been filed). */}
        {(() => {
          const hasRatings = !isDefaultRatings(d.ratings);
          const hasAlloc = num(d.allocation) > 0;
          return (
            <div className="flex gap-6 mt-3.5 relative flex-wrap">
              {([
                ['Krit Score', hasRatings ? `${s} (${gr.g})` : 'Not rated', hasRatings ? undefined : 'No module ratings entered yet — rate the deal on the Krit Score tab.'],
                ['Thesis', f.verdict, undefined],
                ['Impl P/E', v.ok ? xx(v.entryPE) : '—', v.ok ? undefined : 'Needs a positive FY26 PAT — this deal has no FY26 figure yet (see Financials).'],
                ['MoS', v.ok ? pct(v.mos) : '—', v.ok ? undefined : 'Needs a positive FY26 PAT — this deal has no FY26 figure yet (see Financials).'],
                ['Base MOIC', v.ok ? xx(v.base) : '—', v.ok ? undefined : 'Needs a positive FY26 PAT — this deal has no FY26 figure yet (see Financials).'],
                ['Alloc', hasAlloc ? inr(d.allocation) : 'Not set', hasAlloc ? undefined : 'No proposed allocation entered yet.'],
              ] as [string, string, string | undefined][]).map(([l, val, hint]) => (
                <div key={l} title={hint}><div className="text-[9px] text-[#8ba3c7] uppercase tracking-[0.5px] font-bold">{l}</div><div className="text-[18px] font-extrabold mt-0.5">{val}</div></div>
              ))}
            </div>
          );
        })()}
        {/* Two controls only: the stage (the one thing you change often, and a status indicator in
            its own right) and an overflow menu for everything else. This used to be five buttons
            in a row, with permanent Delete sitting at the same visual weight as Archive. */}
        <div className="absolute right-5 top-5 flex gap-2 items-center">
          <StagePill
            status={d.status}
            disabled={patch.isPending}
            onChange={(s) => patch.mutate({ status: s })}
          />
          <Menu
            label="Deal actions"
            trigger={(open) => (
              <span className={`inline-flex items-center justify-center w-[34px] h-[30px] rounded-lg border text-white text-[15px] leading-none
                ${open ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/20'}`}>⋯</span>
            )}
          >
            {(close) => (
              <>
                <MenuItem
                  icon="↻"
                  disabled={autopop.running}
                  note="Fresh web fetch — uses Firecrawl credits"
                  onClick={() => { autopop.run({ force: true }); close(); }}
                >
                  {autopop.running ? 'Auto-populating…' : 'Auto-populate'}
                </MenuItem>
                <MenuItem icon="✎" onClick={() => { setEditing(true); close(); }}>Edit deal</MenuItem>

                {/* Archive: everyone. Restore: admins, and only once it's archived. */}
                {d.archivedAt ? (
                  admin && (
                    <MenuItem icon="↩" disabled={restore.isPending}
                      note="Put it back in the pipeline"
                      onClick={() => { restore.mutate(); close(); }}>
                      {restore.isPending ? 'Restoring…' : 'Restore'}
                    </MenuItem>
                  )
                ) : (
                  <MenuItem icon="🗄" disabled={archive.isPending}
                    note="Off the board — reversible"
                    onClick={() => {
                      if (confirm(`Archive ${d.company}?\n\nIt will be removed from the deal board, Intake, Monitoring and the WhatsApp bot. Nothing is deleted — an admin can restore it from the Archive.`)) archive.mutate();
                      close();
                    }}>
                    {archive.isPending ? 'Archiving…' : 'Archive'}
                  </MenuItem>
                )}

                {admin && (
                  <>
                    <MenuSeparator />
                    <MenuItem icon="🗑" tone="danger" trailing="admin" disabled={del.isPending}
                      note="Permanent — cannot be undone"
                      onClick={() => {
                        if (confirm(`Delete "${d.company}" permanently? This removes the deal and its documents, calls, tasks and notes. This cannot be undone.\n\nArchive it instead if you just want it off the board.`)) del.mutate();
                        close();
                      }}>
                      {del.isPending ? 'Deleting…' : 'Delete deal…'}
                    </MenuItem>
                  </>
                )}
              </>
            )}
          </Menu>
        </div>
      </div>

      {editing && <EditDealModal deal={d} saving={patch.isPending} onClose={() => setEditing(false)} onSave={(p) => patch.mutate(p, { onSuccess: () => setEditing(false) })} />}

      {d.archivedAt && (
        <div className="mb-3.5 rounded-lg border border-amber/30 bg-amber-50 px-4 py-2.5 text-[11.5px] text-amber">
          This deal is archived — it doesn’t appear on the board, in Monitoring, or in bot answers.
          {admin ? ' Restore it above, or delete it for good.' : ' An admin can restore it from the Archive.'}
        </div>
      )}

      {(archive.error || restore.error) && (
        <div className="mb-3.5 rounded-lg border border-red/30 bg-red-50 px-4 py-2.5 text-[11.5px] text-red">
          {archive.error ? 'Couldn’t archive this deal: ' : 'Couldn’t restore this deal: '}
          {String(((archive.error ?? restore.error) as Error).message)}
        </div>
      )}

      {autopop.running && (
        <StagedLoader stages={AUTOPOP_STAGES} current={autopop.current} settled={autopop.done} className="mb-3.5"
          note="Auto-populating this deal — deck, registry, industry, peers & an open-ended research dossier, each as its own request. You can keep working; tabs fill in as each pass lands." />
      )}
      {!autopop.running && Object.keys(autopop.errors).length > 0 && (
        <div className="mb-3.5 rounded-lg border border-amber/30 bg-amber-50 px-4 py-2.5 text-[11.5px] text-amber">
          Some passes didn’t complete: {Object.entries(autopop.errors).map(([k, v]) => `${k} (${v})`).join(' · ')}. Click “↻ Auto-populate” to retry the rest.
        </div>
      )}
      {/* Persisted counterpart of the banner above: passes that already RAN for this deal and came
          back with nothing. Auto-populate won't retry these on its own (that's what used to make the
          loader appear on every open), so say so and point at the manual retry. */}
      {!autopop.running && !Object.keys(autopop.errors).length && unproductive.length > 0 && (
        <div className="mb-3.5 rounded-lg border border-line bg-white px-4 py-2.5 text-[11.5px] text-muted">
          Nothing to show for {unproductive.map((u) => AUTOPOP_LABELS[u.key]).join(' · ')} — {unproductive.length === 1 ? 'that pass' : 'those passes'} already ran on this deal and found no usable data,
          so they aren’t retried automatically. Click “↻ Auto-populate” to try again with a fresh fetch.
        </div>
      )}

      {/* tabs */}
      <div className="flex gap-0.5 border-b border-line mb-4 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3.5 py-2 text-[12.5px] font-semibold whitespace-nowrap border-b-2 ${tab === t ? 'text-navy border-teal' : 'text-muted border-transparent hover:text-navy'}`}>{t}</button>
        ))}
      </div>

      <TabNavContext.Provider value={setTab}>
      {tab === 'Overview' && <Overview d={d} f={f} r={r} v={v} dealId={id} onChanged={() => qc.invalidateQueries({ queryKey: ['deal', id] })} />}
      {tab === 'Team & Mgmt' && <TeamTab d={d} dealId={id} onChanged={() => qc.invalidateQueries({ queryKey: ['deal', id] })} />}
      {tab === 'Compliance' && <Compliance d={d} dealId={id} onPatch={(p) => patch.mutate(p)} onChanged={() => qc.invalidateQueries({ queryKey: ['deal', id] })} />}
      {tab === 'Business' && <BusinessTab d={d} dealId={id} onChanged={() => qc.invalidateQueries({ queryKey: ['deal', id] })} />}
      {tab === 'Research' && <ResearchTab d={d} autopopRunning={autopop.running} onRefresh={() => autopop.run({ force: true, only: 'research' })} />}
      {tab === 'Financials' && <Financials d={d} dealId={id} onChanged={() => qc.invalidateQueries({ queryKey: ['deal', id] })} />}
      {tab === 'Valuation' && <Valuation d={d} onPatch={(p) => patch.mutate(p)} />}
      {tab === 'Peers & Industry' && <Peers d={d} onPatch={(p) => patch.mutate(p)} />}
      {tab === 'Questions' && <QuestionsTab d={d} dealId={id} />}
      {tab === 'Internal Calls' && <InternalCalls d={d} dealId={id} onChanged={() => qc.invalidateQueries({ queryKey: ['deal', id] })} />}
      {tab === 'Earnings Calls' && <ExternalCalls d={d} dealId={id} onChanged={() => qc.invalidateQueries({ queryKey: ['deal', id] })} />}
      {tab === 'Discussion' && <Discussion dealId={id} />}
      {tab === 'Tasks' && <DealTasks dealId={id} />}
      {tab === 'Krit Score' && <ScoreTab d={d} weights={weights} icThreshold={icThreshold} onSave={(ratings) => patch.mutate({ ratings })} />}
      {tab === 'Analyst View' && <Analyst d={d} onPatch={(p) => patch.mutate(p)} />}
      {tab === 'Documents' && <Docs d={d} dealId={id} onChanged={() => qc.invalidateQueries({ queryKey: ['deal', id] })} onUploaded={() => autopop.run({ ignoreAttempts: true })} />}
      {tab === 'Registry Intelligence' && <Probe42Tab d={d} dealId={id} admin={admin} onChanged={() => qc.invalidateQueries({ queryKey: ['deal', id] })} />}
      </TabNavContext.Provider>
      <ChatWidget dealId={id} label={d.company} />
    </div>
  );
}

/* ---------- Registry Intelligence (chargeable corporate-data) tab ---------- */
// Never shown as "Probe42" in the product — the tab, buttons and copy all say "Registry
// Intelligence"; internal identifiers (this component, the API routes/audit actions) keep the
// probe42* names since those never render to a viewer. See decision.md D18.
//
// The curated sections shown here — everything ELSE this data maps onto the deal (directors,
// shareholding, charges, compliance/litigation, credit ratings, financial history, GST/legal detail,
// peer comparison) now lives in its natural home (Team & Mgmt / Compliance / Financials / Peers &
// Industry / Overview, all source-tagged) instead of being duplicated here — see decision.md D17/D18.
// This tab stays focused on the snapshot + Documents + the true long tail (group structure and
// anything else with no existing home).
const P42_SECTIONS: [string, keyof import('@krit/core').Probe42Data][] = [
  ['Key statistics', 'keyStatistics'], ['Overview', 'overview'], ['Documents', 'documents'],
];
// Raw top-level keys already folded into a curated field above OR merged straight into the deal
// elsewhere — skipped here so nothing renders twice. Everything else this data returns (related-party
// transactions, group structure, name history, …) still gets its own labelled section instead of only
// being reachable via "raw JSON".
const P42_CURATED_RAW_KEYS = new Set([
  'company', 'llp', 'pnp', 'metadata', 'authorized_signatories', 'directors', 'director_details',
  'shareholdings', 'shareholding', 'shareholders', 'shareholdings_more_than_five_percent', 'shareholdings_summary',
  'director_shareholdings', 'open_charges', 'charges', 'credit_ratings', 'ratings', 'credit_rating_rationale',
  'unaccepted_rating', 'key_indicators', 'probe_financial_score', 'key_statistics', 'keyStatistics',
  'struckoff248_details', 'defaulter_list', 'legal_history', 'legal_cases_of_financial_disputes', 'compliance',
  'financials', 'financial_details', 'nbfc_financials', 'financial_parameters', 'documents', 'gst_details',
  'msme_supplier_payment_delays', 'establishments_registered_with_epfo', 'peer_comparison',
  'description', 'name_history', 'contact_details', 'industry_segments', 'principal_business_activities', 'filing_dates',
  // Corporate structure / related-party — given their own dedicated block below (product review item
  // 14/15) instead of the generic "extra" dump; director_network moves to Team & Mgmt (item 3).
  'holding_entities', 'subsidiary_entities', 'associate_entities', 'joint_ventures', 'related_party_transactions',
  'director_network',
]);
/** Every other real top-level section this data returned, sorted alphabetically for a stable layout. */
function extraProbe42Sections(raw: unknown): [string, unknown][] {
  const root = ((raw as Record<string, unknown> | null)?.data ?? raw ?? {}) as Record<string, unknown>;
  if (!root || typeof root !== 'object') return [];
  return Object.entries(root)
    .filter(([k, v]) => !P42_CURATED_RAW_KEYS.has(k) && v !== null && v !== undefined
      && !(Array.isArray(v) && !v.length) && !(typeof v === 'object' && !Array.isArray(v) && !Object.keys(v as object).length))
    .sort(([a], [b]) => a.localeCompare(b));
}
/** ₹ chargeable marker so paid actions are unmistakable (decision.md D4). */
const ChargeChip = () => <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber border border-amber/40">₹ chargeable</span>;

/**
 * Corporate structure (holding/subsidiary/associate/JV) + related-party transactions — a dedicated
 * block (product review items 14/15), not mixed into generic sections. AI analysis on top (grounded,
 * "insufficient data" if there's nothing to say), each group in its own labelled table below, kept
 * distinct from director info (that's Team & Mgmt's job — item 3).
 */
function CorpStructureAndRelatedParty({ raw, dealId }: { raw: unknown; dealId: string }) {
  const root = ((raw as Record<string, unknown> | null)?.data ?? raw ?? {}) as Record<string, any>;
  const groups: [string, any[]][] = [
    ['Holding company', root.holding_entities], ['Subsidiaries', root.subsidiary_entities],
    ['Associate companies', root.associate_entities], ['Joint ventures', root.joint_ventures],
  ].filter((g): g is [string, any[]] => Array.isArray(g[1]) && g[1].length > 0);
  const rpt = Array.isArray(root.related_party_transactions) ? root.related_party_transactions : [];
  if (!groups.length && !rpt.length) return null;
  return (
    <div className="mb-3 pt-3 border-t border-line-2">
      <div className="text-[11px] font-semibold text-muted uppercase tracking-wide mb-2">Corporate structure & related parties</div>
      <SectionInsight dealId={dealId} section="relatedParty" label="Related-party analysis" />
      {groups.map(([title, items]) => (
        <div key={title} className="mb-3">
          <div className="text-[12px] font-semibold text-navy mb-1.5">{title} <span className="text-muted-2 font-normal">({items.length})</span></div>
          <P42Table rows={items} />
        </div>
      ))}
      {rpt.length > 0 && (
        <div className="mb-1">
          <div className="text-[12px] font-semibold text-navy mb-1.5">Related-party transactions <span className="text-muted-2 font-normal">({rpt.length})</span></div>
          <P42Table rows={rpt} />
        </div>
      )}
    </div>
  );
}

function Probe42Tab({ d, dealId, admin, onChanged }: { d: Deal; dealId: string; admin: boolean; onChanged: () => void }) {
  const qc = useQueryClient();
  const refreshInsight = useInsightRefresh(dealId);
  // Always fire the GET — for a non-admin, a 403 here is the reliable signal that they're not
  // (yet) granted access (decision.md D15), rather than trusting only the client-side `admin` flag.
  const { data: res, isLoading, error: getErr } = useQuery({ queryKey: ['probe42', dealId], queryFn: () => probe42Get(dealId), retry: false, staleTime: 60_000 });
  const hasAccess = admin || (!!res && !getErr);
  const { data: usageRes } = useQuery({ queryKey: ['probe42-usage', dealId], queryFn: () => probe42Usage(dealId), enabled: hasAccess, retry: false, staleTime: 30_000 });
  const [busy, setBusy] = useState<'fetch' | 'refresh' | 'req' | null>(null);
  const [msg, setMsg] = useState('');
  const [consentFor, setConsentFor] = useState<'fetch' | 'refresh' | null>(null);

  // Not yet granted — admins always pass; everyone else needs an explicit grant (decision.md D3/D15).
  // Only a way to ask; the actual chargeable controls never render until access is confirmed.
  if (!hasAccess) {
    if (isLoading) return <Card title="Registry Intelligence"><div className="text-[12.5px] text-muted">Loading…</div></Card>;
    return (
      <Card title="Registry Intelligence" sub="Licensed MCA/registry data. Chargeable — restricted to admins, or anyone an admin has individually granted.">
        <p className="text-[12.5px] text-muted mb-3">This pulls detailed corporate data (directors, charges, financials, compliance, GST, credit ratings, and more). Because each pull is billed, access is per-person — ask an admin to approve you in Settings → Registry Intelligence access.</p>
        <button disabled={busy === 'req'} onClick={async () => { setBusy('req'); setMsg(''); try { await probe42RequestAccess(dealId); setMsg('✓ Access requested — an admin can approve it in Settings → Registry Intelligence access.'); } catch (e) { setMsg(String((e as Error).message)); } finally { setBusy(null); } }}
          className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-navy text-white disabled:opacity-50">{busy === 'req' ? 'Requesting…' : 'Request Access'}</button>
        {msg && <span className="ml-3 text-[11.5px] text-muted">{msg}</span>}
      </Card>
    );
  }

  const identifier = res?.identifier ?? d.intel?.registry?.cin ?? d.intel?.registry?.pan ?? null;
  const data = res?.data ?? null;
  const configured = res?.configured ?? false;

  async function run(kind: 'fetch' | 'refresh') {
    setBusy(kind); setMsg('');
    try {
      if (kind === 'fetch') { const r = await probe42Fetch(dealId, { fresh: true, consent: true }); qc.setQueryData(['probe42', dealId], (p: typeof res | undefined) => ({ ...(p ?? { configured, identifier, entityType: null }), data: r.data })); setMsg('✓ Pulled comprehensive data.'); onChanged(); refreshInsight(['overview', 'litigation', 'gst', 'relatedParty', 'compliance']); }
      else { const r = await probe42Refresh(dealId, { consent: true }); setMsg(r.requestId ? `✓ Refresh requested (id ${r.requestId}) — fulfils in ~2–4h.` : '✓ Refresh requested.'); }
      qc.invalidateQueries({ queryKey: ['probe42-usage', dealId] });
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); setConsentFor(null); }
  }

  const usage = usageRes?.usage ?? [];
  return (
    <>
    <Card title="Registry Intelligence"
      sub={<>Licensed MCA/registry data. <span className="text-amber font-semibold">Actions marked ₹ are chargeable</span> — you’ll confirm before any billed call.{data?.sandbox && <span className="ml-1 px-1.5 py-0.5 rounded bg-canvas border border-line-2 text-[10px]">SANDBOX</span>}</>}
      actions={
        <div className="flex items-center gap-2">
          <button disabled={!configured || !identifier || !!busy} onClick={() => setConsentFor('fetch')}
            className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">{busy === 'fetch' ? 'Fetching…' : (data ? 'Re-pull' : 'Fetch registry intelligence')}<ChargeChip /></button>
          {data && <button disabled={!configured || !identifier || !!busy} onClick={() => setConsentFor('refresh')} title="Ask the source to refresh its data (takes ~2–4h)"
            className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">{busy === 'refresh' ? 'Requesting…' : 'Refresh source'}<ChargeChip /></button>}
        </div>
      }>
      {!configured && <div className="mb-3 text-[12px] text-amber bg-amber-50 border border-amber/40 rounded-lg px-3 py-2">Registry Intelligence isn’t configured yet (the API key isn’t set). The buttons will work once it’s added.</div>}
      {!identifier && <div className="mb-3 text-[12px] text-muted bg-canvas border border-line-2 rounded-lg px-3 py-2">No CIN / PAN / LLPIN on this deal yet — add one in <b>Team&nbsp;&amp;&nbsp;Mgmt → registry</b> so Registry Intelligence knows which entity to pull.</div>}
      {identifier && <div className="text-[11.5px] text-muted mb-3">Entity: <span className="font-mono text-navy">{identifier}</span>{data?.legalName && <> · <span className="text-navy font-semibold">{data.legalName}</span></>}</div>}
      {msg && <div className={`mb-3 text-[12px] ${msg.startsWith('✓') ? 'text-green' : 'text-red'}`}>{msg}</div>}

      {isLoading ? <div className="text-[12px] text-muted">Loading…</div>
        : data ? (
          <div>
            <div className="text-[11px] text-muted mb-3">Pulled {data.fetchedAt ? new Date(data.fetchedAt).toLocaleString() : '—'}{data.lastUpdated && <> · data as of {data.lastUpdated}</>}</div>
            <div className="mb-3 text-[11.5px] text-muted bg-canvas border border-line-2 rounded-lg px-3 py-2">
              Directors, shareholding and registered charges are on <b>Team&nbsp;&amp;&nbsp;Mgmt</b>; rating, GST/PF status and litigation are on <b>Compliance</b>; filed financials are on <b>Financials</b>; industry benchmark is on <b>Peers&nbsp;&amp;&nbsp;Industry</b> — all tagged "via Registry Intelligence". This tab has the raw snapshot and everything else without an existing home.
            </div>
            {P42_SECTIONS.map(([title, key]) => <P42Section key={key} title={title} value={(data as unknown as Record<string, unknown>)[key]} />)}
            <CorpStructureAndRelatedParty raw={data.raw} dealId={dealId} />
            {(() => {
              const extra = extraProbe42Sections(data.raw);
              return extra.length > 0 ? (
                <div className="mt-3 pt-3 border-t border-line-2">
                  <div className="text-[11px] font-semibold text-muted uppercase tracking-wide mb-2">More sections ({extra.length})</div>
                  {extra.map(([key, value]) => <P42Section key={key} title={humanize(key)} value={value} collapsed />)}
                </div>
              ) : null;
            })()}
            <details className="mt-2"><summary className="text-[11.5px] text-teal-d cursor-pointer font-semibold">View raw JSON (reference)</summary>
              <pre className="mt-2 text-[10.5px] leading-snug bg-canvas border border-line-2 rounded-lg p-2.5 overflow-auto max-h-96">{JSON.stringify(data.raw ?? data, null, 2)}</pre>
            </details>
          </div>
        ) : <div className="text-[12.5px] text-muted">No data pulled for this deal yet. Click <b>Fetch registry intelligence</b> above (chargeable) to pull it.</div>}

      {consentFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !busy && setConsentFor(null)}>
          <div className="bg-white rounded-xl shadow-xl border border-line max-w-md w-[92%] p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[14px] font-semibold text-navy mb-1">Confirm a chargeable Registry Intelligence call</h3>
            <p className="text-[12.5px] text-muted mb-1">This will {consentFor === 'fetch' ? 'pull comprehensive data' : 'request a source refresh'} for:</p>
            <p className="text-[13px] text-navy font-semibold mb-3">{data?.legalName || d.company} <span className="font-mono text-[11.5px] text-muted">({identifier})</span></p>
            <p className="text-[12px] text-amber bg-amber-50 border border-amber/40 rounded-lg px-3 py-2 mb-4">⚠ This is a <b>billed call</b>. It runs only after you confirm, and is logged.</p>
            <div className="flex justify-end gap-2">
              <button disabled={!!busy} onClick={() => setConsentFor(null)} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line disabled:opacity-50">Cancel</button>
              <button disabled={!!busy} onClick={() => run(consentFor)} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">{busy ? 'Working…' : 'Confirm & proceed'}</button>
            </div>
          </div>
        </div>
      )}
    </Card>
    {usage.length > 0 && (
      <Card title="Registry Intelligence usage" sub="Every billed call on this deal (from the audit log).">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead><tr className="text-muted text-left border-b border-line-2">
              <th className="py-1.5 pr-3 font-semibold">When</th><th className="py-1.5 pr-3 font-semibold">Who</th><th className="py-1.5 pr-3 font-semibold">Action</th><th className="py-1.5 font-semibold">Details</th>
            </tr></thead>
            <tbody>
              {usage.map((u) => (
                <tr key={u.id} className="border-b border-line-2/60">
                  <td className="py-1.5 pr-3 whitespace-nowrap text-muted">{new Date(u.ts).toLocaleString()}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-navy font-medium">{u.actor ?? '—'}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{u.action.replace(/^probe42-/, '')}{(u.diff as { chargeable?: boolean } | null)?.chargeable && <ChargeChip />}</td>
                  <td className="py-1.5 text-muted font-mono text-[11px] break-all">{(u.diff as { identifier?: string } | null)?.identifier ?? ''}{(u.diff as { requestId?: string } | null)?.requestId ? ` · req ${(u.diff as { requestId?: string }).requestId}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    )}
    </>
  );
}

/* ---------- tabs ---------- */
/** Small source chip — a link for URLs, a plain label for "uploaded deck" etc. */
function srcChip(url: string | null | undefined): ReactNode {
  if (!url) return null;
  return /^https?:\/\//i.test(url)
    ? <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[9.5px] text-teal-d underline shrink-0">↗ source</a>
    : <span className="text-[9.5px] text-muted-2 shrink-0">· {url}</span>;
}
/** A value + its source, for the At-a-glance rows. */
function glance(value: string | undefined, p: ProvenancedField | undefined): ReactNode {
  return <span className="inline-flex items-center gap-1.5">{value || '—'}{p?.sourceUrl && srcChip(p.sourceUrl)}</span>;
}
/** RegistryInfo's capital fields are typed as `string` but a Registry Intelligence pull actually
 *  stores the raw rupee number (Probe42 gives `paid_up_capital: 1996972240`, not a formatted
 *  string) — shown unformatted, that printed as a bare 10-digit number. Format either shape:
 *  a raw number → ₹ Cr; an already-formatted string (e.g. Tofler's "199.70 cr") → pass through. */
function fmtRupee(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return `₹${(v / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;
  const n = Number(v);
  return typeof v === 'string' && /^\d+$/.test(v.trim()) && Number.isFinite(n) ? `₹${(n / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr` : String(v);
}

/** Icon per at-a-glance fact — gives the strip visual anchors instead of a bare label/value list. */
const FACT_ICON: Record<string, string> = {
  Founded: '📅', Employees: '👥', Facilities: '🏭', Promoter: '👤', 'IPO target': '🎯', 'Credit rating': '⭐',
  Website: '🌐', Email: '✉️',
};
/** Icon per business narrative section — turns the accordion wall into a scannable briefing grid. */
const SECTION_ICON: Record<string, string> = {
  history: '🕰️', whatTheyMake: '🏭', businessModel: '💼', customersChannel: '🤝', operations: '⚙️',
  supplyChain: '🔗', moat: '🛡️', useOfProceeds: '💰', tailwinds: '📈',
};
const MOS_TONE = (ok: boolean, mos: number) => (!ok ? 'gray' : mos >= 30 ? 'green' : mos >= 15 ? 'amber' : 'red');
const GROWTH_TONE = (g: number) => (g >= 15 ? 'green' : g > 0 ? 'amber' : 'red');
const TONE_TEXT: Record<string, string> = { green: 'text-green', amber: 'text-amber', red: 'text-red', gray: 'text-muted-2' };
const TONE_BAR: Record<string, string> = { green: 'bg-green', amber: 'bg-amber', red: 'bg-red', navy: 'bg-navy', gray: 'bg-line' };

/** Small icon-led fact tile — replaces a plain DL row so profile facts read as data points, not a form. */
function FactTile({ icon, label, children }: { icon: string; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-line-2 bg-canvas px-3 py-2.5">
      <span className="text-[16px] leading-none mt-0.5 flex-none">{icon}</span>
      <div className="min-w-0">
        <div className="text-[9.5px] font-bold uppercase tracking-[0.5px] text-muted-2">{label}</div>
        <div className="text-[12.5px] font-semibold text-navy mt-0.5 break-words">{children}</div>
      </div>
    </div>
  );
}

/** Sidebar metric tile with an accent bar — a compact, colour-coded alternative to the KPI DL. */
function MetricTile({ label, value, tone = 'navy', note }: { label: string; value: ReactNode; tone?: string; note?: ReactNode }) {
  return (
    <div className="relative overflow-hidden rounded-lg border border-line-2 bg-canvas px-3 py-2.5">
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${TONE_BAR[tone] ?? 'bg-navy'}`} />
      <div className="text-[9.5px] font-bold uppercase tracking-[0.5px] text-muted-2 pl-1.5">{label}</div>
      <div className={`mt-0.5 pl-1.5 text-[16.5px] font-extrabold mono leading-none ${TONE_TEXT[tone] ?? 'text-navy'}`}>{value}</div>
      {note && <div className="pl-1.5 mt-1 text-[10px] text-muted-2">{note}</div>}
    </div>
  );
}

/** Deck-mined company briefing — the "read this instead of the PDF" block on the Overview. */
function BriefingBlock({ b }: { b: BusinessBrief }) {
  const sections = BRIEF_SECTIONS.filter((s) => b[s.key]?.content);
  const has = !!(b.history?.length || b.products?.length || b.keyNumbers?.length || b.openQuestions?.length || sections.length);
  if (!has) return null;
  return (
    <Card title="Company briefing" sub="Built from the uploaded deck — read this instead of the PDF">
      {b.keyNumbers?.length ? (
        <div className="flex flex-wrap gap-2 mb-3">
          {b.keyNumbers.map((k, i) => (
            <div key={i} className="px-2.5 py-1.5 rounded-lg bg-line-2/40 border border-line-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-2">{k.label}</div>
              <div className="text-[12.5px] font-semibold text-navy">{k.value}</div>
            </div>
          ))}
        </div>
      ) : null}
      {sections.map((s, i) => (
        <details key={s.key} open={i < 2} className="border-t border-line-2 py-1.5">
          <summary className="text-[11.5px] font-semibold text-navy cursor-pointer">{s.label}</summary>
          <p className="text-[12px] text-muted leading-relaxed mt-1 whitespace-pre-line">{b[s.key]!.content}</p>
        </details>
      ))}
      {b.products?.length ? (
        <div className="mt-3">
          <H4>Products</H4>
          <div className="overflow-x-auto">
            <table className="w-full text-[11.5px]">
              <thead><tr className="text-muted-2 text-left">
                <th className="py-1 pr-2">Product</th><th className="py-1 pr-2">What</th><th className="py-1 pr-2">Application</th>
                <th className="py-1 pr-2">Capacity</th><th className="py-1 pr-2">Rev share</th><th className="py-1 pr-2">Stage</th>
              </tr></thead>
              <tbody>
                {b.products.map((p, i) => (
                  <tr key={i} className="border-t border-line-2 align-top">
                    <td className="py-1 pr-2 font-semibold text-navy">{p.name}</td>
                    <td className="py-1 pr-2 text-muted">{p.what || '—'}</td>
                    <td className="py-1 pr-2 text-muted">{p.application || '—'}</td>
                    <td className="py-1 pr-2 text-muted">{p.capacity || '—'}</td>
                    <td className="py-1 pr-2 text-muted">{p.revenueShare || '—'}</td>
                    <td className="py-1 pr-2 text-muted">{p.stage || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {b.history?.length ? (
        <div className="mt-3">
          <H4>History</H4>
          <ul className="border-l-2 border-line-2 ml-1">
            {b.history.map((m, i) => (
              <li key={i} className="relative pl-3 pb-2 text-[12px]">
                <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-teal" />
                <span className="font-semibold text-navy mr-2">{m.date || '—'}</span>
                <span className="text-muted">{m.event}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {b.openQuestions?.length ? (
        <div className="mt-3">
          <H4>Open questions (diligence)</H4>
          <ul className="list-disc pl-4 text-[12px] text-muted space-y-0.5">
            {b.openQuestions.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}

/** Computed credit rating — grade, score bar, outlook, and the per-factor breakdown that explains it. */
function AgencyRatingsBlock({ dealId, ratings, onChanged }: { dealId: string; ratings?: AgencyRatings; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  async function run() {
    setBusy(true); setMsg('');
    try {
      const r = await fetchAgencyRatings(dealId);
      if (!r.agencyRatings.ratings.length) setMsg(r.agencyRatings.note || 'No published rating found.');
      onChanged();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(false); }
  }
  const list = ratings?.ratings ?? [];
  return (
    <div className="rounded-lg border border-line-2 bg-canvas px-3.5 py-3 mb-3.5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-muted-2">Credit rating <span className="normal-case font-semibold text-teal-d">· agencies (CRISIL / ICRA / CARE)</span></span>
        <button disabled={busy} onClick={run} className="text-[10.5px] font-semibold text-teal-d disabled:opacity-50">{busy ? <InlineBusy>Searching…</InlineBusy> : ratings ? '↻ Refresh' : '🔍 Fetch'}</button>
      </div>
      {list.length ? (
        <div className="space-y-2">
          {list.map((r, i) => (
            <div key={i} className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[12px] font-semibold text-navy">{r.agency} <span className="text-teal-d">{r.longTerm}</span>{r.shortTerm ? <span className="text-muted"> · {r.shortTerm}</span> : null}</div>
                {[r.instrument, r.action, r.amount, r.date].filter(Boolean).length > 0 && <div className="text-[10.5px] text-muted-2">{[r.instrument, r.action, r.amount, r.date].filter(Boolean).join(' · ')}</div>}
              </div>
              {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-[10.5px] font-semibold text-teal-d hover:underline flex-none">Report ↗</a>}
            </div>
          ))}
          {ratings?.fetchedAt && <div className="text-[9.5px] text-muted-2 pt-1">Fetched {new Date(ratings.fetchedAt).toLocaleDateString()} · from the agencies' public rationales</div>}
        </div>
      ) : (
        <p className="text-[11px] text-muted">{ratings ? (ratings.note || 'No published agency rating found in the public domain.') : 'Search CRISIL / ICRA / CARE / India Ratings for a published rating on this company.'}{msg && <span className="text-amber"> · {msg}</span>}</p>
      )}
    </div>
  );
}

function CreditRatingBlock({ credit }: { credit: CreditRating }) {
  const [open, setOpen] = useState(false);
  const bandTone: Record<string, string> = { strong: 'green', ok: 'amber', weak: 'red' };
  const outlookLabel = credit.outlook ? credit.outlook[0]!.toUpperCase() + credit.outlook.slice(1) : null;
  return (
    <div className="rounded-lg border border-line-2 bg-canvas px-3.5 py-3 mb-3.5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-muted-2">Credit rating <span className="normal-case font-semibold text-muted-2/80">· internal estimate</span></span>
        <div className="flex items-center gap-1.5">
          <Pill tone={credit.color}>{credit.grade}{credit.rated ? ` · ${credit.score}` : ''}</Pill>
          {credit.investmentGrade && <Pill tone="teal">IG</Pill>}
          {outlookLabel && <span className="text-[10px] text-muted-2">{outlookLabel}</span>}
        </div>
      </div>
      {credit.rated && <ProgressBar pct={credit.score} tone={credit.color} />}
      <p className="text-[11px] text-muted mt-2 leading-relaxed">{credit.rationale}</p>
      {credit.factors.some((fc) => fc.available) && (
        <>
          <button onClick={() => setOpen((o) => !o)} className="text-[10.5px] font-semibold text-teal-d mt-1.5">
            {open ? 'Hide factors' : 'Why this grade →'}
          </button>
          {open && (
            <div className="mt-2 space-y-1.5">
              {credit.factors.map((fc) => (
                <div key={fc.key} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className={fc.available ? 'text-navy' : 'text-muted-2'}>{fc.label}</span>
                  <span className="flex items-center gap-1.5 flex-none">
                    <span className={fc.available ? 'text-muted' : 'text-muted-2 italic'}>{fc.detail}</span>
                    {fc.available && <Pill tone={bandTone[fc.band]!}>{fc.band}</Pill>}
                  </span>
                </div>
              ))}
              <p className="text-[10px] text-muted-2 pt-1">Computed from financials · {Math.round(credit.coverage * 100)}% factor coverage. Distinct from any external agency rating.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * MCA directors in the company-profile (Overview) — the board of record, surfaced up front rather
 * than only on the Team & Mgmt tab. Fetches from the MCA registry (Tofler) on demand when empty,
 * so every company gets a director register with one click. `intel.directors` is the source.
 */
function BoardDirectors({ d, dealId, onChanged }: { d: DealDto; dealId: string; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const directors = d.intel?.directors ?? [];
  const reg = d.intel?.registry;

  async function fetchMca() {
    setBusy(true); setMsg('Querying the MCA registry (Tofler)…');
    try {
      const res = await peopleFetch(dealId, { fresh: true });
      const n = res.sources?.mca?.count ?? 0;
      setMsg(n ? `${n} director${n === 1 ? '' : 's'} on record from MCA.` : (res.sources?.mca?.note ?? 'No directors found on the registry.'));
      onChanged();
    } catch (e) { setMsg(`Failed: ${String((e as Error).message)}`); }
    finally { setBusy(false); }
  }

  return (
    <Card
      title="Board & directors"
      sub={directors.length
        ? <>MCA register · {directors.length} on record{reg?.fetchedAt ? ` · as of ${new Date(reg.fetchedAt).toLocaleDateString()}` : ''}</>
        : 'Directors of record from the MCA registry (Ministry of Corporate Affairs)'}
      actions={
        <button disabled={busy} onClick={fetchMca} title="Fetch the director register from the MCA registry (Tofler)"
          className="px-2.5 py-1.5 rounded-lg text-[11.5px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">
          {busy ? <InlineBusy>Fetching…</InlineBusy> : directors.length ? '↻ Refresh from MCA' : '🏛 Fetch from MCA'}
        </button>
      }>
      {msg && <div className={`text-[11.5px] mb-2 ${msg.startsWith('Failed') ? 'text-red' : 'text-muted'}`}>{msg}</div>}
      {directors.length ? (
        <>
          <div className="overflow-x-auto">
            <DataTable head={['Name', 'DIN', 'Designation', 'Appointed']}
              rows={directors.map((x) => [
                tidyName(x.name),
                x.din ? <span className="mono text-[11px] text-muted" title="Director Identification Number">{x.din}</span> : <span className="text-muted-2">—</span>,
                x.role,
                x.appointed || '—',
              ])} />
          </div>
          <p className="text-[11px] text-muted-2 mt-2">
            {reg?.source?.startsWith('http') ? <>Source: <a href={reg.source} target="_blank" rel="noreferrer" className="text-teal-d hover:underline">MCA registry</a> · </> : d.intel?.probe42 ? <>Source: Registry Intelligence · </> : null}
            Full board, web-found leadership & per-person research on the <span className="font-semibold text-navy">Team &amp; Mgmt</span> tab.
          </p>
        </>
      ) : (
        <Empty label={busy ? 'Fetching the director register…' : 'No MCA directors captured yet — click “🏛 Fetch from MCA”.'} />
      )}
    </Card>
  );
}

function Overview({ d, f, r, v, dealId, onChanged }: { d: DealDto; f: ReturnType<typeof thesisFit>; r: ReturnType<typeof rec>; v: ReturnType<typeof valn>; dealId: string; onChanged: () => void }) {
  const recTone = r.c === 'pass' ? 'green' : r.c === 'cond' ? 'amber' : 'red';
  const bd = d.businessDetail;
  const bdSections = bd ? BUSINESS_SECTIONS.filter((s) => bd[s.key]?.content) : [];
  const prov = d.provenance ?? {};
  const [bBusy, setBBusy] = useState(false);
  const [bMsg, setBMsg] = useState('');
  const fromDeck = bd?.builtFrom === 'deck';
  const g = growth(d);
  const growthTone = GROWTH_TONE(g);
  const mosTone = MOS_TONE(v.ok, v.mos);
  const peTone = v.ok && v.entryPE > v.peNow ? 'red' : 'green';
  const peMax = Math.max(v.ok ? v.entryPE : 0, v.peNow, 1);
  const reg0 = d.intel?.registry;
  const hasReg0 = !!(reg0 && (reg0.cin || reg0.llpin || reg0.pan || reg0.incorporationDate));
  // At-a-glance facts — ALWAYS rendered now (see the Company snapshot card below). Previously these
  // only showed inside the deck-derived Business narrative, so a deal with no deck and no web-written
  // summary (Coca-Cola, this session) showed NONE of them even though Founded/Employees/Website/Email
  // were sitting right there in the data — found auditing the two demo deals, 2026-09-09.
  const facts: [string, ReactNode][] = [
    ['Founded', glance(d.founded, prov.founded)],
    ['Employees', glance(d.employees, prov.employees)],
    ['Facilities', glance(d.facilities, prov.facilities)],
    ['Promoter', glance(d.promoter, prov.promoter)],
    ['IPO target', glance(d.ipoTarget, prov.ipoTarget)],
    ['Credit rating', <span key="cr" className="inline-flex items-center gap-1.5">{glance(d.intel?.rating, prov.rating)}{d.intel?.rating && <JumpToTab tab="Compliance" label="details →" />}</span>],
    ...(reg0?.website ? [['Website', <a key="w" href={reg0.website} target="_blank" rel="noreferrer" className="text-teal-d hover:underline break-all">{reg0.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</a>] as [string, ReactNode]] : []),
    ...(reg0?.email ? [['Email', <a key="e" href={`mailto:${reg0.email}`} className="text-teal-d hover:underline break-all">{reg0.email}</a>] as [string, ReactNode]] : []),
  ];

  // Registry-derived company facts — paid-up/authorised capital, type, status, listing, incorporation —
  // real data that was only ever shown on the Team & Mgmt tab. Surfaced here too so the important
  // basics don't require a tab switch (product review item 1, 2026-09-09).
  const companyFacts: [string, ReactNode][] = hasReg0 ? [
    ['Type', reg0!.companyType || '—'],
    ['Status', reg0!.companyStatus ? <Pill tone={/active/i.test(reg0!.companyStatus!) ? 'green' : /strike|dissolved|liquidat/i.test(reg0!.companyStatus!) ? 'red' : 'amber'}>{reg0!.companyStatus}</Pill> : '—'],
    ['Listing', reg0!.listingStatus ? <Pill tone={reg0!.listingStatus === 'Listed' ? 'teal' : 'gray'}>{reg0!.listingStatus}</Pill> : '—'],
    ['Incorporated', reg0!.incorporationDate || '—'],
    ['Paid-up capital', fmtRupee(reg0!.paidUpCapital)],
    ['Authorised capital', fmtRupee(reg0!.authorisedCapital)],
    ['Open charges', reg0!.openCharges != null ? <span className={reg0!.openCharges > 0 ? 'text-amber font-semibold' : 'text-green font-semibold'}>{reg0!.openCharges > 0 ? `${reg0!.openCharges} open` : 'None'}</span> : '—'],
  ] : [];

  // Corporate structure (holding/subsidiary/associate/JV) — a free (cached) re-read of an already-paid
  // Registry Intelligence pull; silently absent if there's no access/pull yet, same pattern as the
  // Compliance/Peers tabs' own supplementary reads.
  const { data: p42OverviewRes } = useQuery({ queryKey: ['probe42', dealId], queryFn: () => probe42Get(dealId), enabled: !!d.intel?.probe42, retry: false, staleTime: 60_000 });
  const p42OverviewRoot = ((p42OverviewRes?.data?.raw as any)?.data ?? p42OverviewRes?.data?.raw ?? {}) as Record<string, any>;
  const corpStructure: { label: string; items: any[] }[] = [
    { label: 'Holding company', items: p42OverviewRoot.holding_entities ?? [] },
    { label: 'Subsidiaries', items: p42OverviewRoot.subsidiary_entities ?? [] },
    { label: 'Associate companies', items: p42OverviewRoot.associate_entities ?? [] },
    { label: 'Joint ventures', items: p42OverviewRoot.joint_ventures ?? [] },
  ].filter((g) => Array.isArray(g.items) && g.items.length > 0);

  // Rewrite the card from the deal's own deck text (plain language, deck-only sources).
  async function buildFromDeck() {
    setBBusy(true); setBMsg('Reading the uploaded deck…');
    try {
      const res = await businessFromDeck(dealId);
      setBMsg(`Written from ${res.docs.length === 1 ? res.docs[0] : `${res.docs.length} documents`} · ${res.sections} section${res.sections === 1 ? '' : 's'}.`);
      onChanged();
    } catch (e) { setBMsg(`Failed: ${String((e as Error).message)}`); }
    finally { setBBusy(false); }
  }

  return (
    <div>
      {d.businessBrief && <BriefingBlock b={d.businessBrief} />}
      {/* Verdict hero: the IC call + why, in one glance — this used to be two disconnected cards */}
      <div className={`mb-4 rounded-xl border overflow-hidden shadow-sh ${recTone === 'green' ? 'border-green/25' : recTone === 'amber' ? 'border-amber/25' : 'border-red/25'}`}>
        <div className={`px-5 py-4 flex items-center justify-between gap-4 flex-wrap ${recTone === 'green' ? 'bg-green-50' : recTone === 'amber' ? 'bg-amber-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <span className={`text-[28px] leading-none flex-none ${TONE_TEXT[recTone]}`}>{r.i}</span>
            <div className="min-w-0">
              <div className={`text-[15px] font-extrabold tracking-[-0.2px] ${TONE_TEXT[recTone]}`}>{r.h}</div>
              <div className="text-[12px] font-semibold text-navy/80 mt-0.5">{r.s}</div>
            </div>
          </div>
          <Pill tone={FIT_TONE[f.verdict]!} className="text-[11px] px-2.5 py-1 flex-none">Thesis · {f.verdict}</Pill>
        </div>
        <div className="bg-card px-5 py-3 flex flex-wrap gap-2">
          {f.reasons.map((rr, i) => (
            <span key={i} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${REASON_TONE[rr[0]]}`}>
              <span aria-hidden="true">{rr[0] === 'ok' ? '✓' : rr[0] === 'warn' ? '!' : '✕'}</span>{rr[1]}
            </span>
          ))}
        </div>
      </div>

      {/* Company snapshot — the important basics, visible without a tab switch (product review
          item 1). Independent of whether a deck/business narrative exists — the facts tiles used
          to be nested inside that conditional and so never rendered at all for a deck-less deal. */}
      {(facts.length > 0 || companyFacts.length > 0) && (
        <Card title="Company snapshot">
          <SectionInsight dealId={dealId} section="overview" label="Company snapshot analysis" />
          {companyFacts.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-3 mb-3.5 pb-3.5 border-b border-line-2">
              {companyFacts.map(([label, val]) => (
                <div key={label}>
                  <div className="text-[9.5px] font-bold uppercase tracking-[0.5px] text-muted-2">{label}</div>
                  <div className="mt-0.5 text-[12.5px] font-semibold text-navy">{val}</div>
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {facts.map(([label, val]) => <FactTile key={label} icon={FACT_ICON[label]!} label={label}>{val}</FactTile>)}
          </div>
          {corpStructure.length > 0 && (
            <div className="mt-3.5 pt-3.5 border-t border-line-2">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[9.5px] font-bold uppercase tracking-[0.5px] text-muted-2">Corporate structure</div>
                <JumpToTab tab="Registry Intelligence" label="Full detail →" />
              </div>
              <div className="flex flex-wrap gap-2">
                {corpStructure.map((g) => (
                  <span key={g.label} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-navy-50 text-navy text-[11px] font-semibold">
                    {g.label}: {g.items.length}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Business — the wider, primary column */}
        <div className="lg:col-span-3">
          <Card
            title="Business"
            sub={fromDeck
              ? <>From the uploaded deck, in plain language{bd?.fetchedAt ? ` · built ${new Date(bd.fetchedAt).toLocaleString()}` : ''}</>
              : bdSections.length ? 'From deck + web sources — rebuild to use the deck only' : undefined}
            actions={
              <button disabled={bBusy} onClick={buildFromDeck}
                className="px-2.5 py-1.5 rounded-lg text-[11.5px] font-semibold bg-navy text-white disabled:opacity-50">
                {bBusy ? <InlineBusy>Reading deck…</InlineBusy> : bdSections.length ? '↻ Rebuild from deck' : '📄 Build from deck'}
              </button>
            }>
            {bMsg && <div className={`text-[11.5px] mb-2 ${bMsg.startsWith('Failed') ? 'text-red' : 'text-muted'}`}>{bMsg}</div>}
            {bBusy && !bdSections.length ? <Skeleton lines={5} h={11} /> : bdSections.length ? (
              <div>
                {bd!.summary?.content && (
                  <div className="rounded-lg border-l-4 border-teal bg-teal-50 px-4 py-3 mb-3.5">
                    <p className="text-[13px] leading-relaxed text-navy font-medium">{bd!.summary.content}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {bdSections.map((s) => (
                    <div key={s.key} className="rounded-xl border border-line bg-card p-3.5 hover:shadow-sh2 transition-shadow">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-[15px] leading-none flex-none">{SECTION_ICON[s.key] ?? '📌'}</span>
                        <span className="text-[11.5px] font-bold text-navy">{s.label}</span>
                        {srcChip(bd![s.key]!.sourceUrl)}
                      </div>
                      <Bullets text={bd![s.key]!.content} />
                    </div>
                  ))}
                </div>
              </div>
            ) : d.business ? (
              <>
                <div className="rounded-lg border-l-4 border-teal bg-teal-50 px-4 py-3 mb-3.5">
                  <p className="text-[13px] leading-relaxed text-navy font-medium">{d.business}</p>
                </div>
                {d.use && <><H4>Use of proceeds</H4><p className="text-[12.5px]">{d.use}</p></>}
                {d.tailwinds && <><H4>Sector tailwinds</H4><p className="text-[12.5px]">{d.tailwinds}</p></>}
              </>
            ) : (
              <Empty label={d.deck
                ? 'No business detail yet — click “📄 Build from deck” to write it from the uploaded deck.'
                : 'No business detail yet — upload a deck on the Documents tab, then click “📄 Build from deck”.'} />
            )}
          </Card>
        </div>

        {/* Deal snapshot — narrower sidebar, all the CRM + pricing numbers at a glance */}
        <div className="lg:col-span-2">
          <Card title="Deal snapshot" sub="CRM stage, terms & pricing vs peers">
            <div className="flex items-center justify-between mb-3.5">
              <div>
                <div className="text-[9.5px] font-bold uppercase tracking-[0.5px] text-muted-2">Stage</div>
                <div className="mt-1"><Pill tone={STATUS_TONE[d.status]!}>{d.status}</Pill></div>
              </div>
              <div className="text-right">
                <div className="text-[9.5px] font-bold uppercase tracking-[0.5px] text-muted-2">Date in CRM</div>
                <div className="mt-1 text-[12.5px] font-semibold text-navy">{d.dateCRM || '—'}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5 mb-3.5">
              <MetricTile label="Valuation ask" value={inr(d.valuationAsk)} tone="navy" />
              <MetricTile label="Total round" value={inr(d.totalRaise)} tone="navy" />
              <MetricTile label="Allocation" value={inr(d.allocation)} tone="gold" />
              <MetricTile label="Rev growth YoY" value={pct(g, 1)} tone={growthTone} />
            </div>

            <AgencyRatingsBlock dealId={dealId} ratings={d.intel?.agencyRatings} onChanged={onChanged} />
            <CreditRatingBlock credit={d.computed.credit} />


            <div className="rounded-lg border border-line-2 bg-canvas px-3.5 py-3 mb-3.5">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-muted-2">Pricing vs peers</span>
                <Pill tone={mosTone}>{v.ok ? `${pct(v.mos)} MoS` : 'MoS —'}</Pill>
              </div>
              <div className="space-y-2.5">
                <div>
                  <div className="flex justify-between text-[11px] mb-1"><span className="text-muted font-semibold">This deal · Implied P/E</span><span className="mono font-bold text-navy">{v.ok ? xx(v.entryPE) : '—'}</span></div>
                  <ProgressBar pct={v.ok ? (v.entryPE / peMax) * 100 : 0} tone={peTone} />
                </div>
                <div>
                  <div className="flex justify-between text-[11px] mb-1"><span className="text-muted font-semibold">Peers now · P/E</span><span className="mono font-bold text-navy">{xx(v.peNow)}</span></div>
                  <ProgressBar pct={(v.peNow / peMax) * 100} tone="navy" />
                </div>
              </div>
            </div>

            <DL rows={[['IPO timeline', d.ipo || '—']]} />
          </Card>
        </div>
      </div>

      <div className="mt-4">
        <BoardDirectors d={d} dealId={dealId} onChanged={onChanged} />
      </div>
    </div>
  );
}

const PEOPLE_STAGES = ['Reading the stored deck', 'Querying the MCA registry (Tofler)', 'Searching the web for leadership', 'Merging & de-duplicating people', 'Done'];

/* ---------- Team & Mgmt ---------- */

type SrcKey = 'Deck' | 'MCA' | 'Web';
interface Person { name: string; role: string; bg: string; din?: string; appointed?: string; srcs: SrcKey[]; url: string | null }

const SRC_TONE: Record<SrcKey, string> = { Deck: 'teal', MCA: 'navy', Web: 'purple' };
const AVATAR_TONES = ['bg-navy', 'bg-teal-d', 'bg-purple', 'bg-gold', 'bg-green'];
const HONORIFIC = /^(?:mr|mrs|ms|miss|dr|shri|sri|smt|capt|col|ca|adv|prof)\.?\s+/i;
/** Letters-only key so "Mr. R. K. Sharma" and "R K SHARMA" collapse to one person. */
const personKey = (s: string) => s.replace(HONORIFIC, '').toLowerCase().replace(/[^a-z]/g, '');
/** Drop the honorific and title-case a shouty registry name ("MR. NATHU LAL" → "Nathu Lal"). */
const tidyName = (s: string) => {
  const t = s.replace(HONORIFIC, '').replace(/\s+/g, ' ').trim();
  return /[a-z]/.test(t) ? t : t.toLowerCase().replace(/\b[a-z]/g, (m) => m.toUpperCase());
};

function Avatar({ name }: { name: string }) {
  const parts = name.replace(/[^A-Za-z ]/g, ' ').trim().split(/\s+/).filter(Boolean);
  const ini = ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : '')).toUpperCase();
  let h = 0; for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 997;
  return <span aria-hidden="true" className={`flex-none w-9 h-9 rounded-full grid place-items-center text-white text-[12px] font-bold ${AVATAR_TONES[h % AVATAR_TONES.length]}`}>{ini}</span>;
}

/** Role buckets so a long merged list reads as a management structure, not a dump. */
const ROLE_GROUPS: { label: string; re: RegExp }[] = [
  { label: 'Promoters, founders & board', re: /promoter|founder|chair|managing director|whole[- ]?time|independent|nominee|additional|executive director|^director$|director$|partner/i },
  { label: 'Management', re: /ceo|cfo|coo|cto|cmo|chief|president|vice|head|manager|vp\b|md\b|secretary|leadership|management/i },
];

function PersonCard({ p, depth }: { p: Person; depth?: PersonDepth }) {
  const [open, setOpen] = useState(false);
  // The drill-down has content when the research pass found something for this person.
  const hasDepth = !!depth && !depth.notFound && !!(depth.background || depth.tenure || depth.otherDirectorships?.length || depth.news?.length || depth.adverse?.length || depth.linkedin);
  const searched = !!depth; // the per-person pass ran for this person (grounds the "not found" copy)
  return (
    <div className="rounded-xl border border-line bg-card hover:shadow-sh2 transition-shadow">
      <div className="flex gap-3 p-3.5">
        <Avatar name={p.name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[13px] font-bold text-navy leading-tight">{p.name}</span>
            {p.srcs.map((s) => <Pill key={s} tone={SRC_TONE[s]}>{s}</Pill>)}
            {srcChip(p.url)}
            {depth?.adverse?.length ? <Pill tone="red">⚠ adverse</Pill> : null}
          </div>
          <div className="text-[11.5px] font-semibold text-teal-d mt-0.5">{p.role || 'Management'}</div>
          {p.bg && <p className="text-[11.5px] text-muted leading-relaxed mt-1">{p.bg}</p>}
          {(p.din || p.appointed) && (
            <div className="flex gap-3 mt-1.5 text-[10.5px] text-muted-2 flex-wrap">
              {p.din && <span>DIN <span className="mono text-muted">{p.din}</span></span>}
              {p.appointed && <span>Appointed {p.appointed}</span>}
            </div>
          )}
          {(hasDepth || searched) && (
            <button type="button" onClick={() => setOpen((v) => !v)} className="mt-1.5 text-[10.5px] font-semibold text-teal-d hover:underline">
              {hasDepth ? (open ? '▾ Hide research' : '▸ Background, news & directorships') : '▸ No web research found'}
            </button>
          )}
        </div>
      </div>
      {open && hasDepth && depth && (
        <div className="px-3.5 pb-3.5 pt-0.5 border-t border-line-2 text-[11.5px] space-y-2">
          {depth.background && <div><span className="font-semibold text-navy">Background: </span><span className="text-muted">{depth.background}</span>{srcChip(depth.sourceUrl ?? null)}</div>}
          {depth.tenure && <div><span className="font-semibold text-navy">Tenure: </span><span className="text-muted">{depth.tenure}</span></div>}
          {depth.otherDirectorships?.length ? <div><span className="font-semibold text-navy">Other directorships: </span><span className="text-muted">{depth.otherDirectorships.join(', ')}</span></div> : null}
          {depth.adverse?.length ? (
            <div>
              <span className="font-semibold text-red">Adverse signals:</span>
              <ul className="list-disc pl-4 mt-0.5 text-muted space-y-0.5">
                {depth.adverse.map((a, i) => <li key={i}>{a.note} {srcChip(a.sourceUrl)}</li>)}
              </ul>
            </div>
          ) : null}
          {depth.news?.length ? (
            <div>
              <span className="font-semibold text-navy">In the news:</span>
              <ul className="mt-0.5 space-y-0.5">
                {depth.news.map((n, i) => (
                  <li key={i}><a href={n.url} target="_blank" rel="noreferrer" className="text-teal-d underline">{n.title || n.url}</a><span className="text-muted-2"> · {n.source}{n.date ? ` · ${n.date}` : ''}</span></li>
                ))}
              </ul>
            </div>
          ) : null}
          {depth.linkedin && <div><a href={depth.linkedin} target="_blank" rel="noreferrer" className="text-teal-d underline font-semibold">LinkedIn / bio ↗</a></div>}
        </div>
      )}
      {open && !hasDepth && (
        <div className="px-3.5 pb-3 pt-0.5 border-t border-line-2 text-[11px] text-muted-2">Searched the web — no background, news or directorships found for this person.</div>
      )}
    </div>
  );
}

/** True when the team synthesis carries at least one evidenced statement. */
function teamHasSynthesis(s?: { strength?: TeamSynthesisItem[]; keyManRisk?: TeamSynthesisItem[]; governance?: TeamSynthesisItem[] } | null): boolean {
  return !!(s && (s.strength?.length || s.keyManRisk?.length || s.governance?.length));
}

/** One labelled group of evidenced synthesis statements (each a short claim + its source link). */
function SynthList({ label, items, tone }: { label: string; items?: TeamSynthesisItem[]; tone: 'green' | 'amber' | 'red' }) {
  if (!items?.length) return null;
  const dot = tone === 'green' ? 'bg-green' : tone === 'amber' ? 'bg-amber' : 'bg-red';
  return (
    <div className="mb-2.5 last:mb-0">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-muted-2 mb-1">{label}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex gap-1.5 text-[12px] leading-snug">
            <span className={`w-1.5 h-1.5 rounded-full flex-none mt-1.5 ${dot}`} />
            <span className="text-muted">{it.text}{srcChip(it.sourceUrl ?? null)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Copy-to-clipboard affordance for identifiers (CIN / PAN / GSTIN). */
function CopyBtn({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" title={`Copy ${value}`} aria-label={`Copy ${value}`}
      onClick={() => { void navigator.clipboard?.writeText(value).then(() => { setDone(true); setTimeout(() => setDone(false), 1200); }).catch(() => {}); }}
      className={`text-[10px] leading-none ${done ? 'text-green' : 'text-muted-2 hover:text-teal-d'}`}>{done ? '✓' : '⧉'}</button>
  );
}

/** One labelled registry fact. Renders a muted em-dash when the registry didn't disclose it. */
function Fact({ label, value, mono = false, copy = false, wide = false, children }: {
  label: string; value?: string | number | null; mono?: boolean; copy?: boolean; wide?: boolean; children?: ReactNode;
}) {
  const text = value == null || value === '' ? '' : String(value);
  return (
    <div className={wide ? 'col-span-2 md:col-span-3' : ''}>
      <div className="text-[9.5px] font-bold uppercase tracking-[0.5px] text-muted-2">{label}</div>
      <div className={`mt-0.5 flex items-center gap-1.5 text-[12.5px] font-semibold ${text || children ? 'text-navy' : 'text-muted-2'} ${mono ? 'mono' : ''}`}>
        {children ?? <span className="break-words">{text || '—'}</span>}
        {copy && text ? <CopyBtn value={text} /> : null}
      </div>
    </div>
  );
}

/** Per-source status pip in the toolbar: green = data, amber = ran but empty, red = failed. */
function SourceChip({ label, count, unit, rep }: { label: SrcKey; count: number; unit: [string, string]; rep?: PeopleSourceReport }) {
  const failed = rep?.status === 'error';
  const dot = failed ? 'bg-red' : count > 0 ? 'bg-green' : rep ? 'bg-amber' : 'bg-line';
  const state = count > 0 ? `${count} ${count === 1 ? unit[0] : unit[1]}` : failed ? 'failed' : rep ? (rep.status === 'skipped' ? 'no deck' : 'none found') : 'not fetched';
  return (
    <span title={rep?.note ?? `${label}: ${state}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-card border border-line text-[11px]">
      <span className={`w-1.5 h-1.5 rounded-full flex-none ${dot}`} />
      <span className="font-bold text-navy">{label}</span>
      <span className="text-muted">{state}</span>
    </span>
  );
}

interface DirNetCompany {
  cin?: string; legal_name?: string; company_status?: string; designation?: string;
  incorporation_date?: string; date_of_appointment?: string; date_of_cessation?: string | null;
  sum_of_charges?: number; paid_up_capital?: number; cirp_status?: string | null;
}
interface DirNetEntry { din?: string; name?: string; network?: { companies?: DirNetCompany[]; llps?: DirNetCompany[] } }
const INACTIVE_STATUS_RE = /strike|dissolved|liquidat|amalgamat|converted|wound/i;

/**
 * Director Network — for each director, the OTHER companies they're associated with (product review
 * item 3). Deterministic, not LLM-written: every count here is a plain array length or regex match
 * against Probe42's own `company_status` string, so nothing here can be a hallucinated pattern —
 * exactly the "only when supported by data" constraint. Skips a director with no other associations
 * entirely rather than showing an empty row.
 */
function DirectorNetworkCard({ dealId, selfCin }: { dealId: string; selfCin?: string }) {
  const { data: res } = useQuery({ queryKey: ['probe42', dealId], queryFn: () => probe42Get(dealId), retry: false, staleTime: 60_000 });
  const root = ((res?.data?.raw as any)?.data ?? res?.data?.raw ?? {}) as Record<string, any>;
  const entries: DirNetEntry[] = Array.isArray(root.director_network) ? root.director_network : [];
  if (!entries.length) return null;

  const perDirector = entries.map((e) => {
    const all = [...(e.network?.companies ?? []), ...(e.network?.llps ?? [])];
    const others = selfCin ? all.filter((c) => c.cin !== selfCin) : all;
    const inactive = others.filter((c) => INACTIVE_STATUS_RE.test(c.company_status ?? ''));
    const cirp = others.filter((c) => c.cirp_status);
    return { name: e.name || '—', din: e.din, others, inactive, cirp };
  }).filter((p) => p.others.length > 0);
  if (!perDirector.length) return null;

  const totalOther = perDirector.reduce((n, p) => n + p.others.length, 0);
  const totalInactive = perDirector.reduce((n, p) => n + p.inactive.length, 0);
  const totalCirp = perDirector.reduce((n, p) => n + p.cirp.length, 0);
  const mostConnected = [...perDirector].sort((a, b) => b.others.length - a.others.length)[0];

  const notes: string[] = [];
  if (mostConnected && mostConnected.others.length > 1) notes.push(`${mostConnected.name} is also associated with ${mostConnected.others.length} other compan${mostConnected.others.length === 1 ? 'y' : 'ies'}.`);
  if (totalInactive > 0) notes.push(`${totalInactive} associated compan${totalInactive === 1 ? 'y is' : 'ies are'} struck off / dissolved / amalgamated.`);
  if (totalCirp > 0) notes.push(`${totalCirp} associated compan${totalCirp === 1 ? 'y has' : 'ies have'} an active insolvency (CIRP) status.`);

  return (
    <Card title="Director network" sub={`${perDirector.length} director${perDirector.length === 1 ? '' : 's'} with other company associations · ${totalOther} total — via Registry Intelligence`}>
      {notes.length > 0 && (
        <ul className="mb-3 space-y-1">
          {notes.map((n, i) => <li key={i} className="text-[12px] text-navy">• {n}</li>)}
        </ul>
      )}
      <div className="space-y-3">
        {perDirector.map((p) => (
          <details key={p.din ?? p.name} className="border border-line-2 rounded-lg px-3 py-2">
            <summary className="text-[12.5px] font-semibold text-navy cursor-pointer">
              {p.name} <span className="text-muted-2 font-normal">— {p.others.length} other compan{p.others.length === 1 ? 'y' : 'ies'}{p.inactive.length ? `, ${p.inactive.length} inactive` : ''}</span>
            </summary>
            <div className="mt-2">
              <P42Table rows={p.others.map((c) => ({
                company: c.legal_name, designation: c.designation, status: c.company_status,
                since: c.date_of_appointment, cessation: c.date_of_cessation, cin: c.cin,
              }))} />
            </div>
          </details>
        ))}
      </div>
    </Card>
  );
}

function TeamTab({ d, dealId, onChanged }: { d: DealDto; dealId: string; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [res, setRes] = useState<PeopleFetchResult | null>(null);
  const [cinOpen, setCinOpen] = useState(false);
  const [cin, setCin] = useState('');
  const [filter, setFilter] = useState<'All' | SrcKey>('All');
  const [deepBusy, setDeepBusy] = useState(false);
  const [deepMsg, setDeepMsg] = useState('');
  const reg = d.intel.registry;
  const tr = d.teamResearch;
  const refreshInsight = useInsightRefresh(dealId);
  // Match per-person research to each unified person by the same normalised name key.
  const depthByKey = useMemo(() => {
    const m = new Map<string, PersonDepth>();
    for (const pd of tr?.people ?? []) m.set(personKey(pd.name), pd);
    return m;
  }, [tr]);

  /** Per-person deep research (background, news, directorships, promoter view, synthesis). */
  async function runDeepen() {
    setDeepBusy(true); setDeepMsg('');
    try {
      const r = await teamResearchFetch(dealId, true);
      setDeepMsg(r.people > 0 ? `Researched ${r.people} ${r.people === 1 ? 'person' : 'people'}${r.adverse ? ` · ${r.adverse} adverse signal${r.adverse === 1 ? '' : 's'}` : ''}.` : 'No per-person web results found.');
      onChanged();
    } catch (e) { setDeepMsg(String((e as Error).message)); }
    finally { setDeepBusy(false); }
  }

  /** One click → deck pass + MCA registry + web leadership, merged server-side. */
  async function run(manualCin?: string) {
    setBusy(true); setErr(''); setRes(null);
    try {
      // An explicit click always bypasses the 24h registry + web caches.
      const r = await peopleFetch(dealId, { ...(manualCin ? { cin: manualCin } : {}), fresh: true });
      setRes(r);
      if (r.sources.mca.status !== 'ok') setCinOpen(true);
      onChanged();
      refreshInsight(['overview', 'compliance']); // registry facts (status/CIRP/RoC) may have just changed
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  // Unified people view: deck team ∪ MCA directors ∪ web leadership, deduped by name, tagged by source.
  const people = useMemo(() => {
    const m = new Map<string, Person>();
    const add = (name: string, patch: Omit<Person, 'name' | 'srcs' | 'role' | 'bg'> & { role: string; bg: string; src: SrcKey }) => {
      const clean = tidyName(name);
      const k = personKey(clean);
      if (!k) return;
      const ex = m.get(k);
      if (!ex) { m.set(k, { name: clean, role: patch.role, bg: patch.bg, din: patch.din, appointed: patch.appointed, srcs: [patch.src], url: patch.url }); return }
      if (!ex.srcs.includes(patch.src)) ex.srcs.push(patch.src);
      if (!ex.role || /^(management|leadership|director)$/i.test(ex.role)) ex.role = patch.role || ex.role;
      if (!ex.bg && patch.bg) ex.bg = patch.bg;
      ex.din = ex.din ?? patch.din;
      ex.appointed = ex.appointed ?? patch.appointed;
      ex.url = ex.url ?? patch.url;
    };
    for (const t of d.team) add(t.name, { role: t.role, bg: t.bg, src: 'Deck', url: null });
    for (const x of d.intel.directors ?? []) add(x.name, { role: x.role, bg: '', din: x.din, appointed: x.appointed, src: 'MCA', url: reg?.source ?? null });
    for (const l of d.intel.leadership ?? []) add(l.name, { role: l.role, bg: l.bg, src: 'Web', url: l.sourceUrl });
    return [...m.values()];
  }, [d.team, d.intel.directors, d.intel.leadership, reg?.source]);

  const shown = filter === 'All' ? people : people.filter((p) => p.srcs.includes(filter));
  // First matching bucket wins, so nobody is listed twice (an "MD & Founder" is board, not both).
  const bucket = (p: Person) => ROLE_GROUPS.findIndex((g) => g.re.test(p.role));
  const sections = [...ROLE_GROUPS.map((g, i) => ({ label: g.label, items: shown.filter((p) => bucket(p) === i) })),
    { label: 'Others named', items: shown.filter((p) => bucket(p) < 0) }].filter((g) => g.items.length);
  const counts: Record<SrcKey, number> = { Deck: d.team.length, MCA: (d.intel.directors ?? []).length, Web: (d.intel.leadership ?? []).length };
  const statusTone = (s?: string) => (/active/i.test(s ?? '') ? 'green' : /strike|dissolved|liquidat/i.test(s ?? '') ? 'red' : /dormant|amalgamat|converted|process/i.test(s ?? '') ? 'amber' : 'gray');
  const hasReg = Boolean(reg && (reg.cin || reg.incorporationDate || reg.authorisedCapital || reg.registeredAddress));

  return (
    <>
      {/* ── Sources toolbar: one action, three sources, per-source status ── */}
      <div className="mb-4 rounded-xl border border-line bg-canvas px-4 py-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[12.5px] font-semibold text-navy">People & registry sources</div>
            <p className="text-[11px] text-muted mt-0.5">Pulls the management team from the uploaded deck, the director register from MCA, and founders / CXOs from the open web — then merges and de-duplicates them.</p>
          </div>
          <div className="flex items-center gap-2 flex-none">
            <button disabled={busy} onClick={() => run()} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-navy text-white hover:bg-navy-2 disabled:opacity-50">
              {busy ? <InlineBusy>Fetching…</InlineBusy> : `${people.length ? '⟳ Refresh' : '⚡ Fetch'} from all sources`}
            </button>
            {people.length > 0 && (
              <button disabled={deepBusy} onClick={runDeepen} title="Search each person: background, tenure, other directorships, news mentions, adverse signals + promoter view"
                className="px-3 py-2 rounded-lg text-[12px] font-semibold bg-card text-navy border border-line hover:bg-white disabled:opacity-50">
                {deepBusy ? <InlineBusy>Researching…</InlineBusy> : `${tr ? '⟳ Re-deepen' : '🔍 Deepen (per-person)'}`}
              </button>
            )}
            <button type="button" onClick={() => setCinOpen((v) => !v)} className="px-3 py-2 rounded-lg text-[12px] font-semibold bg-card text-navy border border-line hover:bg-white">
              {cinOpen ? 'Hide CIN' : 'Enter CIN'}
            </button>
          </div>
        </div>
        {(deepBusy || deepMsg) && (
          <p className="text-[11.5px] text-muted mt-2">{deepBusy ? 'Searching each person across the web — background, tenure, directorships, news, adverse signals…' : deepMsg}</p>
        )}
        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
          <SourceChip label="Deck" count={counts.Deck} unit={['person', 'people']} rep={res?.sources.deck} />
          <SourceChip label="MCA" count={counts.MCA} unit={['director', 'directors']} rep={res?.sources.mca} />
          <SourceChip label="Web" count={counts.Web} unit={['leader', 'leaders']} rep={res?.sources.web} />
          {reg?.fetchedAt && <span className="text-[10.5px] text-muted-2">registry fetched {new Date(reg.fetchedAt).toLocaleDateString()}</span>}
        </div>
        {cinOpen && (
          <div className="flex items-center gap-2 mt-2.5 flex-wrap">
            <input value={cin} onChange={(e) => setCin(e.target.value.toUpperCase().trim())} placeholder="U24292RJ2014PTC045462"
              className="px-2.5 py-1.5 border border-line rounded-lg text-[12px] mono w-[250px] bg-card" />
            <button disabled={busy || cin.length < 21} onClick={() => run(cin)} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">Fetch this CIN</button>
            <span className="text-[10.5px] text-muted-2">21-character MCA CIN — use this when the name search finds the wrong company.</span>
          </div>
        )}
        {res && !busy && (
          <p className="text-[11.5px] text-muted mt-2.5">
            {(['deck', 'mca', 'web'] as const).map((k) => `${k === 'mca' ? 'MCA' : k[0]!.toUpperCase() + k.slice(1)} ${res.sources[k].count}`).join(' · ')}
            {res.added > 0 ? ` · ${res.added} new ${res.added === 1 ? 'person' : 'people'} added` : ' · nothing new'}
            {(['deck', 'mca', 'web'] as const).filter((k) => res.sources[k].note).map((k) => ` — ${res.sources[k].note}`).join('')}
          </p>
        )}
      </div>
      {busy && <StagedLoader stages={PEOPLE_STAGES} stageMs={7000} className="mb-4" />}
      {err && !busy && <StagedLoader stages={PEOPLE_STAGES} error={err} onRetry={() => run()} className="mb-4" />}

      {/* ── People ── */}
      <Card title={`Management & board${people.length ? ` · ${people.length}` : ''}`}
        sub="Deck ∪ MCA director register ∪ web leadership, deduped by name"
        actions={people.length > 3 ? (
          <div className="flex items-center gap-1 p-0.5 rounded-lg bg-canvas border border-line-2">
            {(['All', 'Deck', 'MCA', 'Web'] as const).map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-2 py-1 rounded-md text-[11px] font-semibold ${filter === f ? 'bg-card text-navy shadow-sh' : 'text-muted hover:text-navy'}`}>
                {f}{f !== 'All' && <span className="text-muted-2 font-normal"> {counts[f]}</span>}
              </button>
            ))}
          </div>
        ) : undefined}>
        {busy && !people.length ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex gap-3 p-3.5 rounded-xl border border-line">
                <Skeleton w={36} h={36} rounded="rounded-full" className="flex-none" />
                <div className="flex-1"><Skeleton w="55%" h={12} className="mb-2" /><Skeleton lines={2} h={10} /></div>
              </div>
            ))}
          </div>
        ) : sections.length ? (
          <div className="space-y-4">
            {sections.map((g) => (
              <div key={g.label}>
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-muted-2">{g.label}</h4>
                  <span className="text-[10.5px] text-muted-2">· {g.items.length}</span>
                  <span className="h-px bg-line-2 flex-1" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {g.items.map((p) => <PersonCard key={p.name} p={p} depth={depthByKey.get(personKey(p.name))} />)}
                </div>
              </div>
            ))}
          </div>
        ) : people.length ? (
          <Empty label={`No people from ${filter} — switch the filter back to All.`} />
        ) : (
          <div className="text-center py-8">
            <div className="text-[26px] mb-1.5">👥</div>
            <p className="text-[13px] font-semibold text-navy">No people on this deal yet</p>
            <p className="text-[11.5px] text-muted mt-1 max-w-[440px] mx-auto">One click reads the uploaded deck, the MCA director register and the open web, then merges what each of them knows about this company's management.</p>
            <button disabled={busy} onClick={() => run()} className="mt-3 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-navy text-white hover:bg-navy-2 disabled:opacity-50">⚡ Fetch from all sources</button>
          </div>
        )}
      </Card>

      {/* ── Director network: other companies each director is associated with (item 3) ── */}
      <DirectorNetworkCard dealId={dealId} selfCin={reg?.cin} />

      {/* ── Team synthesis + promoter view (per-person research) ── */}
      {tr && (teamHasSynthesis(tr.synthesis) || tr.promoter) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {teamHasSynthesis(tr.synthesis) && (
            <Card title="Team read" sub={`Analyst synthesis · researched ${new Date(tr.fetchedAt).toLocaleDateString()}`}>
              <SynthList label="Team strength" items={tr.synthesis?.strength} tone="green" />
              <SynthList label="Key-man risk" items={tr.synthesis?.keyManRisk} tone="amber" />
              <SynthList label="Governance flags" items={tr.synthesis?.governance} tone="red" />
            </Card>
          )}
          {tr.promoter && (
            <Card title="Promoter view">
              <div className="space-y-2 text-[12px]">
                {tr.promoter.promoters?.length ? <div><span className="font-semibold text-navy">Promoters: </span><span className="text-muted">{tr.promoter.promoters.join(', ')}</span>{srcChip(tr.promoter.sourceUrl ?? null)}</div> : null}
                {tr.promoter.note && <p className="text-muted leading-relaxed">{tr.promoter.note}</p>}
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-muted-2">Registry holding</div><div className="text-[13px] font-semibold text-navy">{tr.promoter.promoterHoldingPct != null ? `${tr.promoter.promoterHoldingPct}%` : '—'}</div></div>
                  <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-muted-2">Deck-stated stake</div><div className="text-[13px] font-semibold text-navy">{tr.promoter.deckPromoterStake != null ? `${tr.promoter.deckPromoterStake}%` : '—'}</div></div>
                </div>
                {tr.promoter.mismatch && <div className="text-[11.5px] text-amber font-semibold bg-amber-50 rounded-lg px-2.5 py-1.5">⚠ {tr.promoter.mismatch}</div>}
                {tr.promoter.pledged && <div><span className="font-semibold text-navy">Pledged: </span><span className="text-muted">{tr.promoter.pledged}</span></div>}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ── Registry facts ── */}
      {hasReg && reg && (
        <Card title="Company registry"
          sub={`MCA${reg.source?.startsWith('http') ? ' via Tofler' : d.intel?.probe42 ? ' via Registry Intelligence' : ''}${reg.fetchedAt ? ` · fetched ${new Date(reg.fetchedAt).toLocaleDateString()}` : ''}`}
          actions={reg.source?.startsWith('http') ? <a href={reg.source} target="_blank" rel="noreferrer" className="text-[11px] font-semibold text-teal-d hover:underline">↗ View on source</a> : undefined}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3.5">
            <Fact label="CIN" value={reg.cin} mono copy />
            <Fact label="PAN" value={reg.pan} mono copy />
            <Fact label="GSTIN" value={reg.gstin} mono copy />
            <Fact label="Status">{reg.companyStatus ? <Pill tone={statusTone(reg.companyStatus)}>{reg.companyStatus}</Pill> : <span className="text-muted-2">—</span>}</Fact>
            <Fact label="Type" value={reg.companyType} />
            <Fact label="Incorporated" value={reg.incorporationDate} />
            <Fact label="Authorised capital" value={fmtRupee(reg.authorisedCapital)} mono />
            <Fact label="Paid-up capital" value={fmtRupee(reg.paidUpCapital)} mono />
            <Fact label="RoC" value={reg.roc} />
            <Fact label="Open charges">
              {reg.openCharges != null
                ? <span className={reg.openCharges > 0 ? 'text-amber' : 'text-green'}>{reg.openCharges}{reg.openCharges > 0 ? ' open' : ' — none'}</span>
                : <span className="text-muted-2">—</span>}
            </Fact>
            <Fact label="Last AGM" value={reg.lastAgm} />
            <Fact label="Registered email">
              {reg.email ? <a href={`mailto:${reg.email}`} className="text-teal-d hover:underline break-all">{reg.email}</a> : <span className="text-muted-2">—</span>}
            </Fact>
            <Fact label="Website">
              {reg.website ? <a href={reg.website} target="_blank" rel="noreferrer" className="text-teal-d hover:underline break-all">{reg.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</a> : <span className="text-muted-2">—</span>}
            </Fact>
            <Fact label="Listing">
              {reg.listingStatus ? <Pill tone={reg.listingStatus === 'Listed' ? 'teal' : 'gray'}>{reg.listingStatus}</Pill> : <span className="text-muted-2">—</span>}
            </Fact>
            <Fact label="LEI" value={reg.lei} mono copy />
            <Fact label="Registered address" value={reg.registeredAddress} wide />
          </div>
        </Card>
      )}

      {/* ── Shareholding + raw MCA register ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Shareholding" sub={(d.intel.shareholding ?? []).length ? 'As disclosed on the registry page' : undefined}>
          {(d.intel.shareholding ?? []).length ? (
            <ul className="space-y-3">
              {(d.intel.shareholding ?? []).map((s, i) => (
                <li key={`${s.holder}-${i}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <span className="text-[12px] font-semibold text-navy truncate">{s.holder}</span>
                    <span className="text-[12px] mono text-muted flex-none">{pct(s.pct)}</span>
                  </div>
                  <ProgressBar pct={s.pct} tone={i === 0 ? 'navy' : 'teal'} />
                </li>
              ))}
            </ul>
          ) : <Empty label={hasReg ? 'Not disclosed on the registry page.' : 'No shareholding yet — fetch from all sources above.'} />}
        </Card>
        <Card title="MCA director register" sub={(d.intel.directors ?? []).length ? `${(d.intel.directors ?? []).length} on record · DIN + date of appointment` : undefined}>
          {(d.intel.directors ?? []).length ? (
            <div className="overflow-x-auto">
              <DataTable head={['Name', 'DIN', 'Designation', 'Appointed']}
                rows={(d.intel.directors ?? []).map((x) => [tidyName(x.name), <span className="mono text-[11px]">{x.din}</span>, x.role, x.appointed || '—'])} />
            </div>
          ) : <Empty label="No director register yet — fetch from all sources above." />}
        </Card>
      </div>
    </>
  );
}

/** Renders prose as-is; renders newline-separated text as a pointed bullet list (a "storyboard"). */
function Bullets({ text }: { text: string | null | undefined }) {
  if (!text) return null;
  const lines = text.split('\n').map((l) => l.replace(/^[-–•]\s*/, '').trim()).filter(Boolean);
  if (lines.length <= 1) return <p className="text-[12px] text-muted leading-relaxed mt-1.5 whitespace-pre-line">{text}</p>;
  return (
    <ul className="mt-1.5 space-y-1">
      {lines.map((l, i) => <li key={i} className="flex gap-2 text-[12px] text-muted leading-relaxed"><span className="text-teal-d font-bold shrink-0">–</span><span>{l}</span></li>)}
    </ul>
  );
}

/** One narrative sub-section: heading (+ optional source chip) and pointed content. */
function IndustrySection({ label, content }: { label: string; content: string }) {
  return (
    <details open className="border-t border-line-2 py-2">
      <summary className="text-[11.5px] font-semibold text-navy cursor-pointer">{label}</summary>
      <Bullets text={content} />
    </details>
  );
}

/** Bullet block (tailwinds / headwinds / thesis-breakers). */
function BulletBlock({ label, items, tone }: { label: string; items: string[]; tone: 'green' | 'red' | 'amber' }) {
  if (!items.length) return null;
  const dot = tone === 'green' ? 'text-green' : tone === 'red' ? 'text-red' : 'text-amber';
  return (
    <div>
      <H4>{label}</H4>
      <ul className="space-y-1">
        {items.map((it, i) => <li key={i} className="flex gap-2 text-[12px] leading-relaxed"><span className={`${dot} font-bold`}>•</span><span className="text-navy">{it}</span></li>)}
      </ul>
    </div>
  );
}

function BusinessTab({ d, dealId, onChanged }: { d: DealDto; dealId: string; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const ind = d.industryDetail as IndustryDetail | null;
  const sections = ind ? INDUSTRY_SECTIONS.filter((s) => ind[s.key]) : [];
  const bd = d.businessDetail;
  const bdSections = bd ? BUSINESS_SECTIONS.filter((s) => bd[s.key]?.content) : [];
  const prov = d.provenance ?? {};
  const [sbBusy, setSbBusy] = useState(false);
  const [sbMsg, setSbMsg] = useState('');
  const refreshInsight = useInsightRefresh(dealId);

  // Rewrite the storyboard from the deck + a bounded web search (founding history, customers, revenue model).
  async function buildStoryboard() {
    setSbBusy(true); setSbMsg('Reading the deck and searching the web…');
    try {
      const res = await businessFromDeck(dealId, { withWeb: true });
      setSbMsg(`Written from ${res.docs.length === 1 ? res.docs[0] : `${res.docs.length} documents`}${res.sources.length ? ` + ${res.sources.length - (res.docs.length ? 1 : 0)} web source${res.sources.length - (res.docs.length ? 1 : 0) === 1 ? '' : 's'}` : ''} · ${res.sections} section${res.sections === 1 ? '' : 's'}.`);
      onChanged();
      refreshInsight(['business']);
    } catch (e) { setSbMsg(`Failed: ${String((e as Error).message)}`); }
    finally { setSbBusy(false); }
  }

  async function refresh() {
    setBusy(true); setMsg('Analysing sub-industry & India market (deck + web + live peers)…');
    try {
      const r = await industryFetch(dealId, true); // manual "↻ Refresh analysis" → bypass the 24h cache
      setMsg(`Updated · ${r.industryDetail.sources.length} source${r.industryDetail.sources.length === 1 ? '' : 's'}${r.creditsUsed ? ` · ${r.creditsUsed} credits` : ''}.`);
      onChanged();
    } catch (e) { setMsg(`Failed: ${String((e as Error).message)}`); }
    finally { setBusy(false); }
  }

  const refreshBtn = (
    <div className="flex items-center gap-2.5">
      {ind?.fetchedAt && <span className="text-[11px] text-muted-2">Last refreshed {new Date(ind.fetchedAt).toLocaleString()}</span>}
      <button disabled={busy} onClick={refresh} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">
        {busy && <span className="inline-block w-3 h-3 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />}
        {busy ? 'Analysing…' : ind ? '↻ Refresh analysis' : '↻ Generate analysis'}
      </button>
    </div>
  );

  const storyboardBtn = (
    <button disabled={sbBusy} onClick={buildStoryboard} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">
      {sbBusy && <span className="inline-block w-3 h-3 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />}
      {sbBusy ? 'Reading…' : bdSections.length ? '↻ Refresh storyboard' : '✨ Generate storyboard'}
    </button>
  );

  return (
    <>
      {(d.business || bdSections.length > 0) && (
        <Card title="Business summary">
          <SectionInsight dealId={dealId} section="business" label="What this company does, at a glance" />
        </Card>
      )}
      {/* Company storyboard — everything a research analyst needs, without opening the deck */}
      <Card
        title="Company storyboard"
        sub={bdSections.length
          ? <>{bd?.builtFrom === 'deck+web' ? 'From the uploaded deck + web research' : bd?.builtFrom === 'agent' ? 'From the deck + enrichment agent' : 'From the uploaded deck only — refresh to add web research'}, in plain language{bd?.fetchedAt ? ` · built ${new Date(bd.fetchedAt).toLocaleString()}` : ''}</>
          : 'What the company does, since when, who pays it and how — written so someone with zero background fully understands it'}
        actions={storyboardBtn}>
        {sbMsg && <div className={`text-[11.5px] mb-2 ${sbMsg.startsWith('Failed') ? 'text-red' : 'text-muted'}`}>{sbMsg}</div>}
        {sbBusy && !bdSections.length ? <Skeleton lines={6} h={11} /> : bdSections.length ? (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2.5"><div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2">Founded</div><div className="text-[13px] font-semibold text-navy mt-0.5">{glance(d.founded, prov.founded)}</div></div>
              <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2.5"><div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2">Employees</div><div className="text-[13px] font-semibold text-navy mt-0.5">{glance(d.employees, prov.employees)}</div></div>
              <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2.5"><div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2">Facilities</div><div className="text-[13px] font-semibold text-navy mt-0.5">{glance(d.facilities, prov.facilities)}</div></div>
              <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2.5"><div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2">Promoter</div><div className="text-[13px] font-semibold text-navy mt-0.5">{glance(d.promoter, prov.promoter)}</div></div>
            </div>
            {bd!.summary?.content && <p className="text-[13px] font-semibold text-navy leading-relaxed mb-2">{bd!.summary.content}</p>}
            {bdSections.map((s, i) => (
              <details key={s.key} open={i < 5} className="border-t border-line-2 py-1.5">
                <summary className="text-[11.5px] font-semibold text-navy cursor-pointer flex items-center gap-2">{s.label}{srcChip(bd![s.key]!.sourceUrl)}</summary>
                <Bullets text={bd![s.key]!.content} />
              </details>
            ))}
            {bd!.sources?.length ? (
              <div className="mt-3 pt-3 border-t border-line-2">
                <div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2 mb-1.5">Sources ({bd!.sources.length})</div>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {bd!.sources.map((s, i) => (
                    <span key={i} className="text-[11px]">
                      {/^https?:\/\//i.test(s) ? <a href={s} target="_blank" rel="noreferrer" className="text-teal-d underline">{(() => { try { return new URL(s).hostname.replace(/^www\./, ''); } catch { return s; } })()}</a> : <span className="text-muted">{s}</span>}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <Empty label={d.deck
            ? 'No storyboard yet — click "✨ Generate storyboard" to write it from the uploaded deck + web research.'
            : 'No storyboard yet — upload a deck on the Documents tab, then click "✨ Generate storyboard".'} />
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Revenue by customer">{d.customers.length ? <DataTable head={['Customer', '%', 'Note']} rows={d.customers.map((c) => [c.name, pct(c.pct), <span className="text-left block text-muted">{c.note ?? ''}</span>])} /> : <Empty />}</Card>
        <Card title="Product / revenue mix">{d.products.length ? <DataTable head={['Product', '%', 'Margin']} rows={d.products.map((p) => [p.name, pct(p.pct), pct(p.margin)])} /> : <Empty />}</Card>
      </div>

      {/* Sub-industry — prominent */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 p-3 rounded-xl bg-canvas border border-line-2">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2">Sub-industry</div>
          <div className="text-[15px] font-bold text-navy tracking-[-0.2px]">{d.subIndustry || <span className="text-muted font-semibold text-[13px]">Not classified yet</span>}</div>
          <div className="text-[11px] text-muted mt-0.5">Broad sector (valuation anchor): <b className="text-navy">{d.sector}</b></div>
        </div>
        {refreshBtn}
      </div>
      {msg && <div className={`text-[11.5px] mb-3 ${msg.startsWith('Failed') ? 'text-red' : 'text-muted'}`}>{msg}</div>}

      {ind ? (
        <Card title="Industry & market — deep dive" sub="India-specific · sourced. Never invents market-size figures without a source.">
          {(ind.marketSize || ind.cagr) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2.5"><div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2">Market size</div><div className="text-[13px] font-semibold text-navy mt-0.5">{ind.marketSize || '—'}</div></div>
              <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2.5"><div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2">Growth (CAGR)</div><div className="text-[13px] font-semibold text-navy mt-0.5">{ind.cagr || '—'}</div></div>
            </div>
          )}
          {sections.filter((s) => s.key !== 'marketSize' && s.key !== 'cagr').map((s) => (
            <IndustrySection key={s.key} label={s.label} content={String(ind[s.key])} />
          ))}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
            <BulletBlock label="Tailwinds" items={ind.tailwinds} tone="green" />
            <BulletBlock label="Headwinds" items={ind.headwinds} tone="red" />
            <BulletBlock label="What would change the thesis" items={ind.whatWouldChangeTheThesis} tone="amber" />
          </div>

          {ind.peerMoves && ind.peerMoves.length > 0 && (
            <>
              <H4>What the peers are doing</H4>
              <div className="space-y-1.5">
                {ind.peerMoves.map((p, i) => (
                  <div key={i} className="text-[12px] leading-snug border-t border-line-2 pt-1.5">
                    <span className="font-semibold text-navy">{p.name}</span>
                    <span className={`ml-1.5 text-[9.5px] uppercase tracking-wide px-1 py-0.5 rounded ${p.listed === false ? 'bg-amber-50 text-amber' : 'bg-teal-50 text-teal-d'}`}>{p.listed === false ? 'unlisted' : 'listed'}</span>
                    <span className="text-muted"> — {p.note}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {ind.glossary && ind.glossary.length > 0 && (
            <>
              <H4>Glossary</H4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                {ind.glossary.map((g, i) => (
                  <div key={i} className="leading-snug">
                    <dt className="font-semibold text-navy inline">{g.term}: </dt>
                    <dd className="text-muted inline">{g.def}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}

          {ind.fromTheInternet.length > 0 && (
            <>
              <H4>From the internet</H4>
              <ul className="space-y-1.5">
                {ind.fromTheInternet.map((l, i) => (
                  <li key={i} className="text-[12px] leading-snug">
                    <a href={l.url} target="_blank" rel="noreferrer" className="text-teal-d underline font-semibold">{l.title || l.url}</a>
                    <span className="text-muted-2"> · {l.source}{l.date ? ` · ${l.date}` : ''}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {ind.sources.length > 0 && (
            <div className="mt-4 pt-3 border-t border-line-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2 mb-1.5">Sources ({ind.sources.length})</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {ind.sources.map((s, i) => (
                  <span key={i} className="text-[11px]">
                    {/^https?:\/\//i.test(s) ? <a href={s} target="_blank" rel="noreferrer" className="text-teal-d underline">{(() => { try { return new URL(s).hostname.replace(/^www\./, ''); } catch { return s; } })()}</a> : <span className="text-muted">{s}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      ) : (
        <Card title="Industry & market">
          <p className="text-[12.5px] leading-relaxed">{d.industry || <Empty label="No industry analysis yet — click “✨ Generate analysis” above to build a sourced, India-specific deep-dive from the deck + web + live peers." />}</p>
        </Card>
      )}
    </>
  );
}

function ResearchTab({ d, autopopRunning, onRefresh }: { d: DealDto; autopopRunning: boolean; onRefresh: () => void }) {
  const res = d.research as ResearchDossier | null;

  const running = autopopRunning;
  const refreshBtn = (
    <div className="flex items-center gap-2.5">
      {res?.fetchedAt && <span className="text-[11px] text-muted-2">Fetched {new Date(res.fetchedAt).toLocaleString()}</span>}
      <button disabled={running} onClick={onRefresh} title="Fetches fresh from the web (uses Firecrawl credits) — normal opens are cached & free." className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">
        {running && <span className="inline-block w-3 h-3 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />}
        {running ? 'Researching…' : '↻ Refresh research'}
      </button>
    </div>
  );

  const sections = res?.sections ?? [];
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 p-3 rounded-xl bg-canvas border border-line-2">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-2">Web research dossier</div>
          <div className="text-[13px] text-muted mt-0.5">Open-ended, sourced facts gathered automatically from the web. Every fact links to where it came from — never invented.</div>
        </div>
        {refreshBtn}
      </div>

      {sections.length ? (
        <>
          {sections.map((sec, i) => (
            <Card key={i} title={sec.title}>
              <dl className="grid grid-cols-[minmax(120px,auto)_1fr] gap-x-4 gap-y-2 text-[12.5px]">
                {sec.items.map((it, j) => (
                  <div key={j} className="contents">
                    <dt className="text-muted font-semibold">{it.label}</dt>
                    <dd className="text-navy">
                      <span className="inline-flex flex-wrap items-baseline gap-1.5">
                        <span>{it.value}</span>
                        {it.date && <span className="text-[10px] text-muted-2">· {it.date}</span>}
                        {srcChip(it.sourceUrl)}
                      </span>
                    </dd>
                  </div>
                ))}
              </dl>
            </Card>
          ))}
          {res!.sources.length > 0 && (
            <Card title={`Sources (${res!.sources.length})`}>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {res!.sources.map((s, i) => (
                  <span key={i} className="text-[11px]">
                    {/^https?:\/\//i.test(s)
                      ? <a href={s} target="_blank" rel="noreferrer" className="text-teal-d underline">{(() => { try { return new URL(s).hostname.replace(/^www\./, ''); } catch { return s; } })()}</a>
                      : <span className="text-muted">{s}</span>}
                  </span>
                ))}
              </div>
            </Card>
          )}
        </>
      ) : running ? (
        <StagedLoader stages={AUTOPOP_STAGES} stageMs={9000} note="Gathering an open-ended, sourced dossier from the web…" />
      ) : (
        <Card title="Research">
          <Empty label="No research yet — attach a deck (it auto-populates on upload / first open) or click “↻ Refresh research” to build a sourced dossier from the web." />
        </Card>
      )}
    </>
  );
}

function Valuation({ d, onPatch }: { d: DealDto; onPatch: (p: Partial<Deal>) => void }) {
  // Founder demo (3 Aug 2026): enter a proposed valuation → implied P/E, MoS and MOIC recompute
  // live against real peer multiples. We feed the input straight through the ported valn() engine
  // (no engine math changed) so the numbers stay consistent with the rest of the app.
  const [proposed, setProposed] = useState<string>(d.valuationAsk ? String(d.valuationAsk) : '');
  const [peIpo, setPeIpo] = useState<string>(d.peIPO ? String(d.peIPO) : '');
  const propVal = Number(proposed) || d.valuationAsk || 0;
  const peIpoVal = Number(peIpo) || 0;
  const v = valn({ ...d, valuationAsk: propVal, ...(peIpoVal ? { peIPO: peIpoVal } : {}) } as unknown as Deal);
  const dirty = (propVal || 0) !== (d.valuationAsk || 0) || (!!peIpoVal && peIpoVal !== d.peIPO);
  // P/E, MoS and MOIC all divide by FY26 PAT — without a positive profit figure the engine
  // can't compute them (they show "—"). Say why, so the tab doesn't read as broken.
  const noPat = (Number(d.pat26) || 0) <= 0;
  const mosTone = !v.ok ? 'gray' : v.mos >= 30 ? 'green' : v.mos >= 15 ? 'amber' : 'red';
  const inputCls = 'w-full px-2.5 py-2 border border-line rounded-lg text-[13px] mono';
  return (
    <>
      <Card title="Valuation inputs" sub="Enter the proposed/asked valuation — P/E, margin of safety and MOIC recompute live against the peer multiples.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <div>
            <label className="block text-[11px] font-semibold text-muted mb-1">Proposed valuation (₹ Cr)</label>
            <input type="number" className={inputCls} value={proposed} onChange={(e) => setProposed(e.target.value)} placeholder={d.valuationAsk ? String(d.valuationAsk) : 'e.g. 300'} />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-muted mb-1">Peer P/E at IPO <span className="text-muted-2 font-normal">(editable)</span></label>
            <input type="number" className={inputCls} value={peIpo} onChange={(e) => setPeIpo(e.target.value)} placeholder={xx(v.peIPO)} />
            <div className="text-[10.5px] text-muted-2 mt-1">Default is a 20% discount to peer P/E now ({xx(v.peNow)}). Override to change the exit multiple.</div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-muted mb-1">PAT (FY26)</label>
            <div className={`${inputCls} bg-canvas flex items-center`}>{d.pat26 ? inr(d.pat26) : <span className="text-muted-2">— not set</span>}</div>
            <div className="text-[10.5px] text-muted-2 mt-1">Implied P/E = valuation ÷ PAT.</div>
          </div>
        </div>
        {noPat && (
          <div className="mt-3 text-[11.5px] text-amber font-semibold bg-amber-50 rounded-lg px-3 py-2">
            ⚠ No FY26 PAT on this deal, so implied P/E, margin of safety and MOIC can't be computed (they show "—"). Add the FY26 PAT (Edit deal, or the Financials tab) — this valuation is profit-multiple based.
          </div>
        )}
        {dirty && (
          <div className="mt-3 flex items-center gap-2">
            <button onClick={() => onPatch({ valuationAsk: propVal, ...(peIpoVal ? { peIPO: peIpoVal } : {}) })} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d">Save to deal</button>
            <span className="text-[11px] text-muted-2">Numbers below update live; Save writes the valuation to the deal record.</span>
          </div>
        )}
      </Card>
      <Card title="Margin of safety" sub={`Entry ${v.ok ? xx(v.entryPE) : '—'} vs peer ${xx(v.peNow)}`}>
        <div className="text-center py-3">
          <div className="text-[34px] font-extrabold text-navy leading-none">{v.ok ? pct(v.mos) : '—'}</div>
          <div className="mt-2"><Pill tone={mosTone}>{v.ok ? (v.mos >= 30 ? 'Attractive discount' : v.mos >= 15 ? 'Thin discount' : 'Little/no discount') : 'n/m'}</Pill></div>
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Entry vs peer P/E">
          <DL rows={[['Implied entry P/E', v.ok ? xx(v.entryPE) : '—'], ['Peer P/E now', xx(v.peNow)], ['Peer P/E at IPO', xx(v.peIPO)], ['Hold period', `${v.yrs.toFixed(1)} yr`], ['Assumed growth', pct(v.g, 1)]]} />
        </Card>
        <Card title="MOIC scenarios" sub="PAT growth → listing multiple over the IPO horizon">
          <DataTable head={['Scenario', 'MOIC']} rows={[['Bear', v.ok ? xx(v.bear) : '—'], ['Base', v.ok ? xx(v.base) : '—'], ['Bull', v.ok ? xx(v.bull) : '—']]} />
        </Card>
      </div>
    </>
  );
}

function Compliance({ d, dealId, onPatch, onChanged }: { d: DealDto; dealId: string; onPatch: (p: Partial<Deal>) => void; onChanged: () => void }) {
  // Defensive on every level — this component went from dead code (never rendered, so never
  // exercised against a real deal) to live in one pass, and jsonb `intel` blobs aren't guaranteed to
  // carry every key EMPTY_INTEL would (older/hand-built deals, deals never touched by a merge pass
  // that fills these specific fields). Same class of bug as the Team & Mgmt crash — see decision.md D14/D17.
  const t = d.intel ?? ({} as NonNullable<Deal['intel']>);
  const compliance = t.compliance ?? { gst: '', tds: '', pf: '', roc: '' };
  const litigation = t.litigation ?? { sc: 0, hc: 0, trib: 0, note: '' };
  const strengths = t.strengths ?? [];
  const weaknesses = t.weaknesses ?? [];
  const viaProbe42 = !!t.probe42; // a Probe42 pull has landed on this deal — rating/GST/PF/litigation below are sourced from it, not web research

  // The FULL detail behind the counts above — free (cached) re-read of an already-paid pull, so this
  // never bills again. Silently absent (not an error state) for anyone without access to Registry
  // Intelligence yet, or before a pull has happened — the summary cards above still work either way.
  const { data: res } = useQuery({ queryKey: ['probe42', dealId], queryFn: () => probe42Get(dealId), enabled: viaProbe42, retry: false, staleTime: 60_000 });
  const root = ((res?.data?.raw as any)?.data ?? res?.data?.raw ?? {}) as Record<string, any>;
  const struckOff = root.struckoff248_details?.struck_off_status as string | undefined;
  const isStruckOff = !!struckOff && !/never removed|not.*struck|no.*record/i.test(struckOff);
  const defaulters = Array.isArray(root.defaulter_list) ? root.defaulter_list : [];
  const gstRows = Array.isArray(root.gst_details) ? root.gst_details : [];
  const legalRows: any[] = (Array.isArray(root.legal_cases_of_financial_disputes) && root.legal_cases_of_financial_disputes.length)
    ? root.legal_cases_of_financial_disputes
    : (Array.isArray(root.legal_history) ? root.legal_history : []);
  const msmeDelays = root.msme_supplier_payment_delays;
  const cirpStatus = t.registry?.cirpStatus;
  const ratingRationale = Array.isArray(root.credit_rating_rationale) ? root.credit_rating_rationale : [];

  return (
    <>
      {viaProbe42 && (
        <Card title="Compliance & risk summary">
          <SectionInsight dealId={dealId} section="compliance" label="Overall compliance & risk read" />
        </Card>
      )}
      {!viaProbe42 && <div className="mb-4"><ComingSoon title="Full credit & compliance — coming soon" note="Rating, GST/TDS/PF status and litigation are populated from web research today. Pull Registry Intelligence for licensed registry data instead." /></div>}
      {(isStruckOff || defaulters.length > 0 || !!cirpStatus) && (
        <div className="mb-4 rounded-lg border border-red/40 bg-red-50 px-4 py-3">
          <div className="text-[12.5px] font-bold text-red mb-1">⚠ Flagged</div>
          {cirpStatus && <div className="text-[12px] text-navy font-semibold">Active insolvency proceeding (CIRP): {cirpStatus}</div>}
          {isStruckOff && <div className="text-[12px] text-navy">{struckOff}</div>}
          {defaulters.length > 0 && <div className="text-[12px] text-navy mt-1">On {defaulters.length} bureau defaulter list{defaulters.length === 1 ? '' : 's'} — see Registry Intelligence for detail.</div>}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Credit & compliance" sub={viaProbe42 ? 'GST/PF/RoC via Registry Intelligence' : undefined}>
          <DL rows={[
            ['Rating', t.rating || '—'],
            ['Registered charges', String(t.charges ?? 0)],
            ['GST', compliance.gst || '—'],
            ['TDS', compliance.tds || <span title="No source — deck, Registry Intelligence and web research all lack a TDS compliance field for this company. Genuinely unavailable, not a bug.">— <span className="text-muted-2 text-[10px] font-normal">(no source)</span></span>],
            ['PF', compliance.pf || (viaProbe42 ? <span title="Registry Intelligence returned no EPFO-registered establishments for this company — a real absence in the data, not a bug.">— <span className="text-muted-2 text-[10px] font-normal">(none on record)</span></span> : '—')],
            ['RoC', compliance.roc || '—'],
          ]} />
        </Card>
        <Card
          title="Litigation"
          sub={viaProbe42 && legalRows.length ? (
            <a href="#legal-cases-detail" className="text-teal-d hover:underline">{legalRows.length} case{legalRows.length === 1 ? '' : 's'} — view all →</a>
          ) : undefined}
        >
          {viaProbe42 && <SectionInsight dealId={dealId} section="litigation" label="Litigation summary" />}
          <DL rows={[['Supreme Court', String(litigation.sc ?? 0)], ['High Court', String(litigation.hc ?? 0)], ['Tribunal', String(litigation.trib ?? 0)], ['Note', litigation.note || '—']]} />
        </Card>
      </div>
      <Card title="Published agency ratings" sub={viaProbe42 ? 'Per rated facility — via Registry Intelligence' : 'CRISIL / ICRA / CARE / India Ratings'}>
        <AgencyRatingsBlock dealId={dealId} ratings={t.agencyRatings} onChanged={onChanged} />
        {ratingRationale.length > 0 && (
          <div className="mt-3 pt-3 border-t border-line-2">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-muted-2 mb-1.5">Rating rationale documents on file</div>
            <ul className="space-y-1">
              {ratingRationale.map((r: any, i: number) => (
                <li key={i} className="text-[11.5px] text-navy">{humanize(r.rating_agency ?? '—')}{r.financial_year ? ` · FY as of ${r.financial_year}` : ''}<span className="text-muted-2"> — referenced by Registry Intelligence, not yet downloadable in-app</span></li>
              ))}
            </ul>
          </div>
        )}
      </Card>
      {gstRows.length > 0 && (
        <Card title="GST registrations" sub={`${gstRows.length} GSTIN${gstRows.length === 1 ? '' : 's'} on record — via Registry Intelligence`}>
          <SectionInsight dealId={dealId} section="gst" label="GST compliance summary" />
          <P42Table rows={gstRows.map((g) => ({ gstin: g.gstin, state: g.state, status: g.status, taxpayer_type: g.taxpayer_type, filing_timeliness: g.filing_timeliness, date_of_registration: g.date_of_registration }))} />
        </Card>
      )}
      {msmeDelays && (isArrayOfRecords(msmeDelays.delays) && msmeDelays.delays.length > 0) && (
        <Card title="MSME supplier payment delays" sub={`Latest period: ${msmeDelays.delays_for_period?.latest_period ?? '—'} — via Registry Intelligence`}>
          <P42Table rows={msmeDelays.delays} />
        </Card>
      )}
      {legalRows.length > 0 && (
        <Card title="Legal cases" sub={`${legalRows.length} case${legalRows.length === 1 ? '' : 's'} on record — via Registry Intelligence`}>
          <div id="legal-cases-detail" className="scroll-mt-4">
            <details><summary className="text-[12px] font-semibold text-teal-d cursor-pointer">Show cases (largest lists are capped — full detail on Registry Intelligence)</summary>
              <div className="mt-2"><P42Table rows={legalRows} /></div>
            </details>
          </div>
        </Card>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Strengths"><EditableList items={strengths} onSave={(v) => onPatch({ intel: { ...t, strengths: v } })} /></Card>
        <Card title="Weaknesses"><EditableList items={weaknesses} onSave={(v) => onPatch({ intel: { ...t, weaknesses: v } })} /></Card>
      </div>
    </>
  );
}

function RiskTab({ d, onPatch }: { d: DealDto; onPatch: (p: Partial<Deal>) => void }) {
  return (
    <>
      <div className="mb-4"><ComingSoon title="Automated risk data — coming soon" note="Analysts can maintain the risk register manually below. An automated risk/credit data feed (third-party API) is being sourced and will populate this next." /></div>
      <Card title="Risk register">
        <EditableRows
          cols={[{ k: 'risk', label: 'Risk', w: 'flex-[2]' }, { k: 'prob', label: 'Prob' }, { k: 'impact', label: 'Impact' }, { k: 'mit', label: 'Mitigation', w: 'flex-[2]' }]}
          rows={d.risks as unknown as Record<string, string>[]}
          blank={{ risk: '', prob: 'Med', impact: 'Med', mit: '' }}
          onSave={(rows) => onPatch({ risks: rows as unknown as Deal['risks'] })}
        />
      </Card>
    </>
  );
}

/** Questions to ask the promoter — deck-derived where a deck exists, else grounded on Registry
 *  Intelligence data if a pull is on file; either way, categorized and deliberately excluding
 *  anything the source already answers (product review item 11). */
function QuestionsTab({ d, dealId }: { d: DealDto; dealId: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const pq = d.promoterQuestions;
  const total = pq?.categories.reduce((n, c) => n + c.questions.length, 0) ?? 0;
  const hasDeck = d.documents.length > 0;
  const hasRegistry = !!d.intel?.probe42;

  async function gen() {
    setBusy(true); setMsg('');
    try {
      const r = await questionsFetch(dealId, true);
      setMsg(r.categories > 0 ? `Generated ${r.questions ?? 0} questions across ${r.categories} categories.` : `No deck and no Registry Intelligence data to generate questions from — ${hasRegistry ? 'the pull on file had nothing to ground a question on' : 'upload a deck on the Documents tab or pull Registry Intelligence'}.`);
      qc.invalidateQueries({ queryKey: ['deal', dealId] });
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  return (
    <Card title={`Questions to ask the promoter${total ? ` · ${total}` : ''}`}
      sub={`Generated from ${hasDeck ? 'the deck' : hasRegistry ? 'Registry Intelligence data (no deck uploaded)' : 'the deck'} for the diligence call — deliberately excludes anything the source already answers.`}
      actions={<button disabled={busy} onClick={gen} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">{busy ? <InlineBusy>Generating…</InlineBusy> : (pq ? '↻ Regenerate' : '✨ Generate')}</button>}>
      {busy && !pq ? (
        <StagedLoader stages={[hasDeck ? 'Reading the deck' : 'Reading registry data', 'Finding the gaps', 'Drafting questions', 'Done']} stageMs={6000} />
      ) : pq && pq.categories.length ? (
        <div className="space-y-4">
          {pq.categories.map((cat) => (
            <div key={cat.title}>
              <div className="flex items-center gap-2 mb-1.5">
                <h4 className="text-[11px] font-bold uppercase tracking-[0.5px] text-teal-d">{cat.title}</h4>
                <span className="text-[10.5px] text-muted-2">· {cat.questions.length}</span>
                <span className="h-px bg-line-2 flex-1" />
              </div>
              <ol className="list-decimal pl-5 space-y-1">
                {cat.questions.map((q, i) => <li key={i} className="text-[12.5px] text-navy leading-relaxed">{q}</li>)}
              </ol>
            </div>
          ))}
          {pq.fetchedAt && <div className="text-[10.5px] text-muted-2 pt-1">Generated {new Date(pq.fetchedAt).toLocaleDateString()} · review & trim before the call.</div>}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="text-[26px] mb-1.5">❓</div>
          <p className="text-[13px] font-semibold text-navy">No questions yet</p>
          <p className="text-[11.5px] text-muted mt-1 max-w-[440px] mx-auto">
            {hasDeck
              ? "Generate a categorized list of diligence questions from the deck — only the things the deck doesn't already answer, so the promoter call stays sharp."
              : hasRegistry
              ? "No deck uploaded — generate questions grounded on the Registry Intelligence data pulled for this deal instead (business/strategy questions plus follow-ups on anything the registry data flags)."
              : "Upload a deck or pull Registry Intelligence first, then generate a categorized list of diligence questions covering only what isn't already answered."}
          </p>
          <button disabled={busy} onClick={gen} className="mt-3 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-navy text-white hover:bg-navy-2 disabled:opacity-50">✨ Generate questions</button>
        </div>
      )}
      {msg && !busy && <div className="text-[11.5px] text-muted mt-3">{msg}</div>}
    </Card>
  );
}

/** Median of a numeric list, or null. */
function median(xs: number[]): number | null {
  const s = xs.filter((n) => typeof n === 'number' && n > 0).sort((a, b) => a - b);
  if (!s.length) return null;
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}
const SCREENER = 'https://www.screener.in';

/** Tiny inline sparkline for a peer's revenue trend (SVG, no deps). */
function Spark({ vals, tone = 'teal' }: { vals: (number | null)[]; tone?: string }) {
  const nums = vals.filter((v): v is number => typeof v === 'number');
  if (nums.length < 2) return <span className="text-muted-2">—</span>;
  const min = Math.min(...nums), max = Math.max(...nums), span = max - min || 1;
  const w = 68, h = 20;
  const pts = nums.map((v, i) => `${(i / (nums.length - 1)) * w},${h - ((v - min) / span) * h}`).join(' ');
  const stroke = tone === 'green' ? '#1a9d6b' : '#0f9b8e';
  return <svg width={w} height={h} className="inline-block align-middle"><polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" /></svg>;
}

/** Renders the IPO-history CASCADE record: per-field value + source link + hop badge + labels. */
function IpoHistoryBlock({ hist, stated }: { hist: IpoHistory; stated: IpoDetails | null }) {
  const HOP = { 1: 'Chittorgarh', 2: 'NSE/BSE', 3: 'SEBI RHP' } as const;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Row = ({ label, f, fmt }: { label: string; f: IpoField<any>; fmt?: (v: any) => ReactNode }) => {
    if (f.value == null || (Array.isArray(f.value) && !f.value.length)) return null;
    return (
      <>
        <dt className="text-muted">{label}</dt>
        <dd className="text-navy font-semibold text-right">
          <span className="align-middle">{fmt ? fmt(f.value) : String(f.value)}</span>
          {f.source && (
            <a href={f.source} target="_blank" rel="noopener noreferrer" title={`Source: ${HOP[f.hop ?? 1]} · ${f.source}`} className="ml-1.5 text-[9px] font-bold uppercase tracking-wide text-teal-d hover:underline align-middle">{HOP[f.hop ?? 1]}↗</a>
          )}
        </dd>
      </>
    );
  };
  const boardLabel: Record<string, string> = { mainboard: 'Mainboard', sme: 'SME (NSE Emerge / BSE SME)', other: 'Other (demerger / direct)', none: 'Not an IPO listing' };
  const anyValue = [hist.ipoDate, hist.issuePrice, hist.listingPrice, hist.brlm, hist.subscription, hist.issueSize].some((f) => f.value != null && !(Array.isArray(f.value) && !f.value.length));
  return (
    <div>
      {hist.label && (
        <p className="text-[11px] text-amber bg-amber-50 border border-amber/30 rounded px-2 py-1 mb-1.5">{hist.label}</p>
      )}
      {anyValue ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          <Row label="Listed" f={hist.ipoDate} />
          {hist.ipoBoard.value && (<><dt className="text-muted">Board</dt><dd className="text-navy font-semibold text-right">{boardLabel[hist.ipoBoard.value] ?? hist.ipoBoard.value}</dd></>)}
          <Row label="Issue price" f={hist.issuePrice} fmt={(v) => `₹${v}`} />
          <Row label="Listing price" f={hist.listingPrice} fmt={(v) => `₹${v}`} />
          <Row label="Listing gain" f={hist.listingGainPct} fmt={(v) => `${v}%`} />
          <Row label="Issue size" f={hist.issueSize} />
          <Row label="Subscribed" f={hist.subscription} />
          {/* BRLM as a LIST (multiple bankers is the norm) */}
          {hist.brlm.value && hist.brlm.value.length > 0 && (
            <>
              <dt className="text-muted">Lead managers</dt>
              <dd className="text-navy text-right">
                <ul className="inline-block text-right">
                  {hist.brlm.value.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                {hist.brlm.source && <a href={hist.brlm.source} target="_blank" rel="noopener noreferrer" title={`Source: ${HOP[hist.brlm.hop ?? 1]}`} className="ml-1.5 text-[9px] font-bold uppercase tracking-wide text-teal-d hover:underline">{HOP[hist.brlm.hop ?? 1]}↗</a>}
              </dd>
            </>
          )}
          {/* Derived P/E at listing — only when we could compute it honestly; formula in a tooltip */}
          {hist.peAtListing.value != null && (
            <>
              <dt className="text-muted">P/E at listing</dt>
              <dd className="text-navy font-semibold text-right mono" title={hist.peAtListingBasis ?? undefined}>{hist.peAtListing.value}<span className="text-muted-2 text-[10px] ml-0.5">ⓘ</span></dd>
            </>
          )}
          {/* Chittorgarh's stated P/E-at-listing (page value) — kept for continuity, distinct from the derived one */}
          {hist.peAtListing.value == null && stated?.peList && (
            <><dt className="text-muted">P/E at listing (stated)</dt><dd className="text-navy font-semibold text-right mono">{stated.peList}</dd></>
          )}
        </dl>
      ) : (
        <p className="text-muted-2">{hist.label ?? 'No IPO facts resolved — enter manually in Edit if needed.'}</p>
      )}
    </div>
  );
}

/** Expanded per-peer drill-down: About + journey + trend + valuation context + headlines. */
function PeerDrilldown({ peer, d, medianPe }: { peer: PeerRowT; d: DealDto; medianPe: number | null }) {
  const [state, setState] = useState<{ status: 'loading' | 'done' | 'error'; detail?: PeerDetail; msg?: string }>({ status: 'loading' });
  useEffect(() => {
    let live = true;
    peersDetail(peer.name, peer.ticker)
      .then((r) => { if (live) setState({ status: 'done', detail: r.detail }); })
      .catch((e) => { if (live) setState({ status: 'error', msg: String((e as Error).message) }); });
    return () => { live = false; };
  }, [peer.name, peer.ticker]);

  const v = valn(d);
  const entryPe = v.ok ? v.entryPE : null;
  const peerPe = peer.pe || state.detail?.pe || null;
  const trend = state.detail?.trend ?? [];
  const ipo = state.detail?.ipo ?? null;
  const hist = state.detail?.ipoHist ?? peer.ipoHist ?? null;

  return (
    <div className="bg-canvas border-t border-line-2 px-4 py-3.5 text-[12px]">
      {state.status === 'loading' && <div className="text-muted">Loading {peer.name} detail from screener / Chittorgarh / news…</div>}
      {state.status === 'error' && <div className="text-red">Couldn’t load detail: {state.msg}</div>}
      {state.status === 'done' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Business + journey */}
          <div className="space-y-3">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.4px] text-muted mb-1">Business</div>
              <p className="text-navy leading-relaxed">{state.detail!.about || 'No business description on the screener page.'}</p>
            </div>
            {/* Full screener ratios panel — Book Value, ROCE, ROE, Dividend Yield, Face Value, 52w
                High/Low, etc. Shown verbatim so the analyst has as much fundamental data as screener
                exposes, not just the headline P/E. */}
            {peer.ratios && peer.ratios.length > 0 && (
              <div>
                <div className="text-[10.5px] font-bold uppercase tracking-[0.4px] text-muted mb-1">Key ratios (screener)</div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {peer.ratios.map((r) => (
                    <div key={r.label} className="flex justify-between gap-2 border-b border-line-2/50 py-0.5">
                      <dt className="text-muted truncate" title={r.label}>{r.label}</dt>
                      <dd className="text-navy font-semibold text-right mono whitespace-nowrap">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.4px] text-muted mb-1">Journey / IPO history</div>
              {hist ? <IpoHistoryBlock hist={hist} stated={ipo} /> : ipo && ipo.ipoYear ? (
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  <dt className="text-muted">Listed</dt><dd className="text-navy font-semibold text-right">{ipo.ipoYear}</dd>
                  {ipo.issuePrice != null && (<><dt className="text-muted">Issue price</dt><dd className="text-navy font-semibold text-right mono">₹{ipo.issuePrice}</dd></>)}
                  {ipo.brlm && (<><dt className="text-muted">Lead managers</dt><dd className="text-navy text-right">{ipo.brlm}</dd></>)}
                </dl>
              ) : (
                <p className="text-muted-2">Listed pre-2007 — no Chittorgarh page. Enter IPO facts manually in Edit if needed.</p>
              )}
            </div>
          </div>
          {/* Trend + valuation + news */}
          <div className="space-y-3">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.4px] text-muted mb-1">Revenue / EBITDA / PAT trend (₹ Cr)</div>
              {trend.length ? (
                <table className="w-full text-[11px]">
                  <thead><tr className="text-muted-2 text-[10px]">{['Year', 'Rev', 'EBITDA', 'PAT', 'Margin'].map((h, i) => <th key={h} className={`py-0.5 font-bold ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
                  <tbody>
                    {trend.map((t, i) => (
                      <tr key={i} className="border-t border-line-2">
                        <td className="py-0.5 text-navy">{t.y}</td>
                        <td className="py-0.5 text-right mono">{t.rev != null ? Math.round(t.rev).toLocaleString('en-IN') : '—'}</td>
                        <td className="py-0.5 text-right mono">{t.eb != null ? Math.round(t.eb).toLocaleString('en-IN') : '—'}</td>
                        <td className="py-0.5 text-right mono">{t.pat != null ? Math.round(t.pat).toLocaleString('en-IN') : '—'}</td>
                        <td className="py-0.5 text-right mono">{t.margin != null ? `${t.margin}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-muted-2">No multi-year series on screener.</p>}
            </div>
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.4px] text-muted mb-1">Valuation context</div>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                <dt className="text-muted">This peer P/E</dt><dd className="text-navy font-semibold text-right mono">{peerPe ? xx(peerPe) : '—'}</dd>
                <dt className="text-muted">Peer-set median P/E</dt><dd className="text-navy font-semibold text-right mono">{medianPe ? xx(medianPe) : '—'}</dd>
                <dt className="text-muted">Deal implied entry P/E</dt><dd className="text-navy font-semibold text-right mono">{entryPe ? xx(entryPe) : '—'}</dd>
              </dl>
              {peerPe && entryPe && (
                <p className="text-[11px] text-muted-2 mt-1">Entry is {xx(Math.abs(entryPe - peerPe))} {entryPe < peerPe ? 'below' : 'above'} this peer; {medianPe ? `${entryPe < medianPe ? 'below' : 'above'} the set median (median feeds peNow / MoS).` : ''}</p>
              )}
            </div>
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.4px] text-muted mb-1">Recent headlines</div>
              {state.detail!.headlines.length ? (
                <ul className="space-y-0.5">
                  {state.detail!.headlines.map((h, i) => (
                    <li key={i} className="leading-snug">
                      {h.url ? <a href={h.url} target="_blank" rel="noopener noreferrer" className="text-teal-d hover:underline">{h.headline}</a> : <span className="text-navy">{h.headline}</span>}
                      {h.source && <span className="text-muted-2 text-[10.5px]"> · {h.source}</span>}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-muted-2">No recent headlines for this peer in the monitoring feed.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

type SortKey = 'name' | 'rev' | 'mcap' | 'price' | 'ipoYear' | 'pe' | 'margin';
const PEER_STAGES = ['Resolving names', 'Pulling fundamentals', 'Computing median P/E', 'Done'];
const IPO_STAGES = ['Resolving peers', 'Fetching IPO history (Chittorgarh)', 'Filling IPO details', 'Done'];
function Peers({ d, onPatch }: { d: DealDto; onPatch: (p: Partial<Deal>) => void }) {
  const qc = useQueryClient();
  const refreshInsight = useInsightRefresh(d.id);
  // A DIFFERENT peer concept from the listed-comp table above: Probe42 benchmarks against similar
  // REGISTERED companies by revenue (private or public, not necessarily listed) — real, complementary
  // context, kept separate rather than mixed into `deals.peers` (which the screener/IPO lookups above
  // expect to be listed-company names, and would fail to resolve most of these against).
  const { data: p42Res } = useQuery({ queryKey: ['probe42', d.id], queryFn: () => probe42Get(d.id), retry: false, staleTime: 60_000 });
  const p42Root = ((p42Res?.data?.raw as any)?.data ?? p42Res?.data?.raw ?? {}) as Record<string, any>;
  const benchmark = Array.isArray(p42Root.peer_comparison) && p42Root.peer_comparison.length ? p42Root.peer_comparison[0] : null;
  const [names, setNames] = useState<string[]>(d.peers ?? []);
  const [draft, setDraft] = useState('');
  const [busyKind, setBusyKind] = useState<'' | 'peers' | 'ipo' | 'discover'>('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  // Default to most-recent listing first (founder demo: recency, not maturity, is the relevant peer).
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'ipoYear', dir: -1 });
  const busy = busyKind !== '';
  const add = () => { const n = draft.trim(); if (n && !names.includes(n)) { setNames([...names, n]); setDraft(''); } };
  const remove = (n: string) => setNames(names.filter((x) => x !== n));
  // Keep the editable name chips in sync with the persisted peer set — so after "Discover recent
  // peers" (which overwrites deals.peers server-side) the chips show the discovered names. Without
  // this the chips stayed stale and a later "Refresh listed" re-resolved the OLD names, wiping the
  // discovered set. Keyed on the joined set so it only fires when the persisted peers actually change.
  const persistedPeers = (d.peers ?? []).join('|');
  useEffect(() => { setNames(d.peers ?? []); }, [persistedPeers]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchData() {
    if (!names.length) { setMsg('Add at least one listed-peer name first.'); return; }
    setBusyKind('peers'); setErr(''); setMsg('');
    try {
      const { peers } = await peersLookup(names); // IPO folded in — one action fills the table
      if (!peers.length) { setMsg('None of those names resolved on screener.in — check spelling (use listed-company names).'); return; }
      const peerRows = peers.map((f) => ({
        name: f.name, ticker: f.ticker, about: f.about ?? null, price: f.price ?? null, rev: f.revenue ?? 0, mcap: f.marketCap ?? 0,
        ipoYear: f.ipo?.ipoYear ?? '', brlm: f.ipo?.brlm ?? '', peList: f.ipo?.peList ?? '',
        pe: f.pe ?? 0, margin: f.netMargin ?? 0,
        issuePrice: f.ipo?.issuePrice ?? null, issueSize: f.ipo?.issueSize, subscription: f.ipo?.subscription,
        listingGain: f.ipo?.listingGain, ipoChecked: f.ipo?.checked, ipoHist: f.ipoHist ?? null,
        ratios: f.ratios ?? [],
      }));
      const canonical = peers.map((f) => f.name);
      const peNow = median(peers.map((f) => f.pe ?? 0));
      const patch: Partial<Deal> = { peers: canonical, peerRows: peerRows as unknown as Deal['peerRows'] };
      if (peNow) { patch.peNow = Math.round(peNow * 10) / 10; patch.peIPO = Math.round(peNow * 0.8 * 10) / 10; }
      setNames(canonical);
      onPatch(patch);
      const withIpo = peerRows.filter((p) => p.ipoYear).length;
      setMsg(`Updated ${peers.length} peer${peers.length === 1 ? '' : 's'} with live data${peNow ? ` · median P/E ${Math.round(peNow * 10) / 10}` : ''}${withIpo ? ` · ${withIpo} with IPO details` : ''}.`);
      refreshInsight(['valuation']);
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setBusyKind(''); }
  }

  // Auto-discover the RIGHT peer set: prefer recently-listed comparables (they carry real IPO
  // details), grounded in the deck + sector — instead of only re-resolving whatever names are
  // already listed. Runs the server discovery path (force → re-runs + overwrites) and refetches
  // the deal so the table repopulates. This is what surfaces recent peers the deck never named.
  async function discover() {
    setBusyKind('discover'); setErr(''); setMsg('');
    try {
      // overwrite (re-discover + replace) WITHOUT fresh — resolve fundamentals from the 24h cache
      // so the request stays fast/reliable (a full live re-scrape was timing out before persisting).
      const { peers, skipped, enrichingIpo } = await peersPopulate(d.id, { overwrite: true });
      await qc.invalidateQueries({ queryKey: ['deal', d.id] });
      if (!peers) {
        setMsg(`No listed comparables found${skipped ? ` (${skipped})` : ''} — this sector may have no recent listings.`);
      } else if (enrichingIpo) {
        // Peers are saved; IPO details fill in the background — refetch shortly so the columns appear.
        setMsg(`Found ${peers} comparable${peers === 1 ? '' : 's'} (recent first). Fetching their IPO details…`);
        setTimeout(() => { qc.invalidateQueries({ queryKey: ['deal', d.id] }); setMsg(`${peers} recent-listed comparable${peers === 1 ? '' : 's'} with IPO details.`); }, 30000);
      } else {
        setMsg(`Re-discovered ${peers} listed comparable${peers === 1 ? '' : 's'} (recent listings first, with IPO details).`);
      }
      if (peers) refreshInsight(['valuation']);
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setBusyKind(''); }
  }

  async function fetchIpo() {
    if (!d.peerRows.length) { setMsg('Fetch peer data first, then IPO details.'); return; }
    setBusyKind('ipo'); setErr(''); setMsg('');
    try {
      const { details } = await peersIpoDetails(d.peerRows.map((p) => p.name));
      const byName = new Map(details.map((x) => [x.name.toLowerCase(), x]));
      const merged = d.peerRows.map((p) => {
        const ipo = byName.get(p.name.toLowerCase());
        return ipo ? { ...p, ipoYear: ipo.ipoYear || p.ipoYear, brlm: ipo.brlm || p.brlm, peList: ipo.peList || p.peList, issuePrice: ipo.issuePrice ?? p.issuePrice, issueSize: ipo.issueSize || p.issueSize, subscription: ipo.subscription || p.subscription, listingGain: ipo.listingGain || p.listingGain, ipoChecked: true } : p;
      });
      const filled = details.filter((x) => x.ipoYear || x.brlm).length;
      onPatch({ peerRows: merged as unknown as Deal['peerRows'] });
      setMsg(filled ? `IPO details filled for ${filled} of ${details.length} peer${details.length === 1 ? '' : 's'} (recent IPOs only).` : 'No recent-IPO details found — these peers all listed before ~2007.');
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setBusyKind(''); }
  }

  const v = valn(d);
  const rows = d.peerRows;
  const medPe = median(rows.map((p) => p.pe));
  const medMargin = median(rows.map((p) => p.margin));
  const revs = rows.map((p) => p.rev).filter((n) => n > 0);
  const revLo = revs.length ? Math.min(...revs) : null, revHi = revs.length ? Math.max(...revs) : null;

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const pa = pinned.has(a.name) ? 1 : 0, pb = pinned.has(b.name) ? 1 : 0;
      if (pa !== pb) return pb - pa; // pinned first
      const k = sort.key;
      // IPO year: sort by the 4-digit listing year numerically so "most recent first" works
      // (founder demo — recency is the relevant peer, not maturity). Blanks / pre-Chittorgarh
      // (no year) collapse to 0 and fall to the bottom on the default descending sort.
      if (k === 'ipoYear') {
        const ya = Number(String(a.ipoYear ?? '').match(/\d{4}/)?.[0] ?? 0);
        const yb = Number(String(b.ipoYear ?? '').match(/\d{4}/)?.[0] ?? 0);
        return (ya - yb) * sort.dir;
      }
      const va = a[k] ?? '', vb = b[k] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sort.dir;
      return String(va).localeCompare(String(vb)) * sort.dir;
    });
    return arr;
  }, [rows, sort, pinned]);

  const toggleSort = (key: SortKey) => setSort((s) => (s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: key === 'name' ? 1 : -1 }));
  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : '');
  const th = (label: string, key: SortKey, right = true) => (
    <th onClick={() => toggleSort(key)} className={`text-muted font-bold text-[10.5px] uppercase px-2 py-1.5 border-b border-line cursor-pointer select-none hover:text-navy ${right ? 'text-right' : 'text-left'}`}>{label}{arrow(key)}</th>
  );

  return (
    <>
      <Card title="Listed peers" sub="Real fundamentals from screener.in + IPO details from Chittorgarh, folded into one fetch (cached 24h).">
        <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
          {names.map((n) => (
            <span key={n} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-navy-50 text-navy text-[11.5px] font-semibold">
              {n}<button onClick={() => remove(n)} className="text-muted-2 hover:text-red">×</button>
            </span>
          ))}
          {names.length === 0 && <span className="text-[11.5px] text-muted-2">No peers yet — add listed comparables (e.g. “Exide Industries”) or run “Enrich from web” in Edit.</span>}
        </div>
        <div className="flex gap-2">
          <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder="Add a listed peer name…" className="flex-1 px-3 py-2 border border-line rounded-lg text-[13px] outline-none focus:border-teal focus:ring-2 focus:ring-teal/15" />
          <button onClick={add} disabled={!draft.trim()} className="px-3 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line disabled:opacity-50">Add</button>
          <button onClick={discover} disabled={busy} title="Find recently-listed comparables from the web (deck + sector), most-recent IPO first, with their IPO details — overwrites the current set." className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-navy text-white hover:bg-navy-2 disabled:opacity-50">{busyKind === 'discover' ? <InlineBusy>Discovering…</InlineBusy> : '✨ Discover recent peers'}</button>
          <button onClick={fetchData} disabled={busy || !names.length} title="Resolve the exact peer names listed above (curated set)." className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">{busyKind === 'peers' ? <InlineBusy>Fetching…</InlineBusy> : '↻ Refresh listed'}</button>
          <button onClick={fetchIpo} disabled={busy || !d.peerRows.length} title="IPO year / lead managers / P-E-at-listing from Chittorgarh (recent IPOs only)" className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line disabled:opacity-50">{busyKind === 'ipo' ? <InlineBusy>IPO details…</InlineBusy> : '↻ IPO details'}</button>
        </div>
        {busyKind === 'discover' && <StagedLoader stages={['Finding recent comparables', 'Resolving on screener.in', 'Fetching IPO details (Chittorgarh)', 'Done']} stageMs={7000} className="mt-3" />}
        {busyKind === 'peers' && <StagedLoader stages={PEER_STAGES} stageMs={5000} className="mt-3" />}
        {busyKind === 'ipo' && <StagedLoader stages={IPO_STAGES} stageMs={6000} className="mt-3" />}
        {err && !busy && <StagedLoader stages={PEER_STAGES} error={err} onRetry={fetchData} className="mt-3" />}
        {msg && !busy && !err && <p className="text-[11.5px] text-muted mt-2">{msg}</p>}
      </Card>

      {rows.length > 0 && (
        <Card title="What they do" sub="Quick read for relevance — this deal's business vs. the peer set.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.4px] text-muted mb-1">{d.company} (this deal)</div>
              <p className="text-[12.5px] leading-relaxed text-navy">
                {oneLine(d.businessDetail?.whatTheyMake?.content || d.businessDetail?.summary?.content || d.business, 220)
                  || 'No business description yet — see the Business tab.'}
              </p>
            </div>
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.4px] text-muted mb-1">Peers, in brief</div>
              <ul className="space-y-1">
                {rows.map((p) => (
                  <li key={p.name} className="text-[12.5px] leading-snug">
                    <span className="font-semibold text-navy">{p.name}</span>
                    <span className="text-muted"> — {oneLine(p.about) || 'no description on screener'}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {rows.length > 0 && (
        <Card title="Peer-set quality" sub="Median peer P/E feeds the deal’s peNow → margin-of-safety calc.">
          <SectionInsight dealId={d.id} section="valuation" label="Valuation read" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            <div className="bg-canvas border border-line-2 rounded-lg px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-[0.4px] text-muted">Peers</div><div className="text-[20px] font-extrabold text-navy mono leading-tight">{rows.length}</div></div>
            <div className="bg-canvas border border-line-2 rounded-lg px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-[0.4px] text-muted">Median P/E</div><div className="text-[20px] font-extrabold text-navy mono leading-tight">{medPe ? xx(medPe) : '—'}</div><div className="text-[10px] text-muted-2">deal entry {v.ok ? xx(v.entryPE) : '—'}</div></div>
            <div className="bg-canvas border border-line-2 rounded-lg px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-[0.4px] text-muted">Median margin</div><div className="text-[20px] font-extrabold text-navy mono leading-tight">{medMargin != null ? pct(medMargin) : '—'}</div></div>
            <div className="bg-canvas border border-line-2 rounded-lg px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-[0.4px] text-muted">Revenue range</div><div className="text-[14px] font-extrabold text-navy mono leading-tight mt-1">{revLo != null ? `${inr(revLo)} – ${inr(revHi)}` : '—'}</div></div>
          </div>
        </Card>
      )}

      <Card title="Listed-peer comparison" sub={rows.length ? 'Ordered by most recent listing · click a peer to expand its drill-down · click a column to sort · pin to keep on top' : undefined}>
        {busyKind === 'peers' && !rows.length ? (
          <div className="overflow-x-auto"><TableSkeleton head={['', 'Name', 'Revenue', 'Mkt cap', 'CMP', 'IPO yr', 'P/E @list', 'Merchant banker(s)', 'P/E now', 'Margin', '']} rows={Math.max(3, names.length)} /></div>
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead><tr>
                <th className="w-6 border-b border-line" />
                {th('Name', 'name', false)}{th('Revenue', 'rev')}{th('Mkt cap', 'mcap')}{th('CMP', 'price')}{th('IPO yr', 'ipoYear')}
                <th className="text-muted font-bold text-[10.5px] uppercase px-2 py-1.5 border-b border-line text-right">P/E @list</th>
                <th className="text-muted font-bold text-[10.5px] uppercase px-2 py-1.5 border-b border-line text-left">Merchant banker(s)</th>
                {th('P/E now', 'pe')}{th('Margin', 'margin')}
                <th className="w-8 border-b border-line" />
              </tr></thead>
              <tbody>
                {sorted.map((p) => {
                  const open = expanded === p.name;
                  return (
                    <Fragment key={p.name}>
                      <tr className="hover:bg-canvas/60 cursor-pointer" onClick={() => setExpanded(open ? null : p.name)}>
                        <td className="px-2 py-1.5 border-b border-line-2 text-muted-2 text-[10px]">{open ? '▾' : '▸'}</td>
                        <td className="px-2 py-1.5 border-b border-line-2 align-top max-w-[320px]">
                          {p.ticker ? <a href={`${SCREENER}${p.ticker}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="font-semibold text-teal-d hover:underline">{p.name}</a> : <span className="font-semibold text-navy">{p.name}</span>}
                          {/* What this peer actually does — screener's own blurb, condensed to one line (full text on hover / in the drill-down). */}
                          {p.about && <div title={p.about} className="text-[10.5px] text-muted leading-snug mt-0.5 whitespace-normal">{oneLine(p.about)}</div>}
                        </td>
                        <td className="px-2 py-1.5 border-b border-line-2 text-right mono">{p.rev ? inr(p.rev) : 'n/a'}</td>
                        <td className="px-2 py-1.5 border-b border-line-2 text-right mono">{p.mcap ? inr(p.mcap) : 'n/a'}</td>
                        <td className="px-2 py-1.5 border-b border-line-2 text-right mono">{p.price ? `₹${p.price.toLocaleString('en-IN')}` : 'n/a'}</td>
                        <td className="px-2 py-1.5 border-b border-line-2 text-right mono">
                          {p.ipoYear ? (
                            <span className="inline-flex items-center gap-1 justify-end">
                              {p.ipoYear}
                              {p.ipoHist?.ipoBoard.value === 'sme' && <span title="SME IPO (NSE Emerge / BSE SME)" className="text-[9px] font-bold uppercase tracking-wide text-amber not-italic">SME</span>}
                            </span>
                          ) : (
                            <span className="text-muted-2" title={p.ipoHist?.label ?? (p.ipoChecked ? 'Listed pre-2007 — no Chittorgarh page' : 'Not fetched yet')}>
                              {p.ipoHist?.label ? (p.ipoHist.label.startsWith('Listed pre') ? 'pre-’07' : 'non-IPO') : p.ipoChecked ? 'pre-’07' : '—'}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 border-b border-line-2 text-right mono">{p.peList || '—'}</td>
                        <td className="px-2 py-1.5 border-b border-line-2 align-top max-w-[220px]">
                          {(() => { const b = (p.brlm && p.brlm.trim()) || (p.ipoHist?.brlm?.value ?? []).join(', '); return b ? <span title={b} className="text-[11px] text-navy whitespace-normal leading-snug">{b}</span> : <span className="text-muted-2">—</span>; })()}
                        </td>
                        <td className="px-2 py-1.5 border-b border-line-2 text-right mono">{xx(p.pe)}</td>
                        <td className="px-2 py-1.5 border-b border-line-2 text-right mono">{pct(p.margin)}</td>
                        <td className="px-2 py-1.5 border-b border-line-2 text-right">
                          <button onClick={(e) => { e.stopPropagation(); setPinned((s) => { const n = new Set(s); n.has(p.name) ? n.delete(p.name) : n.add(p.name); return n; }); }} title={pinned.has(p.name) ? 'Unpin' : 'Pin to top'} className={`text-[12px] ${pinned.has(p.name) ? 'text-teal-d' : 'text-muted-2 hover:text-navy'}`}>📌</button>
                        </td>
                      </tr>
                      {open && <tr><td colSpan={11} className="p-0"><PeerDrilldown peer={p} d={d} medianPe={medPe} /></td></tr>}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <Empty label="No peer data yet — add names above and fetch." />}
        {rows.length > 0 && (
          <p className="text-[11px] text-muted-2 mt-2">
            Name links to screener.in; the line under it is that company&rsquo;s own screener business description (hover or expand for the full text
            {rows.some((p) => !p.about) ? ' — re-fetch peer data to fill it for older rows' : ''}). Revenue · Mkt cap · CMP · P/E now · Margin are live from screener; IPO yr · P/E @list · Merchant banker(s) from Chittorgarh (recent IPOs only). Never includes unlisted/invented companies.
          </p>
        )}
      </Card>

      {rows.length > 0 && (
        <Card title="Peer business detail" sub="Full descriptions, so you can judge whether each name is actually a relevant comparable — not just a similar P/E.">
          <div className="space-y-3">
            {rows.map((p) => (
              <div key={p.name} className="border-b border-line-2 pb-2.5 last:border-0 last:pb-0">
                <div className="font-semibold text-[12.5px]">
                  {p.ticker ? <a href={`${SCREENER}${p.ticker}`} target="_blank" rel="noopener noreferrer" className="text-teal-d hover:underline">{p.name}</a> : <span className="text-navy">{p.name}</span>}
                </div>
                <p className="text-[12px] text-muted leading-relaxed mt-0.5">{p.about || 'No business description on screener for this peer.'}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
      {d.industry && <Card title="Industry context"><p className="text-[12.5px] leading-relaxed">{d.industry}</p></Card>}

      {benchmark && (
        <Card title="Industry benchmark" sub={`${benchmark.bizIndustry ?? ''}${benchmark.bizSegment ? ` — ${benchmark.bizSegment}` : ''} · FY${String(benchmark.refYear ?? '').slice(-2)} · via Registry Intelligence (registered companies by revenue, not stock-market peers)`}>
          {Array.isArray(benchmark.peers) && benchmark.peers.length > 0 && (
            <div className="mb-3">
              <div className="text-[11px] font-semibold text-muted uppercase tracking-wide mb-1.5">Nearest by revenue</div>
              <P42Table rows={benchmark.peers} />
            </div>
          )}
          {Array.isArray(benchmark.benchMarks) && benchmark.benchMarks.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold text-muted uppercase tracking-wide mb-1.5">Sector benchmark ({benchmark.benchMarks[0]?.no_of_peers_in_sample ?? '—'} peers in sample)</div>
              <P42Table rows={benchmark.benchMarks} />
            </div>
          )}
        </Card>
      )}
    </>
  );
}

function Diligence({ d, onPatch }: { d: DealDto; onPatch: (p: Partial<Deal>) => void }) {
  const st = diliStats(d);
  return (
    <Card title="Diligence tracker" sub={`${st.flag} flag · ${st.open} open · ${st.cleared} cleared`}>
      <EditableRows
        cols={[
          { k: 'item', label: 'Item', w: 'flex-[3]' },
          { k: 'cat', label: 'Category' },
          { k: 'owner', label: 'Owner' },
          { k: 'due', label: 'Due' },
          { k: 'status', label: 'Status', options: ['flag', 'open', 'cleared'] },
        ]}
        rows={d.diligence as unknown as Record<string, string>[]}
        blank={{ item: '', cat: 'Financial', owner: '', due: '', status: 'open' }}
        onSave={(rows) => onPatch({ diligence: rows as unknown as Deal['diligence'] })}
      />
    </Card>
  );
}

const CALL_UPLOAD_STAGES = ['Reading the recording', 'Uploading & storing', 'Queued for transcription', 'Done'];
const CALL_ANALYSE_STAGES = ['Reading transcript', 'Analysing (LLM)', 'Done'];

/** A synced competitor/external earnings call is titled "Competitor · <company>" (screenerSync.ts) —
 *  the one signal used everywhere in the UI to split internal (the deal's own) from external calls. */
const isExternalCall = (c: { title?: string }) => !!c.title?.startsWith('Competitor ·');

function InternalCalls({ d, dealId, onChanged }: { d: DealDto; dealId: string; onChanged: () => void }) {
  const [form, setForm] = useState<CallInput>({ title: '', date: '', participants: '', duration: '', transcript: '', recordingUrl: '' });
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<'' | 'submit' | 'upload'>('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const anyBusy = busy || kind !== '';
  const set = (k: keyof CallInput, v: string) => setForm((p) => ({ ...p, [k]: v }));

  // Auto-poll any call still transcribing until it finishes (server transcribes → analyses).
  const transcribing = d.calls.filter((c) => c.status === 'transcribing' && c.id).map((c) => c.id!);
  const pollKey = transcribing.join(',');
  useEffect(() => {
    if (!pollKey) return;
    let alive = true;
    const tick = async () => {
      for (const callId of pollKey.split(',')) {
        try {
          const r = await getCallStatus(dealId, callId);
          if (alive && r.state !== 'transcribing') onChanged();
        } catch { /* keep polling */ }
      }
    };
    const timer = setInterval(tick, 6000);
    return () => { alive = false; clearInterval(timer); };
  }, [pollKey, dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    if (!form.transcript?.trim()) return;
    // Title is optional now — default it so a pasted transcript is never blocked by an empty title.
    const title = form.title.trim() || `Call — ${form.date?.trim() || new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    setKind('submit'); setErr(''); setMsg('');
    try {
      await addCall(dealId, { ...form, title });
      setMsg('Saved — analysing the transcript in the background. It appears in the list below in a few seconds.');
      setForm({ title: '', date: '', participants: '', duration: '', transcript: '', recordingUrl: '' });
      onChanged();
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setKind(''); }
  }
  async function upload(file: File) {
    setKind('upload'); setErr(''); setMsg('');
    try {
      let toSend = file;
      // A screen recording (video) — or any big file — can't stream through the API's request limit.
      // Extract just the audio in the browser (16 kHz mono WAV) and upload that instead.
      const isVideo = file.type.startsWith('video/') || /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(file.name);
      if (isVideo || file.size > 60 * 1024 * 1024) {
        if (file.size > 800 * 1024 * 1024) throw new Error('That file is very large (>800 MB). Please export/trim an audio-only version and upload that, or paste the transcript.');
        setMsg('Extracting the audio from the recording in your browser (keeps the upload small)… this can take up to a minute for a long call.');
        try { toSend = await extractAudioToWav(file); }
        catch { throw new Error('Could not read the audio from this recording. Export it as MP3 / M4A and upload that, or paste the transcript.'); }
        if (toSend.size > 95 * 1024 * 1024) throw new Error('This recording is too long to upload in one piece (audio still >95 MB). Split it, or upload a compressed audio file.');
      }
      setMsg('Uploading & queuing for transcription…');
      await uploadCallRecording(dealId, toSend, { title: form.title.trim() || file.name, date: form.date, participants: form.participants, duration: form.duration });
      setMsg('Transcribing — this runs in the background and can take a few minutes for a long call. This list updates automatically when it is ready.');
      setForm({ title: '', date: '', participants: '', duration: '', transcript: '', recordingUrl: '' });
      onChanged();
    } catch (e) { setErr(`Upload failed: ${String((e as Error).message)}`); }
    finally { setKind(''); if (fileRef.current) fileRef.current.value = ''; }
  }
  async function reanalyse(callId: string) { setBusy(true); try { await analyseCallApi(dealId, callId); onChanged(); } finally { setBusy(false); } }
  async function remove(callId: string) { setBusy(true); try { await deleteCall(dealId, callId); onChanged(); } finally { setBusy(false); } }
  const internal = d.calls.filter((c) => !isExternalCall(c));
  const [trendMsg, setTrendMsg] = useState('');
  const trend = d.internalCallsTrend;
  const genTrend = useMutation({
    mutationFn: () => synthesizeInternalCallsTrend(dealId),
    onSuccess: () => { setTrendMsg(''); onChanged(); },
    onError: (e) => setTrendMsg(String((e as Error).message)),
  });

  return (
    <>
      <Card title="Add / analyse a promoter call" sub="Upload a recording to auto-transcribe, or paste a transcript. Either way you get a structured analysis (summary, commitments, red flags, follow-ups, sentiment).">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5 mb-2.5">
          <input className="px-2.5 py-1.5 border border-line rounded-lg text-[13px] md:col-span-2" placeholder="Title (optional)" value={form.title} onChange={(e) => set('title', e.target.value)} />
          <input className="px-2.5 py-1.5 border border-line rounded-lg text-[13px]" placeholder="Date" value={form.date} onChange={(e) => set('date', e.target.value)} />
          <input className="px-2.5 py-1.5 border border-line rounded-lg text-[13px]" placeholder="Duration" value={form.duration} onChange={(e) => set('duration', e.target.value)} />
          <input className="px-2.5 py-1.5 border border-line rounded-lg text-[13px] md:col-span-4" placeholder="Participants" value={form.participants} onChange={(e) => set('participants', e.target.value)} />
          <input className="px-2.5 py-1.5 border border-line rounded-lg text-[13px] md:col-span-4" placeholder="Recording link (URL to the audio/video — to listen back for tone)" value={form.recordingUrl ?? ''} onChange={(e) => set('recordingUrl', e.target.value)} />
        </div>

        {/* Audio upload → auto-transcribe */}
        <div className="flex flex-wrap items-center gap-2.5 mb-2.5 p-2.5 rounded-lg bg-canvas border border-line-2">
          <input ref={fileRef} type="file" accept="audio/*,video/*,.mp3,.m4a,.wav,.ogg,.mp4" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} />
          <button disabled={anyBusy} onClick={() => fileRef.current?.click()} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-navy text-white disabled:opacity-50">{kind === 'upload' ? <InlineBusy>Uploading…</InlineBusy> : '🎙️ Upload recording'}</button>
          <span className="text-[11.5px] text-muted-2">mp3 · m4a · wav — transcribed automatically, then analysed. No need to paste anything.</span>
        </div>
        {kind === 'upload' && <StagedLoader stages={CALL_UPLOAD_STAGES} stageMs={4000} note="Uploading now — transcription then continues in the background and this list refreshes on its own." className="mb-2.5" />}

        <div className="text-[11px] text-muted-2 mb-1.5">— or paste a transcript —</div>
        <textarea className="w-full px-2.5 py-2 border border-line rounded-lg text-[13px] min-h-[120px]" placeholder="Paste transcript…" value={form.transcript} onChange={(e) => set('transcript', e.target.value)} />
        <div className="flex items-center gap-3 mt-2.5">
          <button disabled={anyBusy || !form.transcript?.trim()} onClick={submit} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white disabled:opacity-50">{kind === 'submit' ? <InlineBusy>Analysing…</InlineBusy> : 'Add & analyse transcript'}</button>
          {msg && !anyBusy && !err && <span className="text-[11.5px] text-muted">{msg}</span>}
        </div>
        {kind === 'submit' && <StagedLoader stages={CALL_ANALYSE_STAGES} stageMs={6000} className="mt-2.5" />}
        {err && !anyBusy && <StagedLoader stages={CALL_ANALYSE_STAGES} error={err} className="mt-2.5" />}
      </Card>

      {internal.length >= 2 && (
        <Card title="Promoter calls — progress trend"
          sub="How this company itself has evolved across its own calls — sentiment, commitments followed through, recurring concerns."
          actions={<button disabled={genTrend.isPending} onClick={() => genTrend.mutate()} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">{genTrend.isPending ? <InlineBusy>Synthesising…</InlineBusy> : trend ? '↻ Refresh' : '✨ Summarize progress'}</button>}>
          {genTrend.isPending && !trend ? (
            <StagedLoader stages={['Reading the calls', 'Tracking commitments', 'Writing the read', 'Done']} stageMs={5000} />
          ) : trend ? (
            <div>
              <p className="text-[12.5px] text-navy leading-relaxed mb-3">{trend.summary}</p>
              {trend.themes.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2.5">
                  {trend.themes.map((t, i) => (
                    <div key={i}>
                      <div className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-teal-d mb-0.5">{t.label}</div>
                      <p className="text-[12px] text-muted leading-relaxed">{t.detail}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[10.5px] text-muted-2 mt-3">Across {trend.sources.length} call{trend.sources.length === 1 ? '' : 's'} · {new Date(trend.generatedAt).toLocaleDateString()}</div>
            </div>
          ) : (
            <p className="text-[12px] text-muted">Click "Summarize progress" to track how this company's own calls have evolved.{trendMsg && <span className="text-red"> · {trendMsg}</span>}</p>
          )}
        </Card>
      )}

      {internal.length === 0 && <Card title="Promoter calls"><Empty label="No calls yet." /></Card>}
      {internal.map((c, i) => (
        <Card key={i} title={c.title} sub={[c.date ? `📅 ${c.date}` : null, c.duration, c.participants].filter(Boolean).join(' · ') || 'Date not recorded'}
          actions={<div className="flex items-center gap-2">
            <Pill tone={c.status === 'analysed' ? 'green' : c.status === 'transcribing' ? 'teal' : 'amber'}>{c.status === 'transcribing' ? 'transcribing…' : c.status}</Pill>
            {c.recordingUrl && <a href={c.recordingUrl} target="_blank" rel="noreferrer" className="text-[11px] font-semibold text-teal-d hover:underline" title="Listen to the recording (for tone the transcript can't capture)">▶ Listen</a>}
            {c.transcript && <button disabled={anyBusy} onClick={() => reanalyse(c.id ?? '')} className="text-[11px] font-semibold text-teal-d">re-analyse</button>}
            <button disabled={anyBusy} onClick={() => remove(c.id ?? '')} className="text-[11px] font-semibold text-red">delete</button>
          </div>}>
          {c.status === 'transcribing' ? (
            <div role="status" aria-busy="true" className="flex items-start gap-2.5 py-3 px-1">
              <Spinner size={15} className="text-teal-d mt-0.5" />
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold text-navy">Transcribing the recording…</div>
                <div className="text-[11.5px] text-muted-2 mt-0.5">Runs in the background — this card updates automatically when it is ready (can take a few minutes for a long call).</div>
                <Skeleton lines={3} h={10} className="mt-2 max-w-[420px]" />
              </div>
            </div>
          ) : c.analysis ? (
            <>
              <div className="flex items-center gap-2 mb-2"><Pill tone={/pos/i.test(c.analysis.sentiment) ? 'green' : /neg|caut/i.test(c.analysis.sentiment) ? 'amber' : 'gray'}>{c.analysis.sentiment}</Pill></div>
              <p className="text-[12.5px] mb-2">{c.analysis.summary}</p>
              {(['keyPoints', 'commitments', 'redFlags', 'followUps'] as const).map((k) => c.analysis![k].length > 0 && (
                <div key={k}><H4>{k.replace(/([A-Z])/g, ' $1').replace(/^./, (m) => m.toUpperCase())}</H4><ul className="text-[12.5px] list-disc pl-4 space-y-0.5">{c.analysis![k].map((x, j) => <li key={j}>{x}</li>)}</ul></div>
              ))}
            </>
          ) : <Empty label="Uploaded — no analysis yet." />}
        </Card>
      ))}
    </>
  );
}

function ExternalCalls({ d, dealId, onChanged }: { d: DealDto; dealId: string; onChanged: () => void }) {
  const external = d.calls.filter((c) => isExternalCall(c));
  const withT = external.filter((c) => (c.transcript ?? '').trim().length > 0);
  const allWithT = d.calls.filter((c) => (c.transcript ?? '').trim().length > 0); // the industry trend deliberately reads BOTH internal + external calls — a comparative read, not an external-only one
  const [msg, setMsg] = useState('');
  const trend = d.callsTrend;
  const gen = useMutation({
    mutationFn: () => synthesizeCallsTrend(dealId),
    onSuccess: () => { setMsg(''); onChanged(); },
    onError: (e) => setMsg(String((e as Error).message)),
  });
  return (
    <>
      <Card title="Industry trend from the calls"
        sub="The underlying industry read — demand, pricing, margins, AI, outlook — synthesised across the deal's own and its competitors' earnings calls."
        actions={<button disabled={gen.isPending || allWithT.length < 2} onClick={() => gen.mutate()} title={allWithT.length < 2 ? 'Need at least two calls to read a trend' : ''}
          className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">{gen.isPending ? <InlineBusy>Synthesising…</InlineBusy> : trend ? '↻ Refresh' : '✨ Summarize trend'}</button>}>
        {gen.isPending && !trend ? (
          <StagedLoader stages={['Reading the calls', 'Finding the through-line', 'Writing the industry read', 'Done']} stageMs={5000} />
        ) : trend ? (
          <div>
            <p className="text-[12.5px] text-navy leading-relaxed mb-3">{trend.summary}</p>
            {trend.themes.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2.5">
                {trend.themes.map((t, i) => (
                  <div key={i}>
                    <div className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-teal-d mb-0.5">{t.label}</div>
                    <p className="text-[12px] text-muted leading-relaxed">{t.detail}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10.5px] text-muted-2 mt-3">Across {trend.sources.length} call{trend.sources.length === 1 ? '' : 's'} · {new Date(trend.generatedAt).toLocaleDateString()}</div>
          </div>
        ) : (
          <p className="text-[12px] text-muted">{allWithT.length < 2 ? 'Add or sync at least two calls (e.g. the company + its competitors) to read the industry trend.' : 'Click “Summarize trend” to distil the underlying industry trend across these calls.'}{msg && <span className="text-red"> · {msg}</span>}</p>
        )}
      </Card>

      <Card title="Competitor call transcripts" sub="Full earnings-call transcripts for the deal's LISTED PEERS — synced from Screener, for competitive comparison.">
        <p className="text-[12px] text-muted">{withT.length ? `${withT.length} transcript${withT.length === 1 ? '' : 's'} on file.` : 'None yet — run the Screener sync (Peers & Industry tab) to pull competitor earnings-call transcripts.'}</p>
      </Card>
      {withT.map((c, i) => {
        const t = c.transcript ?? '';
        return (
          <Card key={i} title={c.title}
            sub={[c.date ? `📅 ${c.date}` : 'Date not recorded', `${t.length.toLocaleString()} chars`].join(' · ')}
            actions={<span className="text-[10px] font-bold uppercase tracking-[0.5px] text-amber bg-amber/10 border border-amber/20 px-1.5 py-0.5 rounded" title="A competitor's earnings call">Competitor</span>}>
            <div className="max-h-[440px] overflow-auto rounded-lg border border-line-2 bg-canvas p-3.5 text-[12px] leading-[1.7] text-navy whitespace-pre-wrap">{t}</div>
          </Card>
        );
      })}
    </>
  );
}

function ScoreTab({ d, weights, icThreshold, onSave }: { d: DealDto; weights: Record<ModuleKey, number>; icThreshold: number; onSave: (r: Ratings) => void }) {
  const qc = useQueryClient();
  const [ratings, setRatings] = useState<Ratings>(d.ratings);
  const [scoring, setScoring] = useState(false);
  const [scoreMsg, setScoreMsg] = useState('');
  const [aiRatings, setAiRatings] = useState<ModuleRatingsAI | null>(d.moduleRatingsAI ?? null);
  const [autoRating, setAutoRating] = useState(false);
  const [autoRateMsg, setAutoRateMsg] = useState('');
  const liveScore = score({ ratings } as Deal, weights);
  const gr = grade(liveScore);
  const dirty = MODULES.some((m) => ratings[m.k] !== d.ratings[m.k]);
  const r = rec({ ...d, ratings }, weights, icThreshold);
  const ls = d.llmScore;

  async function genScore() {
    setScoring(true); setScoreMsg('');
    try {
      const res = await scoreFetch(d.id, true);
      setScoreMsg(res.skipped ? 'Not enough data to score yet — populate the deal first.' : 'AI read regenerated.');
      qc.invalidateQueries({ queryKey: ['deal', d.id] });
    } catch (e) { setScoreMsg(String((e as Error).message)); }
    finally { setScoring(false); }
  }

  // Suggests all 7 module ratings + a reason each + a team-facing note, grounded in the same
  // enriched data the "AI read" above reads — deliberately FILLS the sliders (still needs Save)
  // rather than writing deals.ratings directly, so a human always reviews before it counts.
  async function autoRate() {
    setAutoRating(true); setAutoRateMsg('');
    try {
      const res = await moduleRatingsFetch(d.id, true);
      setRatings(res.moduleRatingsAI.ratings);
      setAiRatings(res.moduleRatingsAI);
      setAutoRateMsg('Suggested ratings filled in below — review, adjust if needed, then Save.');
      qc.invalidateQueries({ queryKey: ['deal', d.id] });
    } catch (e) { setAutoRateMsg(String((e as Error).message)); }
    finally { setAutoRating(false); }
  }

  return (
    <>
    <Card title="AI read" sub="Krit Score, recommendation and the evidence behind them — one score, one call."
      actions={<button disabled={scoring} onClick={genScore} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">{scoring ? <InlineBusy>Scoring…</InlineBusy> : (ls ? '↻ Re-score' : '✨ Generate AI read')}</button>}>
      {/* Krit Score + Recommendation — the number and the call, up top and unmissable. Computed live
          from the sliders below (including unsaved edits), so this always matches what's on screen. */}
      <div className="flex flex-wrap items-center gap-4 mb-4 pb-4 border-b border-line-2">
        <div className="flex items-baseline gap-3">
          <div className="text-[52px] font-extrabold text-navy leading-none">{liveScore}</div>
          <Pill tone={gr.c}>Grade {gr.g}</Pill>
        </div>
        <div className={`flex-1 min-w-[240px] rounded-xl px-3.5 py-3 text-[13px] font-bold ${r.c === 'pass' ? 'bg-green-50 text-green' : r.c === 'cond' ? 'bg-amber-50 text-amber' : 'bg-red-50 text-red'}`}>{r.h}</div>
        <div className="text-[11px] text-muted-2 whitespace-nowrap">IC threshold {icThreshold}</div>
      </div>
      {dirty && <p className="text-[11px] text-amber mb-3">Unsaved rating changes below — Save to make this the deal's official score.</p>}

      {scoring && !ls ? (
        <StagedLoader stages={['Reading the full deal', 'Weighing the evidence', 'Writing the read', 'Done']} stageMs={6000} />
      ) : ls ? (
        <div>
          {ls.fetchedAt && <div className="text-[10.5px] text-muted-2 mb-2">Last read {new Date(ls.fetchedAt).toLocaleDateString()}</div>}
          {ls.rationale && <p className="text-[12.5px] text-navy leading-relaxed mb-3">{ls.rationale}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-green mb-1">Pros</div>
              <ul className="list-disc pl-4 text-[12px] text-muted space-y-0.5">{ls.pros.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </div>
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.5px] text-red mb-1">Cons / gaps</div>
              <ul className="list-disc pl-4 text-[12px] text-muted space-y-0.5">{ls.cons.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-[12px] text-muted">No AI read yet — it generates on the last auto-populate step, or click "Generate AI read". {scoreMsg && <span className="text-muted-2"> · {scoreMsg}</span>}</div>
      )}
      {ls && scoreMsg && !scoring && <div className="text-[11px] text-muted-2 mt-2">{scoreMsg}</div>}
    </Card>
    {aiRatings?.note && (
      <Card title="Krit note — AI read for the team" sub={`Synthesises the module ratings below into one plain-English read${aiRatings.fetchedAt ? ` · ${new Date(aiRatings.fetchedAt).toLocaleDateString()}` : ''}`}>
        <p className="text-[12.5px] text-navy leading-relaxed">{aiRatings.note}</p>
      </Card>
    )}
    {/* Module ratings — full width so every module + its AI-written reason has room to breathe,
        rather than being squeezed into a narrow column next to the score. */}
    <Card title="Module ratings" sub="0–100 per module; weighted by the fund's calibration — adjust any slider and Save to change the score above"
      actions={<div className="flex items-center gap-2">
        <button disabled={autoRating} onClick={autoRate} title="Suggest all 7 ratings from the deal's own data, each with a reason — you still review and Save" className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">{autoRating ? <InlineBusy>Rating…</InlineBusy> : (aiRatings ? '↻ Re-rate' : '✨ Auto-rate')}</button>
        {dirty && <button onClick={() => onSave(ratings)} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-teal text-white">Save</button>}
      </div>}>
      {autoRating && !aiRatings ? (
        <StagedLoader stages={['Reading the full deal', 'Rating each module', 'Writing the reasons', 'Done']} stageMs={5000} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          {MODULES.map((m) => (
            <div key={m.k} className="mb-4">
              <div className="flex justify-between text-[12.5px] mb-1"><span className="font-semibold">{m.n} <span className="text-muted-2">· w{weights[m.k] ?? m.w}</span></span><span className="mono font-bold" >{ratings[m.k]}</span></div>
              <input type="range" min={0} max={100} value={ratings[m.k]} onChange={(e) => setRatings((p) => ({ ...p, [m.k]: Number(e.target.value) }))} className="w-full accent-teal" />
              {aiRatings?.reasons[m.k] && <p className="text-[11px] text-muted mt-1 leading-snug">✨ {aiRatings.reasons[m.k]}</p>}
            </div>
          ))}
        </div>
      )}
      {autoRateMsg && !autoRating && <p className="text-[11px] text-muted-2 mt-1">{autoRateMsg}</p>}
      {dirty && <p className="text-[11px] text-amber mt-1">Unsaved changes — click Save.</p>}
    </Card>
    </>
  );
}

function Analyst({ d, onPatch }: { d: DealDto; onPatch: (p: Partial<Deal>) => void }) {
  const a = d.analyst;
  return (
    <>
      <Card title="Thesis" sub="The investment thesis — written by the analyst"><EditableText value={a.thesis} onSave={(v) => onPatch({ analyst: { ...a, thesis: v } })} placeholder="Write the investment thesis for this deal…" /></Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Highlights"><EditableList items={a.highlights} onSave={(v) => onPatch({ analyst: { ...a, highlights: v } })} /></Card>
        <Card title="Concerns"><EditableList items={a.concerns} onSave={(v) => onPatch({ analyst: { ...a, concerns: v } })} /></Card>
      </div>
      <Card title="Diligence questions"><EditableList items={a.questions} onSave={(v) => onPatch({ analyst: { ...a, questions: v } })} /></Card>
    </>
  );
}

/* ---------- editors ---------- */
function EditableList({ items, onSave }: { items: string[]; onSave: (v: string[]) => void }) {
  const [list, setList] = useState(items);
  const [add, setAdd] = useState('');
  const dirty = JSON.stringify(list) !== JSON.stringify(items);
  const commitAdd = () => { if (add.trim()) { setList([...list, add.trim()]); setAdd(''); } };
  return (
    <div>
      {list.map((x, i) => (
        <div key={i} className="flex gap-2 items-center mb-1">
          <input value={x} onChange={(e) => setList(list.map((v, j) => (j === i ? e.target.value : v)))} className="flex-1 px-2 py-1 border border-line rounded-md text-[12.5px]" />
          <button onClick={() => setList(list.filter((_, j) => j !== i))} className="text-red text-[16px] leading-none px-1">×</button>
        </div>
      ))}
      <div className="flex gap-2 mt-1.5">
        <input value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && commitAdd()} placeholder="Add item…" className="flex-1 px-2 py-1 border border-line rounded-md text-[12.5px]" />
        <button onClick={commitAdd} className="px-2.5 py-1 rounded-md text-[12px] font-semibold bg-white text-navy border border-line">Add</button>
        {dirty && <button onClick={() => onSave(list)} className="px-2.5 py-1 rounded-md text-[12px] font-semibold bg-teal text-white">Save</button>}
      </div>
    </div>
  );
}

/** Inline single-text editor (paragraph fields like the analyst thesis). Save appears when dirty. */
function EditableText({ value, onSave, placeholder }: { value: string; onSave: (v: string) => void; placeholder?: string }) {
  const [text, setText] = useState(value);
  const dirty = text !== value;
  return (
    <div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder}
        className="w-full px-2.5 py-2 border border-line rounded-lg text-[12.5px] leading-relaxed min-h-[110px] outline-none focus:border-teal focus:ring-2 focus:ring-teal/15" />
      {dirty && (
        <div className="mt-1.5 flex gap-2">
          <button onClick={() => onSave(text)} className="px-2.5 py-1 rounded-md text-[12px] font-semibold bg-teal text-white">Save</button>
          <button onClick={() => setText(value)} className="px-2.5 py-1 rounded-md text-[12px] font-semibold bg-white text-navy border border-line">Cancel</button>
        </div>
      )}
    </div>
  );
}

type Col = { k: string; label: string; w?: string; options?: string[] };
function EditableRows({ cols, rows, blank, onSave }: { cols: Col[]; rows: Record<string, string>[]; blank: Record<string, string>; onSave: (rows: Record<string, string>[]) => void }) {
  const [data, setData] = useState(rows);
  const dirty = JSON.stringify(data) !== JSON.stringify(rows);
  const upd = (i: number, k: string, v: string) => setData(data.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div>
      <div className="flex gap-2 px-1 mb-1">{cols.map((c) => <div key={c.k} className={`text-[10px] uppercase font-bold text-muted ${c.w ?? 'flex-1'}`}>{c.label}</div>)}<div className="w-6" /></div>
      {data.map((r, i) => (
        <div key={i} className="flex gap-2 items-center mb-1">
          {cols.map((c) => c.options ? (
            <select key={c.k} value={r[c.k] ?? ''} onChange={(e) => upd(i, c.k, e.target.value)} className={`${c.w ?? 'flex-1'} px-2 py-1 border border-line rounded-md text-[12px]`}>{c.options.map((o) => <option key={o}>{o}</option>)}</select>
          ) : (
            <input key={c.k} value={r[c.k] ?? ''} onChange={(e) => upd(i, c.k, e.target.value)} className={`${c.w ?? 'flex-1'} px-2 py-1 border border-line rounded-md text-[12px]`} />
          ))}
          <button onClick={() => setData(data.filter((_, j) => j !== i))} className="text-red text-[16px] leading-none w-6">×</button>
        </div>
      ))}
      <div className="flex gap-2 mt-1.5">
        <button onClick={() => setData([...data, { ...blank }])} className="px-2.5 py-1 rounded-md text-[12px] font-semibold bg-white text-navy border border-line">+ Row</button>
        {dirty && <button onClick={() => onSave(data)} className="px-2.5 py-1 rounded-md text-[12px] font-semibold bg-teal text-white">Save</button>}
      </div>
    </div>
  );
}

const DECK_STAGES = ['Reading PDF text', 'Extracting (3 passes)', 'Reconciling financials vs P&L', 'Filling fields', 'Done'];

// Data Room card — lists each file with a per-file "Re-extract" button. Sheets/Docs are read
// server-side; PDFs are downloaded here and run through pdfjs, falling back to OCR for scanned
// pages (same path as a deck upload). The extracted data folds into the deal (financials + blanks).
function DataRoomBlock({ d, dealId, onChanged }: { d: DealDto; dealId: string; onChanged: () => void }) {
  const dr = d.intel?.dataroom;
  const [status, setStatus] = useState<Record<string, string>>({});
  const [allBusy, setAllBusy] = useState('');
  // Extraction is deliberate — the analyst clicks "⚡ Extract all". It is NOT auto-run on open:
  // firing the whole room (read every file + LLM extract + peers) automatically on every open
  // stacked long requests onto the DB pooler and 503'd the app. The button kicks a background job.
  if (!dr) return null;
  const fileId = (f: DataRoomFile) => f.id || /\/d\/([A-Za-z0-9_-]+)/.exec(f.url)?.[1] || '';
  const set = (id: string, s: string) => setStatus((m) => ({ ...m, [id]: s }));

  // Gather each file's TEXT in the BROWSER — one bounded request per file (never hits the server
  // time limit) with live progress; OCR scanned PDFs client-side. Then ONE server call applies the
  // whole corpus (best financial accuracy), stores docs, and re-discovers peers.
  async function extractAll() {
    const files = dr!.files;
    const gathered: { fileId: string; name: string; text: string }[] = [];
    let ocr = 0, errs = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i]!;
      const id = fileId(f);
      setAllBusy(`reading ${i + 1}/${files.length} · ${f.name.slice(0, 22)}`);
      if (!id) continue;
      try {
        const r = await dataroomFileText(dealId, id);
        if (r.needsClient) {
          const resp = await fetch(dataroomFileRawUrl(dealId, id), { credentials: 'include' });
          if (resp.ok) {
            const file = new File([await resp.blob()], f.name, { type: 'application/pdf' });
            let text = (await extractDeckWithMeta(file)).text;
            if (isLikelyScanned(text)) { try { const o = await ocrDeckImages(await renderPdfToImages(file)); if (o.text.trim()) { text = o.text; ocr++; } } catch { /* thin */ } }
            if (text.trim()) gathered.push({ fileId: id, name: f.name, text });
          }
        } else if (r.text && r.text.trim()) { gathered.push({ fileId: id, name: r.name || f.name, text: r.text }); }
      } catch { errs++; }
    }
    setAllBusy(`storing docs…`);
    try {
      const res = await applyDataroomBulk(dealId, gathered);
      if (res.background) {
        // Docs are stored + files flagged; the financials/peers extraction runs server-side in the
        // background. Refetch now (docs + AI grounding are live) and again shortly as fields land.
        setAllBusy(`✓ ${res.storedDocs} docs stored${ocr ? `, ${ocr} OCR` : ''}${errs ? `, ${errs} failed` : ''} · extracting financials + peers…`);
        onChanged();
        window.setTimeout(onChanged, 20000);
        window.setTimeout(() => { onChanged(); setAllBusy(''); }, 45000);
        return;
      }
      setAllBusy(`✓ ${res.filled?.length ?? 0} fields, ${res.storedDocs} docs${res.peers ? `, ${res.peers} peers` : ''}${ocr ? `, ${ocr} OCR` : ''}${errs ? `, ${errs} failed` : ''}`);
    } catch (e) { setAllBusy(`✗ ${String((e as Error).message).slice(0, 40)}`); }
    onChanged();
  }

  async function reextract(f: DataRoomFile) {
    const id = fileId(f);
    if (!id) { return; }
    set(id, 'Reading…');
    try {
      const r = await reextractDataroomFile(dealId, id);
      let filled = r.filled ?? [];
      if (r.needsClient) {
        set(id, 'Downloading…');
        const resp = await fetch(dataroomFileRawUrl(dealId, id), { credentials: 'include' });
        if (!resp.ok) throw new Error(`download ${resp.status}`);
        const file = new File([await resp.blob()], f.name, { type: 'application/pdf' });
        let text = (await extractDeckWithMeta(file)).text;
        if (isLikelyScanned(text)) {
          set(id, 'OCR…');
          try { const o = await ocrDeckImages(await renderPdfToImages(file)); if (o.text.trim()) text = o.text; } catch { /* server will report thin text */ }
        }
        set(id, 'Applying…');
        filled = (await applyDataroomText(dealId, id, text)).filled;
      }
      const parts = [filled.length ? `filled ${filled.length}` : '', r.added ? `unzipped ${r.added}` : ''].filter(Boolean);
      set(id, parts.length ? `✓ ${parts.join(', ')}` : (r.note || '✓ read'));
      onChanged();
    } catch (e) {
      set(id, `✗ ${String((e as Error).message).slice(0, 36)}`);
    }
  }

  return (
    <div className="mb-3 p-3 rounded-lg border border-teal/30 bg-teal/5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-[12.5px] font-semibold text-navy">🗂 Data Room{dr.filename ? <span className="text-muted-2 font-normal"> · {dr.filename}</span> : null}<span className="text-muted-2 font-normal"> · {dr.files.length} file{dr.files.length === 1 ? '' : 's'}</span></div>
        <div className="flex items-center gap-2 shrink-0">
          {allBusy && <span className={`text-[10px] ${allBusy.startsWith('✗') ? 'text-red' : allBusy.startsWith('✓') ? 'text-teal-d' : 'text-muted-2'}`}>{allBusy}</span>}
          <button disabled={!!allBusy && !allBusy.startsWith('✓') && !allBusy.startsWith('✗')} onClick={extractAll} title="Read every file in the room into the deal (spreadsheets/docs/zips server-side; PDFs were read at intake)" className="text-[11px] font-semibold text-white bg-teal hover:bg-teal-d rounded px-2 py-0.5 disabled:opacity-50">{allBusy && !allBusy.startsWith('✓') && !allBusy.startsWith('✗') ? <InlineBusy>Extracting…</InlineBusy> : '⚡ Extract all'}</button>
          <a href={dr.url} target="_blank" rel="noreferrer" className="text-[11.5px] font-semibold text-teal-d hover:underline">Open folder ↗</a>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {dr.files.map((f, i) => {
          const id = fileId(f);
          const st = status[id];
          const working = !!st && !st.startsWith('✓') && !st.startsWith('✗');
          return (
            <div key={i} className="flex items-center gap-2 text-[11.5px]">
              <span className="text-[9px] font-bold uppercase text-muted-2 w-[64px] shrink-0 truncate">{f.category || ''}</span>
              <a href={f.url} target="_blank" rel="noreferrer" className="text-muted hover:text-teal-d hover:underline truncate flex-1 min-w-0">{f.name} ↗</a>
              {st && <span className={`text-[10px] shrink-0 ${st.startsWith('✗') ? 'text-red' : st.startsWith('✓') ? 'text-teal-d' : 'text-muted-2'}`}>{st}</span>}
              {!st && f.extracted && <span className="text-[10px] text-teal-d shrink-0">✓ extracted</span>}
              {id && <button disabled={working} onClick={() => reextract(f)} title="Read this file into the deal (Sheets/Docs server-side; PDFs via OCR)" className="text-[10.5px] font-semibold text-navy border border-line rounded px-1.5 py-0.5 hover:bg-canvas disabled:opacity-50 shrink-0">{working ? <InlineBusy>…</InlineBusy> : (f.extracted ? '↻' : 'Extract')}</button>}
            </div>
          );
        })}
        {dr.files.length === 0 && <span className="text-[11.5px] text-muted-2">No file list in the manifest.</span>}
      </div>
      <div className="text-[10px] text-muted-2 mt-1.5">“Extract” reads a file into the deal — spreadsheets/docs server-side, PDFs via OCR when scanned — folding in financials + any blank details.</div>
    </div>
  );
}

function Docs({ d, dealId, onChanged, onUploaded }: { d: DealDto; dealId: string; onChanged: () => void; onUploaded?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<'' | 'extract' | 'upload' | 'screener' | 'zip'>('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [viewing, setViewing] = useState<{ name: string; text: string } | null>(null);
  const [docBusy, setDocBusy] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const anyBusy = busy || kind !== '';
  const docWorking = (id?: string) => { const s = id ? docBusy[id] : undefined; return !!s && !s.startsWith('✓') && !s.startsWith('✗'); };

  async function viewText(id: string) {
    setBusy(true);
    try { setViewing(await getDocumentText(dealId, id)); } catch (e) { setMsg(`Failed: ${String((e as Error).message)}`); }
    finally { setBusy(false); }
  }

  // Re-read a stored document from scratch: pull the raw file, extract its text with pdfjs (OCR a
  // scanned PDF), save it, then fill the deal's blank fields from it. This is the manual recovery
  // for any document that landed without extracted text (a scan, or an upload whose extraction
  // didn't run) — the analyst clicks "↻ extract" on the row.
  async function reextractDoc(id: string, name: string) {
    setDocBusy((s) => ({ ...s, [id]: 'Reading…' }));
    try {
      const resp = await fetch(documentDownloadUrl(dealId, id), { credentials: 'include' });
      if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
      const blob = await resp.blob();
      const file = new File([blob], name, { type: blob.type || 'application/pdf' });
      const meta = await extractDeckWithMeta(file);
      let text = meta.text;
      const isPdf = name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
      if (isPdf && isLikelyScanned(text)) {
        setDocBusy((s) => ({ ...s, [id]: 'OCR…' }));
        try { const o = await ocrDeckImages(await renderPdfToImages(file)); if (o.text.trim()) text = o.text; } catch { /* thin */ }
      }
      if (!text.trim()) throw new Error('No readable text in this file');
      await saveDocumentText(dealId, id, text, meta.pages);
      setDocBusy((s) => ({ ...s, [id]: `✓ ${text.length.toLocaleString()} chars` }));
      try { await extractFromDeck(dealId, false); } catch { /* field-fill is best-effort */ }
      onChanged();
    } catch (e) {
      setDocBusy((s) => ({ ...s, [id]: `✗ ${String((e as Error).message).slice(0, 32)}` }));
    }
  }
  async function extractFields() {
    setKind('extract'); setErr(''); setMsg('');
    try {
      const r = await extractFromDeck(dealId, true); // explicit click → overwrite from the deck
      setMsg(r.filled.length ? `Updated ${r.filled.length} field group${r.filled.length === 1 ? '' : 's'} from the deck: ${r.filled.join(', ')}. Check the Team / Business / Financials tabs.` : 'The deck yielded no structured team/business/financial data to fill.');
      onChanged();
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setKind(''); }
  }

  async function syncFromScreener() {
    setKind('screener'); setErr(''); setMsg('');
    try {
      const r = await screenerSync(dealId);
      const bits: string[] = [];
      if (r.filingsAdded) bits.push(`${r.filingsAdded} filing${r.filingsAdded === 1 ? '' : 's'}`);
      if (r.callsAdded) bits.push(`${r.callsAdded} earnings call${r.callsAdded === 1 ? '' : 's'}`);
      setMsg(bits.length
        ? `Added ${bits.join(' + ')} from Screener${r.screenerName ? ` (${r.screenerName})` : ''}. New earnings calls appear in the Calls tab.`
        : `Nothing new from Screener${r.notes.length ? ` — ${r.notes[0]}` : ''}.`);
      onChanged();
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setKind(''); }
  }

  async function onUpload(file: File) {
    setKind('upload'); setErr(''); setMsg('');
    try {
      const meta = await extractDeckWithMeta(file);
      let text = meta.text;
      const pages = meta.pages;
      // Scanned / image-only PDF (no text layer) → render pages + OCR them with a vision model.
      const isPdf = file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
      if (isPdf && isLikelyScanned(text)) {
        setMsg('Scanned deck detected — reading the pages with OCR (can take ~30s)…');
        try {
          const images = await renderPdfToImages(file);
          const r = await ocrDeckImages(images);
          if (r.text.trim()) text = r.text;
        } catch { /* fall through — the server will report if text is still too thin */ }
      }
      await uploadDocument(dealId, file, text, pages);
      setMsg(`Stored (${pages}pp). Auto-populating the deal from the deck + web…`);
      onChanged();
      // Kick the FULL auto-populate chain (deck extract → research → registry → industry → peers),
      // same as a WhatsApp intake — not just a deck field-fill. The page-level staged loader takes over.
      onUploaded?.();
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setKind(''); if (fileRef.current) fileRef.current.value = ''; }
  }
  async function remove(id: string, name: string) {
    if (!confirm(`Delete ${name}?`)) return;
    setBusy(true);
    try { await deleteDocument(dealId, id); onChanged(); } catch (e) { setMsg(`Failed: ${String((e as Error).message)}`); }
    finally { setBusy(false); }
  }

  // Attach a data room (ZIP) to THIS deal: uploads every file to the client's Drive folder and sets
  // the deal's data room, then the "⚡ Extract all" block reads them in (financials, peers, docs).
  async function onUploadZip(file: File) {
    setKind('zip'); setErr(''); setMsg('');
    try {
      setMsg(`Uploading ${file.name} to the data room on Google Drive… (large zips can take a minute)`);
      const r = await uploadDataroomZip(dealId, file);
      setMsg(`✓ Data room uploaded — ${r.added} file${r.added === 1 ? '' : 's'} to Drive (${r.total} total). Now click “⚡ Extract all” above to read them into the deal.`);
      onChanged();
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setKind(''); if (zipRef.current) zipRef.current.value = ''; }
  }

  return (
    <Card title="Documents" sub="Decks & data-room files. The extracted text is stored and used to ground enrichment.">
      {d.intel?.dataroom && <DataRoomBlock d={d} dealId={dealId} onChanged={onChanged} />}
      <div className="flex flex-wrap items-center gap-3 mb-3 p-2.5 rounded-lg bg-canvas border border-line-2">
        <input ref={fileRef} type="file" accept=".pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); }} />
        <input ref={zipRef} type="file" accept=".zip,application/zip,application/x-zip-compressed" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadZip(f); }} />
        <button disabled={anyBusy} onClick={() => fileRef.current?.click()} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-navy text-white disabled:opacity-50">{kind === 'upload' ? <InlineBusy>Uploading deck…</InlineBusy> : '📎 Upload deck (PDF / PPTX)'}</button>
        <button disabled={anyBusy} onClick={() => zipRef.current?.click()} title="Upload a data-room ZIP — every file is saved to this deal's Google Drive folder, then read into the deal" className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">{kind === 'zip' ? <InlineBusy>Uploading data room…</InlineBusy> : '🗄 Upload data room (ZIP)'}</button>
        {d.documents.length > 0 && <button disabled={anyBusy} onClick={extractFields} title="Parse the stored deck into the Team / Business / Financials tabs (fills blanks only)" className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">{kind === 'extract' ? <InlineBusy>Extracting…</InlineBusy> : '✨ Extract details from deck'}</button>}
        <button disabled={anyBusy} onClick={syncFromScreener} title="Fetch the company's latest annual reports + most recent earnings call from Screener (or its listed peers if unlisted)" className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line hover:bg-canvas disabled:opacity-50">{kind === 'screener' ? <InlineBusy>Syncing from Screener…</InlineBusy> : '📊 Sync from Screener'}</button>
        {!anyBusy && <span className={`text-[11.5px] ${msg.startsWith('Failed') ? 'text-red' : 'text-muted'}`}>{msg || 'Attach a deck to this deal — stored privately, text extracted for enrichment.'}</span>}
      </div>
      {kind === 'screener' && <StagedLoader stages={['Resolving on Screener', 'Latest filings → Documents', 'Recent earnings call → Calls', 'Done']} stageMs={9000} note="Fetching filings + the latest concall from screener.in. This can take up to ~60s." className="mb-3" />}
      {kind === 'extract' && <StagedLoader stages={DECK_STAGES} stageMs={9000} className="mb-3" />}
      {kind === 'upload' && <StagedLoader stages={DECK_STAGES} stageMs={9000} note="Reading the PDF in your browser, then running 3 extraction passes on the server. This can take up to ~60s — you can keep working." className="mb-3" />}
      {kind === 'zip' && <StagedLoader stages={['Uploading zip to Drive', 'Creating the folder', 'Saving each file', 'Done']} stageMs={12000} note="Uploading the data room to Google Drive. Large rooms can take a minute or two." className="mb-3" />}
      {err && !anyBusy && <StagedLoader stages={DECK_STAGES} error={err} onRetry={d.documents.length ? extractFields : undefined} className="mb-3" />}
      {kind === 'upload' && !d.documents.length ? (
        <div className="overflow-x-auto"><TableSkeleton head={['Name', 'Source', 'Type', 'Pages', 'Chars', '']} rows={1} /></div>
      ) : d.documents.length ? (
        <div className="overflow-x-auto">
          <DataTable head={['Name', 'Source', 'Type', 'Pages', 'Chars', '']} rows={d.documents.map((x) => [
            x.id ? <a href={documentDownloadUrl(dealId, x.id)} className="text-teal-d font-semibold hover:underline" target="_blank" rel="noreferrer">{x.name.replace(/^Registry Intelligence — /, '')}</a> : x.name,
            x.name.startsWith('Registry Intelligence — ') ? <ViaRegistryIntel /> : <span className="text-muted-2">Uploaded</span>,
            (x.name.match(/\.(\w+)$/)?.[1] ?? x.type ?? '—').toUpperCase(),
            String(x.pages ?? '—'),
            x.chars ? x.chars.toLocaleString() : '—',
            x.id ? <div className="flex gap-2 justify-end items-center">
              {docBusy[x.id] && <span className={`text-[10px] ${docBusy[x.id]!.startsWith('✗') ? 'text-red' : docBusy[x.id]!.startsWith('✓') ? 'text-teal-d' : 'text-muted-2'}`}>{docBusy[x.id]}</span>}
              <button onClick={() => reextractDoc(x.id!, x.name)} disabled={anyBusy || docWorking(x.id)} title="Re-read this file (text + OCR for scans) and fill the deal's blank fields" className="text-[11px] font-semibold text-navy hover:underline disabled:opacity-50">{docWorking(x.id) ? <InlineBusy>…</InlineBusy> : (x.chars ? '↻ extract' : 'extract')}</button>
              {x.chars ? <button onClick={() => viewText(x.id!)} disabled={anyBusy} className="text-[11px] font-semibold text-teal-d hover:underline">view text</button> : null}
              <button onClick={() => remove(x.id!, x.name)} disabled={anyBusy} className="text-[11px] font-semibold text-red hover:underline">delete</button>
            </div> : '',
          ])} />
        </div>
      ) : (
        <Empty label={d.intel?.probe42
          ? "No documents yet. Upload a deck above — and note that this deal's Registry Intelligence pull returned no PDF/document references either (verified against the raw payload; Probe42's comprehensive-details response simply doesn't include any for this company), so none were pulled in automatically. If a future pull or refresh does return one, it's stored and auto-extracted here without any extra step."
          : 'No documents yet — upload a deck above.'} />
      )}
      {viewing && (
        <Modal title={`Extracted text — ${viewing.name}`} onClose={() => setViewing(null)}>
          <p className="text-[11px] text-muted-2 mb-2">{viewing.text.length.toLocaleString()} chars · this is what grounds enrichment.</p>
          <pre className="text-[11.5px] leading-relaxed whitespace-pre-wrap bg-canvas border border-line-2 rounded-lg p-3 max-h-[55vh] overflow-y-auto">{viewing.text || '(no text extracted)'}</pre>
        </Modal>
      )}
    </Card>
  );
}

function Memo({ d, weights, icThreshold }: { d: DealDto; weights: Record<ModuleKey, number>; icThreshold: number }) {
  const md = useMemo(() => buildMemo(d, weights, icThreshold), [d, weights, icThreshold]);
  function download() {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([md], { type: 'text/markdown' }));
    a.download = `IC_memo_${d.short}.md`;
    a.click();
  }
  return (
    <Card title="IC memo" sub="Assembled from live deal data" actions={<div className="flex gap-2"><button onClick={download} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-white text-navy border border-line">↓ .md</button><button onClick={() => window.print()} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-navy text-white">Print</button></div>}>
      <pre className="whitespace-pre-wrap text-[12px] leading-relaxed font-sans">{md}</pre>
    </Card>
  );
}

function buildMemo(d: DealDto, weights: Record<ModuleKey, number>, icThreshold: number): string {
  const v = valn(d); const s = score(d, weights); const gr = grade(s); const f = thesisFit(d); const r = rec(d, weights, icThreshold);
  const L: string[] = [];
  L.push(`# IC Memo — ${d.company} (${d.short})`);
  L.push(`\n**Sector:** ${d.sector} · ${d.location}  \n**Stage:** ${d.status} · **POC:** ${d.poc || '—'} · **Banker:** ${d.banker}`);
  L.push(`\n## Recommendation\n**${r.h}** — ${r.s}`);
  L.push(`\n## Thesis\n${d.analyst.thesis || '—'}`);
  L.push(`\n## Thesis-fit: ${f.verdict}\n${f.reasons.map((x) => `- ${x[0].toUpperCase()}: ${x[1]}`).join('\n')}`);
  L.push(`\n## Financials (₹ Cr)\n| | FY25 | FY26 |\n|---|---|---|\n| Revenue | ${d.rev25} | ${d.rev26} |\n| PAT | ${d.pat25} | ${d.pat26} |\n| EBITDA FY26 | | ${d.ebitda26} |`);
  // Data-room statements (and the ratios computed from them) when the deal has them — the same
  // tables the Financials tab renders, so the memo and the app never disagree.
  const stmts = d.financials ? statementsToMarkdown(d.financials, { ratios: true }) : '';
  if (stmts) L.push(`\n## Detailed statements\n${stmts}`);
  L.push(`\n## Valuation\n- Ask: ${inr(d.valuationAsk)} · Round: ${inr(d.totalRaise)} · Allocation: ${inr(d.allocation)}\n- Implied P/E: ${v.ok ? xx(v.entryPE) : 'n/m'} vs peer ${xx(v.peNow)} → MoS ${v.ok ? pct(v.mos) : 'n/m'}\n- MOIC (bear/base/bull): ${v.ok ? `${xx(v.bear)} / ${xx(v.base)} / ${xx(v.bull)}` : 'n/m'} over ${v.yrs.toFixed(1)}y`);
  L.push(`\n## Krit Score: ${s} (${gr.g})\n${MODULES.map((m) => `- ${m.n}: ${d.ratings[m.k]} (w${weights[m.k] ?? m.w})`).join('\n')}`);
  if (d.risks.length) L.push(`\n## Risks\n${d.risks.map((x) => `- **${x.risk}** (${x.prob}/${x.impact}) — ${x.mit}`).join('\n')}`);
  if (d.diligence.length) L.push(`\n## Diligence\n${d.diligence.map((x) => `- [${x.status}] ${x.item} (${x.cat})`).join('\n')}`);
  L.push(`\n---\n*Generated from the Krit Deal Engine. Figures in ₹ crore.*`);
  return L.join('\n');
}

function Empty({ label = 'No data yet.' }: { label?: string }) {
  return <div className="text-center py-6 text-muted text-[12.5px]">{label}</div>;
}
